import {
  askConfirm,
  lastSelectionKey,
  readLastSelection,
  showAlert,
  showError,
  writeLastSelection,
} from "@hereby/components";
import type { NursingGradeTone } from "@hereby/core/constants";
import { normalizeEmployeeNumber, resolveNursingGrade } from "@hereby/core/constants";
import { formatHoursHM } from "@hereby/core/date";
import { SortOrder, TeamManagerStatusFilter, WardPatientCountSource } from "@hereby/core/enums";
import type { NurseSchedulerService, ShiftService, TeamManagerService } from "@hereby/sdk";
import { fetchAllPages } from "@hereby/sdk";
import type { AsyncPollHandle } from "@hereby/sdk/async-poller";
import { pollAsyncOperation } from "@hereby/sdk/async-poller";
import Alpine from "alpinejs";
import { ensureHolidayYears, getHolidayName, isHoliday } from "./data/kr-holidays";

/** 「스케줄에서 보기」로 건너온 줄의 강조를 걷는 타이머 — 컴포넌트 밖에 둬 Alpine 이 프록시하지 않게 한다 */
let schedulerFocusTimer: ReturnType<typeof setTimeout> | undefined;

interface TeamInfo {
  id: string;
  name: string;
  code: string;
  memberCount: number;
}

interface TeamMemberInfo {
  id: string;
  employeeId: string;
  name: string;
  employeeNumber: string;
  teamId: string;
  teamName: string;
}

interface Pagination {
  page: number;
  totalPages: number;
  totalCount: number;
}

// SDK 라이브 타입에서 직접 파생 — 서버 응답 계약과 항상 동기화 유지
type TeamManagerDocument = Awaited<
  ReturnType<TeamManagerService["listDocuments"]>
>["items"][number];
type TeamManagerCertificate = Awaited<
  ReturnType<TeamManagerService["listCertificates"]>
>["items"][number];
type NurseProfileItem = Awaited<ReturnType<NurseSchedulerService["listProfiles"]>>["items"][number];
type ShiftItem = Awaited<ReturnType<ShiftService["list"]>>["items"][number];

interface NurseRow {
  employeeId: string;
  employeeNumber: string;
  name: string;
  workType: string;
  wardId: string;
  wardName: string;
  isHeadNurse: boolean;
  profileId: string;
  totalWardCount?: number;
  // 소속 스케줄러(병동 안에서 근무표를 나누는 단위) — 인력·근무표는 이 단위로 묶인다
  scheduleGroupId?: string;
  scheduleGroupName?: string;
  // 직원 재직 상태(EmployeeStatus) — 퇴사자도 지난 근무표 때문에 명단에 남으므로 인원 셀 때 거른다
  employeeStatus?: string;
  // 대기 중 예약 이동(있으면 이름 툴팁 + 편집 모달에 표시) — 이동 대상은 스케줄러(병동은 파생)
  pendingReservation?: PendingReservation | null;
}

interface PendingReservation {
  id: string;
  toWardId: string;
  toWardName: string;
  toScheduleGroupId?: string | null;
  toScheduleGroupName?: string;
  effectiveDate: string;
}

interface Ward {
  id: string;
  name: string;
  code: string;
}

/** 스케줄러(스케줄 그룹) — 병동마다 기본(isDefault) 하나가 항상 있다. */
interface ScheduleGroup {
  id: string;
  wardId: string;
  wardName: string;
  name: string;
  code: string | null;
  location: string | null;
  isActive: boolean;
  isDefault: boolean;
}

/** 스케줄러 편성 설정 응답(교대 매핑 · 교육 정책 …) — 모달 폼이 읽는 모양. */
type ScheduleGroupSettings = Awaited<ReturnType<NurseSchedulerService["getScheduleGroupSettings"]>>;

/** 겸임 중복 근무 — 이 근무표 직원이 같은 날 다른 근무표에도 근무로 배정된 셀(entry.conflicts). */
type DupConflict = Awaited<
  ReturnType<NurseSchedulerService["getScheduleConflicts"]>
>["items"][number];

/** 스케줄러 표시명 — 위치가 있으면 「이름 (위치)」. 툴바 select · 모달 · 파일명 공용. */
function scheduleGroupLabel(g: { name: string; location?: string | null } | undefined): string {
  if (!g) {
    return "";
  }
  return g.location ? `${g.name} (${g.location})` : g.name;
}

interface ConflictWarning {
  date: string;
  message: string;
}

/** 인원에 넣는 재직 상태 — 재직(ACTIVE) + 휴직(ON_LEAVE). 상태가 없는 응답이면 예전처럼 센다. */
function isCountedNurseStatus(status: string | undefined): boolean {
  return status === undefined || status === "ACTIVE" || status === "ON_LEAVE";
}

function _t(ctx: unknown, key: string): string {
  // biome-ignore lint/suspicious/noExplicitAny: Alpine store typing
  return (ctx as any).$store?.hereby?.t?.(key) ?? key;
}

export function toDateStr(d: string | Date): string {
  if (typeof d === "string") {
    return d.split("T")[0];
  }
  return d.toISOString().split("T")[0];
}

/** 배경 HEX 대비에 따라 가독성 좋은 글자색(흰/검) */
function leaveTextColor(hex: string | undefined): string {
  const h = (hex || "").replace("#", "");
  if (h.length !== 6) {
    return "#1b1b1b";
  }
  const r = Number.parseInt(h.slice(0, 2), 16);
  const g = Number.parseInt(h.slice(2, 4), 16);
  const b = Number.parseInt(h.slice(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 >= 150 ? "#1b1b1b" : "#ffffff";
}

type SchedulerLeaveReason = {
  id: string;
  name: string;
  code?: string;
  color?: string;
  schedulerEnabled?: boolean;
  status?: string;
};

/** 조직의 휴가 유형 전부 — 라벨·색 조회용(이미 배정된 셀은 유형이 비활성·코드 없음이어도 그려져야 한다). */
export function allLeaveReasons(): SchedulerLeaveReason[] {
  return (
    (Alpine.store("hereby")?.currentUser?.leaveReasons as SchedulerLeaveReason[] | undefined) || []
  );
}

/**
 * 근무표 배정 목록에 오르는 휴가 유형 — 스케줄러 연동 + 활성 + **코드 있음**.
 * 코드는 선택 입력(2026-09-16)이지만 근무표 셀에 그려질 약어가 없는 유형은 배정 목록에서 뺀다
 * (휴가 유형 폼이 같은 규칙을 안내한다). 기존 배정은 allLeaveReasons 라벨로 계속 표시된다.
 */
export function schedulerLeaveReasons(): SchedulerLeaveReason[] {
  return allLeaveReasons().filter(
    (r) => r.schedulerEnabled && (r.status || "active") === "active" && String(r.code ?? "").trim(),
  );
}

export function getAssignmentLabels(ctx: unknown): Record<string, string> {
  const labels: Record<string, string> = {
    day: "D",
    evening: "E",
    night: "N",
    off: "OFF",
    annual_leave: _t(ctx, "nurse.assignment_annual_leave"),
    bereavement: _t(ctx, "nurse.assignment_bereavement"),
    education: _t(ctx, "nurse.assignment_education"),
    unavailable: _t(ctx, "nurse.assignment_unavailable"),
  };
  // 스케줄러 연동 근무 유형(셀 값 = shift id) → 코드(없으면 이름) 라벨
  const shifts =
    (ctx as { availableShifts?: Array<{ id: string; code?: string; name: string }> })
      ?.availableShifts || [];
  for (const s of shifts) {
    labels[s.id] = s.code || s.name;
  }
  // 커스텀 휴가 유형(셀 값 = 유형 id) → 코드(없으면 이름) 라벨. 배정 목록(schedulerLeaveReasons)이
  // 아니라 전체 유형을 돈다 — 나중에 코드를 지우거나 연동을 끈 유형의 기존 배정도 이름으로 보여야 한다.
  for (const r of allLeaveReasons()) {
    labels[r.id] = r.code || r.name;
  }
  return labels;
}

const ASSIGNMENT_CLASSES: Record<string, string> = {
  day: "shift-D",
  evening: "shift-E",
  night: "shift-N",
  off: "shift-OFF",
  annual_leave: "shift-AL",
  bereavement: "shift-BV",
  education: "shift-ED",
  unavailable: "shift-UA",
};

/**
 * 칸 값 → 그릴 것(라벨 · 클래스 · 인라인 색). 그리드의 getCellLabel/Class/Style 과 같은 규칙을
 * 컴포넌트 밖에서 쓰려고 떼어 둔 것 — 팀 관리 「스케줄러 미리보기」가 읽기 전용 표를 그릴 때 쓴다.
 * 연동 근무 유형은 코드 색, 연동 휴가 유형은 유형 색, 둘 다 아니면 기본 8종 클래스.
 */
export function assignmentCellView(
  type: string,
  labels: Record<string, string>,
  shifts: Array<{ id: string; codeColor?: string }>,
): { label: string; cls: string; style: string } {
  const label = labels[type] || "";
  if (!type) {
    return { label, cls: "", style: "" };
  }
  if (ASSIGNMENT_CLASSES[type]) {
    return { label, cls: ASSIGNMENT_CLASSES[type], style: "" };
  }
  const shift = shifts.find((s) => s.id === type);
  if (shift) {
    const bg = shift.codeColor || "#B8B8B8";
    return {
      label,
      cls: "shift-OFF sched-cell--custom-shift",
      style: `background-color:${bg};color:${leaveTextColor(bg)}`,
    };
  }
  const reason = schedulerLeaveReasons().find((r) => r.id === type);
  if (reason) {
    const bg = reason.color || "#B8B8B8";
    return {
      label,
      cls: "shift-OFF sched-cell--custom-leave",
      style: `background-color:${bg};color:${leaveTextColor(bg)}`,
    };
  }
  return { label, cls: "", style: "" };
}

/** 셀별 희망근무 매칭 결과. getPrefForCell 의 반환 타입이자 prefCellIndex 의 값. */
type PrefCellResult = {
  type: "vacation" | "restricted" | "unavailable" | "preferred" | null;
  reason: string;
  restrictedShifts: string[];
  preferredShifts: string[];
  /** type==='vacation' 이고 스케줄러 연동 휴가 유형을 지정한 경우 그 id (org.settings.leaveReasons[].id). */
  vacationLeaveId?: string;
};
/** 희망근무 없음 — 모든 빈 셀이 공유하는 불변 기본값(셀당 객체 할당 방지). */
const EMPTY_PREF: PrefCellResult = Object.freeze({
  type: null,
  reason: "",
  restrictedShifts: Object.freeze([]) as unknown as string[],
  preferredShifts: Object.freeze([]) as unknown as string[],
}) as PrefCellResult;

const WORK_TYPE_KEYS: Record<string, string> = {
  three_shift: "nurse.work_type_three_shift",
  night_dedicated: "nurse.work_type_night_dedicated",
  day_dedicated: "nurse.work_type_day_dedicated",
  evening_dedicated: "nurse.work_type_evening_dedicated",
};

const CYCLE_ORDER = ["day", "evening", "night", "off"];

const LEAVE_TYPE_KEYS: Record<string, string> = {
  annual: "team_mgr.leave_type_annual",
  sick: "team_mgr.leave_type_sick",
  personal: "team_mgr.leave_type_personal",
  maternity: "team_mgr.leave_type_maternity",
  paternity: "team_mgr.leave_type_paternity",
  bereavement: "team_mgr.leave_type_bereavement",
  military: "team_mgr.leave_type_military",
  other: "leave.other",
};

const STATUS_KEYS: Record<string, string> = {
  pending: "team_mgr.status_pending",
  approved: "team_mgr.status_approved",
  rejected: "team_mgr.status_rejected",
  cancelled: "team_mgr.status_cancelled",
  paid: "team_mgr.status_paid",
};

const CATEGORY_KEYS: Record<string, string> = {
  weekday: "team_mgr.category_weekday",
  saturday: "team_mgr.category_saturday",
  holiday: "team_mgr.category_holiday",
  night: "team_mgr.category_night",
};

const SHIFT_TYPE_KEYS: Record<string, string> = {
  day: "team_mgr.shift_type_day",
  night: "team_mgr.shift_type_night",
  evening: "team_mgr.shift_type_evening",
  weekend_day: "team_mgr.shift_type_weekend_day",
  weekend_night: "team_mgr.shift_type_weekend_night",
  holiday: "team_mgr.shift_type_holiday",
  flexible: "team_mgr.shift_type_flexible",
};

const DAY_KEYS: Record<string, string> = {
  monday: "team_mgr.day_mon",
  tuesday: "team_mgr.day_tue",
  wednesday: "team_mgr.day_wed",
  thursday: "team_mgr.day_thu",
  friday: "team_mgr.day_fri",
  saturday: "team_mgr.day_sat",
  sunday: "team_mgr.day_sun",
};

const INCENTIVE_TYPE_KEYS: Record<string, string> = {
  referral: "team_mgr.incentive_type_referral",
  inpatient_maintenance: "team_mgr.incentive_type_inpatient_maintenance",
  shockwave: "team_mgr.incentive_type_shockwave",
  other: "leave.other",
};

interface TreatmentItem {
  code: string;
  name: string;
  ratio: string;
  unitPrice: number;
  count: number;
}

interface TreatmentCategoryState {
  id: string;
  label: string;
  hospitalRatio: number;
  therapistRatio: number;
  items: TreatmentItem[];
}

// Embedded in the admin app: the scheduler always starts on the 근무표 sub-tab.
// (In team-manager this derived the tab from the URL; here the admin router owns
// the top-level route, so the scheduler only manages its own internal sub-tabs.)
function initialTabFromUrl(): string {
  return "scheduler";
}

export function schedulerComponent() {
  return {
    /* Shared save-confirm for data-entry (form) modals — mirrors team-manager.
       attemptClose("showXModal") opens the confirm; confirmClose() (닫기) closes
       the target modal, dismissClose() (계속편집) cancels.
       Target may also name a closer method (e.g. "closePatientCountPanel") for
       modals that need teardown beyond flipping a state var. */
    closeConfirm: { open: false, target: "" } as { open: boolean; target: string },
    /* Pristine-close guard: a baseline of each modal's form state is captured
       when it opens ($watch in init). While the form is unchanged, X/취소/Esc
       close directly — the save-confirm only appears once something was typed.
       Targets in neither closeGuardForms nor closeGuardCustom (e.g. patient-count)
       always confirm — the safe default. */
    closeGuardForms: {
      showAddProfile: ["newProfile", "selectedEmployeeIds"],
      showEditProfile: ["editProfileForm"],
      showChangeWard: ["changeWardForm"],
      showScheduleUpload: ["scheduleUploadForm"],
      showAutoRegister: ["autoRegisterCandidates"],
    } as Record<string, string[]>,
    /* Custom pristine checks (target → method name) for modals whose state the
       generic open-time snapshot can't capture (async-loaded multi-tab forms,
       per-row inline edits). The named method returns true when dirty. */
    closeGuardCustom: {
      showWardSettings: "wardSettingsDirty",
      // 환자수 관리: pcEditing이 async 로드라 open-time 스냅샷으론 못 잡음 → 전용 dirty 체크.
      closePatientCountPanel: "patientCountDirty",
      // 환자수 팝업 「병동 관리 ›」 — 페이지 이동도 팝업을 닫으므로 같은 dirty 체크를 거친다.
      goToWardsPage: "patientCountDirty",
    } as Record<string, string>,
    closeGuardBaselines: {} as Record<string, string>,
    closeGuardState(target: string): string {
      const keys: string[] = this.closeGuardForms[target] ?? [];
      return JSON.stringify(keys.map((k: string) => (this as Record<string, unknown>)[k]));
    },
    /**
     * closeGuard baseline(폼이 열린 시점 상태) 대비 변경 여부. 변경이 없으면 저장
     * 버튼을 비활성화하는 데 쓴다(수정 모드에서 0변경 저장 방지). admin app.ts와 동일.
     */
    formDirty(target: string): boolean {
      const baseline = this.closeGuardBaselines[target];
      return baseline !== undefined && baseline !== this.closeGuardState(target);
    },
    /* ward-settings has 4 async-loaded tab forms, so the generic open-time
       snapshot can't be trusted. Compare each form to its saved baseline (set on
       load); dirty if any differs. Empty baselines (not yet loaded) are skipped
       so a quick open → close during load stays pristine (no confirm). */
    wardSettingsDirty(): boolean {
      if (
        this.staffingSettingsSaved &&
        JSON.stringify(this.wardSettingsForm) !== this.staffingSettingsSaved
      ) {
        return true;
      }
      if (
        this.shiftMappingsSaved &&
        JSON.stringify(this.shiftMappingsForm) !== this.shiftMappingsSaved
      ) {
        return true;
      }
      if (this.educationPolicySaved && this.educationPolicyForm !== this.educationPolicySaved) {
        return true;
      }
      if (this.cycleStartDayDraft !== this.cycleStartDay) {
        return true;
      }
      return false;
    },
    attemptClose(stateVar: string) {
      const customCheck = this.closeGuardCustom[stateVar];
      if (customCheck) {
        const dirty = (this as Record<string, unknown>)[customCheck];
        if (typeof dirty === "function" && !(dirty as () => boolean).call(this)) {
          this.performClose(stateVar);
        } else {
          this.closeConfirm = { open: true, target: stateVar };
        }
        return;
      }
      const baseline = this.closeGuardBaselines[stateVar];
      if (baseline !== undefined && baseline === this.closeGuardState(stateVar)) {
        this.performClose(stateVar);
        return;
      }
      this.closeConfirm = { open: true, target: stateVar };
    },
    performClose(target: string) {
      const member = (this as Record<string, unknown>)[target];
      if (typeof member === "function") {
        (member as () => void).call(this);
      } else {
        (this as Record<string, unknown>)[target] = false;
      }
    },
    confirmClose() {
      const target = this.closeConfirm.target;
      this.closeConfirm = { open: false, target: "" };
      if (target) {
        this.performClose(target);
      }
    },
    dismissClose() {
      this.closeConfirm = { open: false, target: "" };
    },

    // Tabs
    activeTab: initialTabFromUrl() as string,
    get allTabs() {
      const t = (k: string) => Alpine.store("hereby")?.t?.(k) ?? k;
      const tabs: { id: string; label: string }[] = [
        { id: "overview", label: t("common.dashboard") },
      ];
      // Scheduler sits between 대시보드 and 연차/휴가 — only for nurses or managers.
      if (this.isNurse || this.isNurseManager || this.canReadScheduler) {
        tabs.push({ id: "scheduler", label: "스케쥴러" });
      }
      tabs.push(
        { id: "leave", label: t("nav.leaves") },
        { id: "overtime", label: t("overtime.overtime_request") },
        // { id: "incentives", label: t("notification.event_incentive") },
        { id: "treatment", label: t("notification.event_incentive") },
        { id: "document-management", label: "문서 관리" },
      );
      return tabs;
    },

    /** Sub-tabs under the 스케쥴러 parent menu. Order matters for default selection. */
    get schedulerSubTabs() {
      const t = (k: string) => Alpine.store("hereby")?.t?.(k) ?? k;
      const subs: { id: string; label: string }[] = [];
      if (this.isNurseManager || this.canReadScheduler) {
        subs.push({ id: "scheduler", label: t("nurse.tab_schedule") });
        subs.push({ id: "profiles", label: t("nurse.tab_profiles") });
        // 희망근무는 **입력** 화면이라 조회 권한만으로는 열지 않는다
        if (this.isNurseManager && this.preferenceTabEnabled) {
          subs.push({ id: "pref-input", label: "희망근무" });
        }
      } else if (this.isNurse) {
        subs.push({ id: "scheduler", label: t("nurse.tab_schedule") });
      }
      // 내 희망 일정 (preferences) — 히든. 추후 모바일앱(Hereby Works)에서 제공 예정.
      // subs.push({ id: "preferences", label: t("nurse.tab_preferences") });
      return subs;
    },

    /** True when activeTab is one of the scheduler sub-tabs (parent highlight). */
    get isSchedulerActive(): boolean {
      return (
        this.activeTab === "scheduler" ||
        this.activeTab === "profiles" ||
        this.activeTab === "preferences" ||
        this.activeTab === "pref-input"
      );
    },

    // General
    /* 마운트 직후부터 true — false 로 시작하면 init() 의 첫 왕복 동안 `!loading && nurses.length === 0`
       분기(「이 병동에 배정된 사람이 없어요」)가 잠깐 떴다가 스피너로 바뀌는 깜빡임이 난다.
       loadTabData() 가 끝나면서 항상 false 로 내린다. */
    loading: true,
    selectedTeamId: "" as string,

    // Teams
    myTeams: [] as TeamInfo[],
    teamMembers: [] as TeamMemberInfo[],

    // Dashboard counts
    pendingCounts: { leave: 0, overtime: 0, incentive: 0, document: 0, certificate: 0 },

    // Leave requests
    leaveRequests: [] as Record<string, unknown>[],
    leaveFilter: TeamManagerStatusFilter.ALL as TeamManagerStatusFilter,
    leavePagination: { page: 1, totalPages: 0, totalCount: 0 } as Pagination,
    selectedLeaveIds: [] as string[],

    // Overtime
    overtimeRecords: [] as Record<string, unknown>[],
    overtimeFilter: TeamManagerStatusFilter.ALL as TeamManagerStatusFilter,
    overtimePagination: { page: 1, totalPages: 0, totalCount: 0 } as Pagination,
    selectedOvertimeIds: [] as string[],
    overtimeViewItem: null as Record<string, unknown> | null,

    // Unified Document Management (certificates + documents)
    docMgmtRows: [] as {
      id: string;
      source: "certificate" | "document";
      kind: "request" | "submission";
      employeeId: string;
      employeeNumber: string;
      employeeName: string;
      type: string;
      createdAt: string;
      status: string;
      notes: string | null;
      rejectionReason: string | null;
      downloadUrl: string | null;
      certificateHtml: string | null;
      cancelledAt: string | null;
    }[],
    docMgmtFilter: "all" as string,
    docMgmtKindFilter: "all" as "all" | "request" | "submission",

    // Incentives
    incentives: [] as Record<string, unknown>[],
    incentiveYear: new Date().getFullYear(),
    incentiveMonth: new Date().getMonth() + 1,
    incentivePagination: { page: 1, totalPages: 0, totalCount: 0 } as Pagination,
    selectedIncentiveIds: [] as string[],
    years: Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i),
    // Attendance summary for incentive verification
    attendanceSummary: [] as { employeeId: string; types: Record<string, number>; total: number }[],
    // Incentive preset categories with verifiable flag
    presetCategories: [] as {
      id: string;
      code: string;
      name: string;
      isAttendanceVerifiable: boolean;
    }[],
    presetCategoriesLoaded: false,

    // Treatment Incentive (치료 인센티브)
    treatmentCategories: [] as TreatmentCategoryState[],
    selectedCategoryId: "" as string,
    // Separate selection for the detail-records section so users can browse
    // history of one category while filling the form for another.
    selectedDetailCategoryId: "" as string,
    treatmentCategoriesLoaded: false,
    treatmentWorkDate: "" as string,
    treatmentTherapists: [] as string[],
    treatmentRecords: [] as Record<string, unknown>[],
    treatmentYear: new Date().getFullYear(),
    treatmentMonth: new Date().getMonth() + 1,
    treatmentSaving: false,
    treatmentNotes: "" as string,
    therapistSearch: "" as string,

    // Documents (서류 승인)
    documents: [] as TeamManagerDocument[],
    documentPagination: { page: 1, totalPages: 0, totalCount: 0 } as Pagination,
    selectedDocumentIds: [] as string[],

    // Certificates (증명서 발급)
    certificates: [] as TeamManagerCertificate[],
    certificatePagination: { page: 1, totalPages: 0, totalCount: 0 } as Pagination,
    selectedCertificateIds: [] as string[],

    // Shifts (근무시간 설정)
    shifts: [] as ShiftItem[],
    showShiftModal: false,
    editingShiftId: "" as string,
    shiftForm: {
      code: "",
      name: "",
      description: "",
      type: "day" as string,
      startTime: "09:00",
      endTime: "18:00",
      workHours: 8,
      breakHours: 1,
      overtimeRate: 1.5,
      nightDifferentialRate: undefined as number | undefined,
      weekendDifferentialRate: undefined as number | undefined,
      holidayDifferentialRate: undefined as number | undefined,
      applicableDays: {
        monday: true,
        tuesday: true,
        wednesday: true,
        thursday: true,
        friday: true,
        saturday: false,
        sunday: false,
      } as Record<string, boolean>,
    },
    shiftSaving: false,

    // Notifications are now handled by the shared NotificationBell component

    // Reject modal
    showRejectModal: false,
    rejectReason: "" as string,
    rejectTarget: { type: "" as string, ids: [] as string[] },

    // ===== Nurse Scheduler state =====

    // Nurse scheduler init / role flags
    nurseSchedulerInitialized: false,
    /* Alpine 은 x-data 객체에 init() 이 있으면 자동으로 부른다. 마크업의 x-init="init()" 과 겹쳐
       초기 로딩 전체가 두 번 돌던 것을 막는 재진입 가드 (team-manager 원본의 booted 와 동일). */
    booted: false,
    /* checkNurseStatus(프로필 조회) 가 끝났는지 — 끝나기 전엔 「권한 없음」 안내를 띄우지 않는다
       (간호사 계정은 isNurse 가 한 왕복 뒤에야 true 가 되므로 그동안 안내가 깜빡였다). */
    roleChecked: false,
    isNurseManager: false,
    isNurse: false,
    /** 인력·근무표를 **볼** 수 있는가 (nurse-scheduler:read). 바꿀 수 있는지는 isNurseManager.
     *  전엔 이 둘이 한 값이라, 조회 권한만 가진 사람에게 탭 자체가 안 보이거나
     *  「간호사가 등록되지 않았습니다」로 보여 인력이 없는 것과 구분되지 않았다(TMA-02). */
    canReadScheduler: false,
    /** 다른 화면(직원 카드)에서 건너와 잠깐 강조할 간호사 — 근무표 줄이 이 값을 읽는다 */
    focusEmployeeId: "" as string,
    /** Wards assigned to the user (regular nurses: their wards; manager: empty = all) */
    myWardIds: [] as string[],
    popstateBound: false,

    // Grid selection state (Excel-like multi-cell)
    selectedCells: new Set<string>(),
    cursorCell: null as { rowIdx: number; colIdx: number } | null,
    lastClickedCell: null as { rowIdx: number; colIdx: number } | null,
    dragStartCell: null as { rowIdx: number; colIdx: number } | null,
    isDragging: false,
    pendingDragTo: null as { rowIdx: number; colIdx: number } | null,
    dragRafPending: false,
    selectionVersion: 0,

    // Work type multi-select
    selectedWorkTypeRows: new Set<number>(),
    lastClickedWtRow: null as number | null,
    wtCursorRow: null as number | null,
    showWorkTypePicker: false,
    wtCtxMenu: { show: false, x: 0, y: 0 },
    wtDragStart: null as number | null,
    wtDragging: false,
    wtDragEnd: null as number | null,
    wtPickerAnchor: { x: 0, y: 0 },

    // Fill-drag (Excel fill handle)
    isFillDragging: false,
    fillDragAnchor: null as { rowIdx: number; colIdx: number } | null,
    fillSourceValue: "" as string,
    fillPreviewKeys: new Set<string>(),

    // Period
    period: { year: new Date().getFullYear(), month: new Date().getMonth() + 1 },
    nurseYears: Array.from({ length: 5 }, (_, i) => new Date().getFullYear() + 2 - i),

    // Hospital-wide nurse-scheduler config
    cycleStartDay: 16,
    cycleStartDayDraft: 16,
    schedulerConfigSaving: false,
    /** 희망근무 탭 노출 여부 (병원 전체 설정). false면 희망근무 탭 히든. */
    preferenceTabEnabled: true,

    // Wards
    wards: [] as Ward[],
    selectedWardId: "" as string,
    /** 기억해 둔 병동 — 목록이 오면 applyWardList 가 목록에 있을 때만 선택한다. */
    restoreWardId: "" as string,
    // 스케줄러 — 조직 전체 목록을 한 번 받아 두고(병동 목록과 같은 왕복), 선택은 병동 안에서 고른다.
    scheduleGroups: [] as ScheduleGroup[],
    selectedScheduleGroupId: "" as string,
    /** 선택 병동의 스케줄러 — 활성 먼저, 기본 먼저. 툴바 select 가 그린다. */
    get wardScheduleGroups(): ScheduleGroup[] {
      return this.scheduleGroupsForWard(this.selectedWardId);
    },
    get selectedScheduleGroup(): ScheduleGroup | undefined {
      return this.scheduleGroups.find((g: ScheduleGroup) => g.id === this.selectedScheduleGroupId);
    },
    /** 겸임 중복 근무 — 근무표 로드·배정 저장 뒤 entry.conflicts 로 다시 받는다. 저장은 막지 않는다. */
    dupConflicts: [] as DupConflict[],
    dupConflictByCell: {} as Record<string, DupConflict[]>,
    showDupConflicts: false,
    /** 준수율(최소 인원)에 합산된 같은 병동의 다른 스케줄러 근무표 수 — 0 이면 단독 계산. */
    siblingScheduleCount: 0,

    // Schedule data
    scheduleId: null as string | null,
    scheduleStatus: "" as string,
    nurses: [] as NurseRow[],
    entries: new Map<string, string>(),

    // === Render-time lookup caches (built once on load, not per cell) ===
    // The grid is ~nurses×days cells and each cell binding called getPrefForCell
    // ~20× (each doing 4 array scans) and dayToDateStr (indexOf) several times.
    // These maps turn every per-cell call into an O(1) lookup.
    prefCellIndex: new Map<string, PrefCellResult>(), // `${employeeId}:${dateStr}` → pref
    dayDateStrMap: new Map<number, string>(), // day-of-month number → "YYYY-MM-DD"
    // Reverse indexes for grid event delegation: instead of 5 listeners on each of
    // ~6000 cells, the <tbody> carries 4 delegated listeners and resolves the cell
    // from data-ck("empId:dateStr"). These map the two halves back to row/col.
    empRowIndex: new Map<string, number>(), // employeeId → row index
    dateColIndex: new Map<string, number>(), // "YYYY-MM-DD" → col index
    lastHoverCk: "" as string, // de-dupe mouseover→mouseenter to one fire per cell

    // === 희망근무(Preference) 표시 — 하이브리드 C ===
    // ON 시: 셀에 옅은 배경 틴트 + 우하단 컬러 배지(휴/제/불) + 이름 옆 📝 (메모/제한 있을 때).
    // 📝 클릭 → showPrefDetailModal 로 상세 모달.
    prefShow: false,
    prefByEmployee: {} as Record<
      string,
      {
        employeeId: string;
        vacationDates: Array<string | { date: string; leaveTypeId?: string }>;
        restrictedDates: Array<{ date: string; restrictedShifts: string[]; reason: string }>;
        unavailableDates: Array<{ date: string; reason: string }>;
        preferredDates: Array<{ date: string; preferredShifts: string[]; reason?: string }>;
        notes?: string;
      }
    >,
    showPrefDetailModal: false,
    prefDetailEmployeeId: "" as string,

    // 간호사 이름 옆 📝 클릭 시 뜨는 인라인 메모 popover.
    // 모달(pref-detail)을 대체 — 표가 이미 시프트/날짜/타입을 다 보여주므로 popover 는 텍스트 메모만 다룸.
    //   - 월 단위 notes: 수간호사가 직접 자유 입력 (textarea)
    //   - 날짜별 reason 리스트: 표에서 보이지 않는 텍스트만 모아서 표시 (날짜 + 사유)
    nurseMemoPopover: {
      visible: false,
      employeeId: "",
      employeeName: "",
      notesDraft: "",
      anchorX: 0,
      anchorY: 0,
    },

    // === 희망근무 입력 (수간호사 대신 입력) — pref-input 서브탭 전용 ===
    // 클릭한 셀(또는 리스트 행 → 모달) 의 작성 상태. 저장 전 임시.
    //   category: 'wish' = 희망(원하는 시프트, OFF 포함) / 'constraint' = 제약(전체 불가/특정 시프트 불가) / 'vacation' = 휴가
    //   type: 데이터 모델 매핑 — preferred / restricted / unavailable / vacation
    prefInputPopover: {
      visible: false,
      employeeId: "",
      employeeName: "",
      date: "",
      category: "" as "" | "wish" | "constraint" | "vacation",
      type: "" as "" | "preferred" | "restricted" | "unavailable" | "vacation",
      shifts: [] as string[],
      reason: "",
      anchorX: 0,
      anchorY: 0,
      // 실제 표시 좌표 — anchor 를 뷰포트 안으로 잠근 결과(_clampPrefPopover).
      left: 0,
      top: 0,
    },

    // 희망근무 그리드 셀 선택 상태 — 근무표(selectedCells) 와 패턴은 같지만 의도/액션이 다르므로 분리.
    //   - 클릭/Shift/Cmd/드래그로 다중 선택
    //   - popover 저장 시 모든 선택 셀에 일괄 적용 (한 명+한 날짜 단위가 아니라 다중 적용)
    //   - 데이터 키 포맷은 동일: `${employeeId}:${dateStr}`
    prefSelectedCells: new Set<string>(),
    prefCursorCell: null as { rowIdx: number; colIdx: number } | null,
    prefLastClickedCell: null as { rowIdx: number; colIdx: number } | null,
    prefIsDragging: false,
    prefDragStartCell: null as { rowIdx: number; colIdx: number } | null,
    prefPendingDragTo: null as { rowIdx: number; colIdx: number } | null,
    prefDragRafPending: false,
    prefDragHappened: false,
    prefSelectionVersion: 0,

    undoStack: [] as Map<string, string>[],
    redoStack: [] as Map<string, string>[],
    // 희망근무(pref-input) 탭 undo/redo — prefByEmployee 전체 스냅샷을 쌓는다.
    // 근무표(entries Map) 와 데이터 모델이 달라서 별도 스택. 동작은 동일 (ctrl+z / ctrl+shift+z).
    prefUndoStack: [] as Record<string, unknown>[],
    prefRedoStack: [] as Record<string, unknown>[],
    savedSnapshots: [] as { date: string; auto?: boolean; entries: Record<string, string> }[],
    showSaveToast: "" as string,
    showSnapshotPicker: false,
    daysInMonth: [] as number[],
    cycleDates: [] as string[],
    cycleStartDate: "" as string,
    cycleEndDate: "" as string,
    complianceRate: null as number | null,
    warnings: [] as ConflictWarning[],

    // Attendance overlay
    showAttendance: false,
    attendanceMap: {} as Record<string, string>,
    attendanceTips: {} as Record<string, string>,
    attendanceCheckInHours: {} as Record<string, number>,

    /** 소수 시간 → h:mm 표기 (8.5 → "8:30"). 실근로시간 정산 표기 규칙(2026-07). */
    fmtHM(hours: unknown): string {
      return formatHoursHM(hours);
    },
    attendanceTimes: {} as Record<string, { checkIn: string; checkOut: string }>,
    attMismatches: [] as {
      key: string;
      nurseName: string;
      date: string;
      scheduled: string;
      actual: string;
      nextDate: string | null;
      checkIn: string;
      checkOut: string;
      expectedTime: string;
    }[],
    showMismatchPanel: false,
    attTooltip: { show: false, x: 0, y: 0, text: "" },
    attendanceLoading: false,

    // Preferences (default = next month, computed at init time)
    prefYear: 0,
    prefMonth: 0,
    prefVacationDates: [] as string[],
    prefUnavailableDates: [] as Array<{ date: string; reason: string }>,
    prefRestrictedDates: [] as Array<{ date: string; restrictedShifts: string[]; reason: string }>,
    prefNotes: "",
    prefId: null as string | null,
    prefStatus: "" as string,

    // My Schedule & Attendance (내 근무시간)
    myScheduleEntries: [] as Array<{
      date: string;
      shift: string;
      checkIn: string | null;
      checkOut: string | null;
      actualHours: number | null;
    }>,
    myScheduleLoaded: false,
    mySchedYear: new Date().getFullYear(),
    mySchedMonth: new Date().getMonth() + 1,

    // Profiles
    profileRows: [] as NurseRow[],
    autoRegisterCandidates: [] as Array<{
      id: string;
      employeeNumber: string;
      name: string;
      jobDuty: string | null;
      status: string;
      isTeamMember: boolean;
      selected: boolean;
    }>,
    showAutoRegister: false,
    autoRegisterLoading: false,
    autoRegistering: false,
    autoRegisterResult: null as { added: number; skipped: number } | null,

    // Patient count management
    showPatientCount: false,
    pcWardId: "" as string,
    pcYear: new Date().getFullYear(),
    pcMonth: new Date().getMonth() + 1,
    pcCounts: new Map<string, number>(),
    pcEditing: new Map<string, number>(),
    pcPrevCounts: new Map<string, number>(),
    /** 병동|일자 → 그날 배치된 D/E/N 간호사 수. 비율·위험 판정용(환자수 관리 팝업). */
    pcShiftCounts: new Map<string, { day: number; evening: number; night: number }>(),
    /**
     * 병동|일자 → 환자수 출처("measured" | "estimated").
     * 병상수로 자동 채운 값은 실측이 아니라서 집계·신고에서 빼야 한다 —
     * 값만 저장하면 구분이 사라지므로 저장·조회 양쪽에서 함께 나른다.
     */
    pcSources: new Map<string, WardPatientCountSource>(),
    /**
     * 팝업에서 선택한 병동(pcWardId)의 병상 수.
     * 근무표 쪽 wardBedCount 는 selectedWardId 기준이라, 팝업 안에서 병동을 바꾸면
     * 다른 병동의 병상 수로 채워진다(실측 확인된 버그) — 팝업은 자기 값을 따로 든다.
     */
    pcBedCount: null as number | null,
    /** 일괄 입력 상자의 값. 팝업을 열거나 달을 바꿀 때 전월 평균으로 초기화된다. */
    pcBulkValue: null as number | null,
    /** 저장 전 편집분의 출처 — 직접 입력하면 measured, 일괄 입력이면 bulk. */
    pcPendingSources: new Map<string, WardPatientCountSource>(),
    pcLoading: false,
    pcSaving: false,

    // Shift mapping settings
    availableShifts: [] as Array<{
      id: string;
      code?: string;
      codeColor?: string;
      name: string;
      type: string;
      startTime: string;
      endTime: string;
    }>,
    shiftMappingsForm: {
      threeShift: {
        day: "",
        evening: "",
        night: "",
        dayWeekend: "",
        eveningWeekend: "",
        nightWeekend: "",
      },
      dayDedicated: { day: "", dayWeekend: "" },
      eveningDedicated: { evening: "", eveningWeekend: "" },
      nightDedicated: { night: "", nightWeekend: "" },
    } as Record<string, Record<string, string>>,
    educationPolicyForm: "work_counted" as string,

    // Modals
    showEntryEdit: false,
    editingEntry: {
      employeeId: "",
      date: "",
      assignmentType: "",
      nurseName: "",
      // 클릭한 셀의 viewport rect — popover 를 셀 옆(기본 오른쪽)에 배치하는 기준.
      cellRect: { left: 0, right: 0, top: 0, bottom: 0 },
    },
    showConflicts: false,
    showConfirmDialog: false,
    showShortcutsHelp: false,
    cellCtxMenu: { show: false, x: 0, y: 0 },
    nurseCtxMenu: { show: false, x: 0, y: 0, nurse: null as NurseRow | null },
    // 공용 확인 다이얼로그의 액션. 'confirm' = 작성→확정, 'publish' = 확정→공유.
    // 같은 모달 컴포넌트를 텍스트/핸들러만 분기해서 재사용.
    confirmDialogAction: "confirm" as "confirm" | "publish",
    showAddProfile: false,
    // 간호사 프로필 편집 모달 — workType / wardId / isHeadNurse 만 수정 가능
    // employee 는 식별자이므로 새로 등록/삭제로만 변경
    showEditProfile: false,
    editProfileSaving: false,
    editProfileForm: {
      profileId: "",
      employeeName: "",
      employeeNumber: "",
      workType: "three_shift",
      wardId: "",
      // 소속 스케줄러 — 병동 select 를 바꾸면 그 병동의 기본 스케줄러로 따라간다
      scheduleGroupId: "",
      isHeadNurse: false,
      // 소속 변경 방식: 즉시 변경(false) / 예약 이동(true). 기본 즉시.
      reserveMode: false,
      // 예약 이동 적용 시작일(YYYY-MM-DD)
      reserveDate: "",
      // 모달 오픈 시점의 현재 소속(예약 모드에서 즉시 반영 대상은 이 값으로 유지)
      originalWardId: "",
      originalScheduleGroupId: "",
      // 대기 중 기존 예약(있으면 배너 + 편집 대상)
      pendingReservation: null as PendingReservation | null,
    },
    // 간호사 프로필 삭제 확인 모달
    showDeleteProfileConfirm: false,
    deleteProfileTarget: null as { profileId: string; name: string; employeeNumber: string } | null,
    showChangeWard: false,
    changeWardSaving: false,
    changeWardForm: {
      profileId: "",
      nurseName: "",
      employeeNumber: "",
      currentWardId: "",
      currentWardName: "",
      currentScheduleGroupId: "",
      currentScheduleGroupName: "",
      newWardId: "",
      newScheduleGroupId: "",
    },

    // Schedule Excel upload
    showScheduleUpload: false,
    scheduleUploadSaving: false,
    scheduleUploadForm: {
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      fileName: "" as string,
      fileSize: 0,
      // True while the workbook is being parsed; UI shows the indeterminate
      // progress state. Flips to false once parsing succeeds OR errors.
      isParsing: false,
      error: "" as string,
      // Parsed (employeeId, date, assignmentType) entries — built from the workbook.
      parsedEntries: [] as { employeeId: string; date: string; assignmentType: string }[],
      parsedCount: 0,
    },
    isEditing: false,

    // Add profile form
    newProfile: {
      employeeId: "",
      workType: "three_shift",
      wardId: "",
      scheduleGroupId: "",
      isHeadNurse: false,
    },

    // Employee picker (nurse scheduler add-profile)
    // 2026-09-28 — 한 줄 드롭다운에서 「검색어 + 표」로 넓혔다(팀 관리 「팀 멤버 추가」와 같은 모양).
    // 전에는 이름을 알아야만 고를 수 있었다 — 누가 아직 안 들어왔는지 훑어볼 수가 없었다.
    employeeSearch: "",
    /** 고를 수 있는 후보 전체 — 팝업을 열 때 한 번 받아 두고 화면에서 거른다(서버 왕복 없음). */
    employeeCandidates: [] as Array<{
      id: string;
      employeeNumber: string;
      firstName: string;
      lastName: string;
      jobDuty: string;
      teamNames: string[];
    }>,
    /** 직무 필터 — 빈 값이면 전체 */
    employeeJobFilter: "",
    employeePickerOpen: false,
    employeeSearchLoading: false,
    /** 조직 전체 (직원, 스케줄러) 등록 쌍. openAddProfile 시점에 채워짐. */
    registeredProfilePairs: [] as { employeeId: string; scheduleGroupId: string }[],
    /** 등록 대상 스케줄러에 이미 있는 직원 ID — 드롭다운 disabled + 「이미 등록됨」.
     *  다른 스케줄러에만 있는 직원은 겸임 등록이 가능하므로 막지 않는다. */
    get registeredNurseIds(): string[] {
      const gid = this.newProfile.scheduleGroupId;
      return this.registeredProfilePairs
        .filter((p: { scheduleGroupId: string }) => !gid || p.scheduleGroupId === gid)
        .map((p: { employeeId: string }) => p.employeeId);
    },
    /** 고른 사람들 — 체크박스 다중 선택(2026-09-28). 한 번에 같은 교대 형태로 배정한다. */
    selectedEmployeeIds: [] as string[],
    /** 배정 결과 — 일부만 실패하면 성공한 사람은 반영되고 실패한 사람과 이유가 남는다 */
    addProfileFailures: [] as { name: string; message: string }[],
    addProfileSaving: false,

    // Ward settings & daily patient count
    wardBedCount: null as number | null,
    wardStaffingRatios: null as { day: number; evening: number; night: number } | null,
    wardAideRatio: 0.5,
    wardCaregiverRatio: 0.3,

    // Hospital-wide totals
    /** 의료기관 종별 — getConfig 로드 시 채워진다. 간호등급 표(일반/요양병원) 분기 키 */
    organizationType: null as string | null,
    hospitalNurseCount: 0,
    hospitalPatientCount: 0,
    hospitalPatientByWard: {} as Record<string, number>,
    todayPatientCount: 0,
    dailyPatientCounts: new Map<string, number>(),
    showWardSettings: false,
    wardSettingsInitTab: "basic" as string,
    /** 병동 설정 모달의 교대 매핑·교육 정책 탭이 편집 중인 스케줄러. 열 때 선택 스케줄러로 맞춘다. */
    wsGroupId: "" as string,
    wsGroupLoading: false,
    get wsGroup(): ScheduleGroup | undefined {
      return this.scheduleGroups.find((g: ScheduleGroup) => g.id === this.wsGroupId);
    },
    /** 모달 안 스케줄러 표시 「병동 · 스케줄러」 */
    get wsGroupCaption(): string {
      const g = this.wsGroup;
      return g ? `${g.wardName} · ${scheduleGroupLabel(g)}` : "";
    },
    wardSettingsForm: {
      bedCount: 0,
      ratioDay: 5,
      ratioEvening: 7,
      ratioNight: 10,
      aideRatio: 0.5,
      caregiverRatio: 0.3,
      // 배치 기준 축(1인당 환자 수) — 0 = 미설정. aideRatio(간호사 대비)와 단위가 다르다.
      aidePatientRatio: 0,
      supportPatientRatio: 0,
      rehabSupportPatientRatio: 0,
    },
    // Baselines (JSON snapshots) for dirty tracking — populated by loadSchedulerConfig
    // and refreshed after each successful save so the save button re-disables.
    staffingSettingsSaved: "" as string,
    shiftMappingsSaved: "" as string,
    educationPolicySaved: "" as string,
    // 저장 완료 후 '✓ 저장됨' 인디케이터 표시(탭별). 재수정 시 footer x-effect가 false로 리셋.
    basicJustSaved: false,
    shiftsJustSaved: false,
    educationJustSaved: false,
    cycleJustSaved: false,
    editingPatientDay: null as number | null,
    editingPatientValue: 0,

    // Generation polling
    generating: false,
    generationPoller: null as AsyncPollHandle<{
      status: string;
      metadata: Record<string, unknown>;
    }> | null,

    // SDK accessor
    sdk: Alpine.store("hereby").sdk,

    // --- Lifecycle ---
    /* Pristine-close guard: snapshot each form modal's state the moment it
       opens (Alpine flushes watchers after the open fn's sync populate).
       init()과 wards 페이지의 병동 설정 모달 호스트(데이터 로드 없는 경량
       마운트, tabs/wards.html) 양쪽에서 부른다. */
    registerCloseGuardWatchers() {
      for (const showVar of Object.keys(this.closeGuardForms)) {
        // biome-ignore lint/suspicious/noExplicitAny: Alpine magic property
        (this as any).$watch(showVar, (open: boolean) => {
          if (open) {
            this.closeGuardBaselines[showVar] = this.closeGuardState(showVar);
          }
        });
      }
      // 병동 설정 모달 안에서 다른 스케줄러의 매핑을 편집하고 닫으면 폼에 그 스케줄러 값이 남는다 —
      // 출근 불일치 판정(detectShiftMismatches)은 선택 스케줄러의 매핑을 읽으므로 닫힐 때 되돌린다.
      // biome-ignore lint/suspicious/noExplicitAny: Alpine magic property
      (this as any).$watch("showWardSettings", (open: boolean) => {
        if (open || !this.selectedScheduleGroupId) {
          return;
        }
        if (this.wsGroupId && this.wsGroupId !== this.selectedScheduleGroupId) {
          this.wsGroupId = "";
          this.loadScheduleGroupSettings(this.selectedScheduleGroupId);
        }
      });
    },

    async init() {
      if (this.booted) {
        return;
      }
      this.booted = true;
      this.registerCloseGuardWatchers();
      const store = Alpine.store("hereby");
      while (store?.isLoading) {
        await new Promise((r) => setTimeout(r, 100));
      }

      // Set year/month/date from org timezone (not browser local time)
      const todayStr = store.todayLocal?.() as string | undefined;
      if (todayStr) {
        const [y, m] = todayStr.split("-").map(Number);
        this.incentiveYear = y;
        this.incentiveMonth = m;
        this.treatmentYear = y;
        this.treatmentMonth = m;
        this.treatmentWorkDate = todayStr;
      }

      // 마지막 선택(병동·년·월) 복원 — 해시(#scheduler/<wardId>:<yyyy>-<m>)가 있으면 해시,
      // 없으면 localStorage. 연도는 셀렉트 범위(nurseYears) 안일 때만 받고, 병동은 목록이
      // 온 뒤 applyWardList 가 검증한다(삭제·비활성 병동은 목록에 없어 첫 병동으로 폴백).
      const remembered = this.readSchedulerSelection();
      if (remembered) {
        if (
          typeof remembered.year === "number" &&
          this.nurseYears.includes(remembered.year) &&
          typeof remembered.month === "number" &&
          remembered.month >= 1 &&
          remembered.month <= 12
        ) {
          this.period = { year: remembered.year, month: remembered.month };
        }
        this.restoreWardId = remembered.wardId ?? "";
      }

      // Compute prefs default = next month (relative to current date)
      const now = new Date();
      let nextY = now.getFullYear();
      let nextM = now.getMonth() + 2;
      if (nextM > 12) {
        nextM = 1;
        nextY++;
      }
      this.prefYear = nextY;
      this.prefMonth = nextM;

      // Detect nurse-scheduler role/access
      this.isNurseManager = false;
      this.canReadScheduler = false;
      if (store?.currentUser?.isSystemAdmin) {
        this.isNurseManager = true;
      } else if (store?.hasPermission?.("nurse-scheduler:update")) {
        this.isNurseManager = true;
      }
      // 조회 전용 권한 — 바꾸지는 못해도 인력·근무표는 본다
      this.canReadScheduler =
        this.isNurseManager || !!store?.hasPermission?.("nurse-scheduler:read");

      // Embedded in admin: the admin router owns the top-level route. The
      // scheduler keeps its own internal sub-tab state (schedule/profiles)
      // and does not read from or bind to the URL/history here.

      /* 왕복 1 — 역할 확인(프로필) · 스케줄러 설정 · 병동 목록은 서로 독립이라 한 배치로 쏜다.
         병동 목록만은 역할(myWardIds) 로 걸러야 하므로 fetch 와 apply 를 나눠, 셋이 모두
         돌아온 뒤 applyWardList 로 반영한다. 팀 정보(loadMyTeams → loadTeamMembers) 는
         스케줄러 화면이 쓰지 않으므로 그리드를 막지 않게 뒤에서 따로 돈다. */
      const teamsPromise = this.loadMyTeams()
        .then(() => this.loadTeamMembers())
        .catch(() => undefined);
      const [, , wardResult, groupResult] = await Promise.all([
        this.checkNurseStatus().catch(() => undefined),
        this.loadSchedulerConfig(),
        this.fetchWardList(),
        this.fetchScheduleGroups(),
      ]);
      this.applyScheduleGroupList(groupResult);
      this.applyWardList(wardResult);
      this.roleChecked = true;

      // 왕복 2 이후 — 활성 탭 데이터 (스케줄러 탭이면 그리드까지 한 배치).
      await this.loadTabData();
      await teamsPromise;
    },

    switchTab(tabId: string) {
      // 'scheduler' is a parent entry — redirect to the first available sub-tab,
      // or stay on the current sub-tab if already in scheduler.
      let target = tabId;
      if (tabId === "scheduler") {
        if (this.activeTab === "scheduler") {
          return;
        }
        const subs = this.schedulerSubTabs;
        if (subs.length === 0) {
          return;
        }
        target = subs[0].id;
      }
      this.activeTab = target;
      // Embedded in admin: sub-tab switching is internal only — the admin
      // router owns the URL, so we do not push history here.
      this.loadTabData();
    },

    async loadTabData() {
      this.rememberSchedulerSelection();
      this.loading = true;
      // 병동 select 가 바뀌었으면 스케줄러 선택도 그 병동 것으로 — 아래 로더는 전부 이 값을 읽는다.
      this.syncScheduleGroupSelection();
      try {
        if (this.activeTab === "overview") {
          await this.loadDashboard();
        } else if (this.activeTab === "leave") {
          await this.loadLeaveRequests();
        } else if (this.activeTab === "overtime") {
          await this.loadOvertimeRecords();
        } else if (this.activeTab === "incentives") {
          await this.loadIncentives();
        } else if (this.activeTab === "treatment") {
          // Ensure team members are available for therapist search
          if (this.teamMembers.length === 0) {
            await this.loadTeamMembers();
          }
          await this.loadTreatmentCategories();
          await this.loadTreatmentRecords();
        } else if (this.activeTab === "documents") {
          await this.loadDocuments();
        } else if (this.activeTab === "certificates") {
          await this.loadCertificates();
        } else if (this.activeTab === "document-management") {
          await this.loadDocMgmt();
        } else if (this.activeTab === "shifts") {
          await this.loadShifts();
        } else if (this.activeTab === "scheduler") {
          // init() 이 병동 목록을 먼저 채우지만, 탭 전환으로 직접 들어온 경우 등
          // 아직 비어 있으면 여기서 채운다 (loadSchedule 은 selectedWardId 없이는 조기 반환).
          if (this.wards.length === 0 || !this.selectedWardId) {
            await Promise.all([this.loadSchedulerConfig(), this.loadWards()]);
          }
          // 병동 설정·근무 유형·오늘 환자수·병원 합계·근무표는 서로 독립 — 직렬로 기다리면
          // 왕복 5~6회(+병동 수) 가 되던 것을 한 배치(1왕복)로 모은다. 그리드는 loadSchedule 이
          // 끝나는 즉시 뜨고(내부에서 loading 을 내림), 합계 등은 뒤따라 채워진다.
          // 스케줄러 설정(교대 매핑)은 출근 불일치 판정(detectShiftMismatches)이 읽으므로 같은 배치에.
          await Promise.all([
            this.loadWardSettings(),
            this.loadScheduleGroupSettings(this.selectedScheduleGroupId),
            this.loadTodayPatientCount(),
            this.loadHospitalTotals(),
            this.loadSchedule(),
          ]);
        } else if (this.activeTab === "preferences") {
          if (this.wards.length === 0 || !this.selectedWardId) {
            await this.loadSchedulerConfig();
            await this.loadWards();
          }
          await this.loadWardSettings();
          await Promise.allSettled([this.loadMyPreference(), this.loadMySchedule()]);
        } else if (this.activeTab === "pref-input") {
          if (this.wards.length === 0 || !this.selectedWardId) {
            await this.loadSchedulerConfig();
            await this.loadWards();
          }
          await this.loadPrefInputData();
        } else if (this.activeTab === "profiles") {
          if (this.wards.length === 0) {
            await this.loadSchedulerConfig();
            await this.loadWards();
          }
          await this.loadProfiles();
        }
      } catch {
        // silently handle
      }
      this.loading = false;
    },

    // --- Data Loading ---
    async loadMyTeams() {
      try {
        const result = await this.sdk.teamManager.myTeams();
        this.myTeams = (result || []) as TeamInfo[];
      } catch {
        this.myTeams = [];
      }
    },

    async loadTeamMembers() {
      try {
        const teamId = this.selectedTeamId || undefined;
        const result = await this.sdk.teamManager.teamMembers(teamId);
        const raw = (result || []) as TeamMemberInfo[];
        // Deduplicate by employeeId (same employee may be in multiple teams)
        const seen = new Set<string>();
        this.teamMembers = raw.filter((m) => {
          if (seen.has(m.employeeId)) {
            return false;
          }
          seen.add(m.employeeId);
          return true;
        });
      } catch {
        this.teamMembers = [];
      }
    },

    async loadDashboard() {
      try {
        const result = await this.sdk.teamManager.dashboard();
        this.pendingCounts = {
          leave: result.pendingLeave ?? 0,
          overtime: result.pendingOvertime ?? 0,
          incentive: result.pendingIncentive ?? 0,
          document: result.pendingDocument ?? 0,
          certificate: result.pendingCertificate ?? 0,
        };
      } catch {
        this.pendingCounts = { leave: 0, overtime: 0, incentive: 0, document: 0, certificate: 0 };
      }
    },

    async loadLeaveRequests() {
      this.selectedLeaveIds = [];
      try {
        const result = await this.sdk.teamManager.listLeaveRequests({
          status: this.leaveFilter,
          teamId: this.selectedTeamId || undefined,
          pagination: { page: this.leavePagination.page, limit: 20, sortOrder: SortOrder.DESC },
        });
        this.leaveRequests = result.items || [];
        this.leavePagination = {
          page: result.page,
          totalPages: result.totalPages,
          totalCount: result.totalCount,
        };
      } catch {
        this.leaveRequests = [];
      }
    },

    async loadOvertimeRecords() {
      this.selectedOvertimeIds = [];
      try {
        const result = await this.sdk.teamManager.listOvertime({
          status: this.overtimeFilter,
          teamId: this.selectedTeamId || undefined,
          pagination: { page: this.overtimePagination.page, limit: 20, sortOrder: SortOrder.DESC },
        });
        this.overtimeRecords = result.items || [];
        this.overtimePagination = {
          page: result.page,
          totalPages: result.totalPages,
          totalCount: result.totalCount,
        };
      } catch {
        this.overtimeRecords = [];
      }
    },

    async loadIncentives() {
      this.selectedIncentiveIds = [];
      try {
        const [result, attendance] = await Promise.all([
          this.sdk.teamManager.listIncentives({
            year: this.incentiveYear,
            month: this.incentiveMonth,
            teamId: this.selectedTeamId || undefined,
            pagination: {
              page: this.incentivePagination.page,
              limit: 100,
              sortOrder: SortOrder.DESC,
            },
          }),
          this.sdk.teamManager.attendanceSummary(this.incentiveYear, this.incentiveMonth),
        ]);
        this.incentives = result.items || [];
        this.incentivePagination = {
          page: result.page,
          totalPages: result.totalPages,
          totalCount: result.totalCount,
        };
        this.attendanceSummary = (attendance || []) as typeof this.attendanceSummary;

        // Load preset categories once
        if (!this.presetCategoriesLoaded) {
          try {
            const cats = await this.sdk.teamManager.incentivePresetCategories();
            this.presetCategories = (cats || []) as typeof this.presetCategories;
          } catch {
            this.presetCategories = [];
          }
          this.presetCategoriesLoaded = true;
        }
      } catch {
        this.incentives = [];
      }
    },

    // Computed: split incentives by category verifiability
    get verifiableIncentives() {
      const verifiableCatNames = new Set(
        this.presetCategories.filter((c) => c.isAttendanceVerifiable).map((c) => c.name),
      );
      // biome-ignore lint/suspicious/noExplicitAny: Alpine data shape
      return this.incentives.filter((i: any) => verifiableCatNames.has(i.incentiveType));
    },

    get manualReviewIncentives() {
      const verifiableCatNames = new Set(
        this.presetCategories.filter((c) => c.isAttendanceVerifiable).map((c) => c.name),
      );
      // biome-ignore lint/suspicious/noExplicitAny: Alpine data shape
      return this.incentives.filter((i: any) => !verifiableCatNames.has(i.incentiveType));
    },

    // Get attendance count for an employee
    getAttendanceCount(employeeId: string): number {
      const entry = this.attendanceSummary.find((a) => a.employeeId === employeeId);
      return entry?.total ?? 0;
    },

    // Get attendance breakdown for an employee
    getAttendanceTypes(employeeId: string): Record<string, number> {
      const entry = this.attendanceSummary.find((a) => a.employeeId === employeeId);
      return entry?.types ?? {};
    },

    // Summarize verifiable incentives by employee for quick count comparison
    get verifiableSummaryByEmployee() {
      const map = new Map<
        string,
        { employeeId: string; employeeName: string; incentiveCount: number; pendingCount: number }
      >();
      for (const item of this.verifiableIncentives) {
        // biome-ignore lint/suspicious/noExplicitAny: Alpine data shape
        const i = item as any;
        let entry = map.get(i.employeeId);
        if (!entry) {
          entry = {
            employeeId: i.employeeId,
            employeeName: i.employeeName,
            incentiveCount: 0,
            pendingCount: 0,
          };
          map.set(i.employeeId, entry);
        }
        entry.incentiveCount += 1;
        if (i.status === "pending") {
          entry.pendingCount += 1;
        }
      }
      return Array.from(map.values());
    },

    // --- Actions: Leave ---
    async approveLeave(id: string) {
      try {
        await this.sdk.teamManager.approveLeaveRequests([id]);
        await this.loadLeaveRequests();
        await this.loadDashboard();
      } catch (e: unknown) {
        showError(
          `${_t(this, "team_mgr.approve_failed")}: ${e instanceof Error ? e.message : _t(this, "team_mgr.unknown_error")}`,
        );
      }
    },

    async revertLeave(id: string) {
      if (
        !(await askConfirm({
          message: "승인/반려를 취소하고 대기 상태로 되돌립니다. 계속하시겠습니까?",
          tone: "danger",
        }))
      ) {
        return;
      }
      try {
        await this.sdk.teamManager.revertLeaveRequests([id]);
        await this.loadLeaveRequests();
        await this.loadDashboard();
      } catch (e: unknown) {
        showError(`취소 실패: ${e instanceof Error ? e.message : "알 수 없는 오류"}`);
      }
    },

    async bulkApproveLeave() {
      if (this.selectedLeaveIds.length === 0) {
        return;
      }
      try {
        await this.sdk.teamManager.approveLeaveRequests(this.selectedLeaveIds);
        await this.loadLeaveRequests();
        await this.loadDashboard();
      } catch (e: unknown) {
        showError(
          `${_t(this, "team_mgr.approve_failed")}: ${e instanceof Error ? e.message : _t(this, "team_mgr.unknown_error")}`,
        );
      }
    },

    // --- Actions: Overtime ---
    async approveOvertime(id: string) {
      try {
        await this.sdk.teamManager.approveOvertime([id]);
        await this.loadOvertimeRecords();
        await this.loadDashboard();
      } catch (e: unknown) {
        showError(
          `${_t(this, "team_mgr.approve_failed")}: ${e instanceof Error ? e.message : _t(this, "team_mgr.unknown_error")}`,
        );
      }
    },

    async bulkApproveOvertime() {
      if (this.selectedOvertimeIds.length === 0) {
        return;
      }
      try {
        await this.sdk.teamManager.approveOvertime(this.selectedOvertimeIds);
        await this.loadOvertimeRecords();
        await this.loadDashboard();
      } catch (e: unknown) {
        showError(
          `${_t(this, "team_mgr.approve_failed")}: ${e instanceof Error ? e.message : _t(this, "team_mgr.unknown_error")}`,
        );
      }
    },

    async approveOvertimeReport(id: string) {
      try {
        await this.sdk.teamManager.approveOvertimeReport([id]);
        await this.loadOvertimeRecords();
        await this.loadDashboard();
      } catch (e: unknown) {
        showError(`보고서 승인 실패: ${e instanceof Error ? e.message : "알 수 없는 오류"}`);
      }
    },

    viewOvertime(item: Record<string, unknown>) {
      this.overtimeViewItem = item;
    },

    overtimeStatusLabel(status: string): string {
      const map: Record<string, string> = {
        pending: "대기",
        approved: "승인",
        rejected: "반려",
        cancelled: "취소",
        report_pending: "보고서 대기",
        report_approved: "보고서 승인",
        report_rejected: "보고서 반려",
      };
      return map[status] || status;
    },

    overtimeStatusBadgeClass(status: string): string {
      const map: Record<string, string> = {
        pending: "badge-pending",
        approved: "badge-approved",
        rejected: "badge-rejected",
        cancelled: "badge-rejected",
        report_pending: "badge-pending",
        report_approved: "badge-approved",
        report_rejected: "badge-rejected",
      };
      return map[status] || "badge-pending";
    },

    // --- Actions: Incentive ---
    async approveIncentive(id: string) {
      try {
        await this.sdk.teamManager.approveIncentives([id]);
        await this.loadIncentives();
        await this.loadDashboard();
      } catch (e: unknown) {
        showError(
          `${_t(this, "team_mgr.approve_failed")}: ${e instanceof Error ? e.message : _t(this, "team_mgr.unknown_error")}`,
        );
      }
    },

    async bulkApproveIncentives() {
      if (this.selectedIncentiveIds.length === 0) {
        return;
      }
      try {
        await this.sdk.teamManager.approveIncentives(this.selectedIncentiveIds);
        await this.loadIncentives();
        await this.loadDashboard();
      } catch (e: unknown) {
        showError(
          `${_t(this, "team_mgr.approve_failed")}: ${e instanceof Error ? e.message : _t(this, "team_mgr.unknown_error")}`,
        );
      }
    },

    // --- Treatment Incentive (치료 인센티브) ---
    async loadTreatmentRecords() {
      try {
        const result = await this.sdk.teamManager.listIncentives({
          year: this.treatmentYear,
          month: this.treatmentMonth,
          teamId: this.selectedTeamId || undefined,
          pagination: { page: 1, limit: 50, sortOrder: SortOrder.DESC },
        });
        this.treatmentRecords = (result.items || []).filter(
          (r) => r.metadata?.type === "treatment_incentive",
        );
      } catch {
        this.treatmentRecords = [];
      }
    },

    async loadTreatmentCategories() {
      if (this.treatmentCategoriesLoaded) {
        return;
      }
      try {
        const result = await this.sdk.teamManager.treatmentCategories();
        // biome-ignore lint/suspicious/noExplicitAny: API response shape
        const categories = (result || []) as any[];

        if (categories.length > 0) {
          this.treatmentCategories = categories.map(
            // biome-ignore lint/suspicious/noExplicitAny: API response shape
            (cat: any) => ({
              id: cat.id,
              label: cat.label,
              hospitalRatio: cat.hospitalRatio,
              therapistRatio: cat.therapistRatio,
              // biome-ignore lint/suspicious/noExplicitAny: API response shape
              items: (cat.items || []).map((item: any) => ({
                code: item.code,
                name: item.name,
                ratio: item.ratio || "1:1",
                unitPrice: Number(item.unitPrice) || 0,
                count: 0,
              })),
            }),
          );
        }
        // If DB returned nothing, treatmentCategories stays empty (shows empty state)

        if (this.treatmentCategories.length > 0) {
          this.selectedCategoryId = this.treatmentCategories[0].id;
          this.selectedDetailCategoryId = this.treatmentCategories[0].id;
        }
        this.treatmentCategoriesLoaded = true;
      } catch {
        // Keep static defaults on error
        this.treatmentCategoriesLoaded = true;
      }
    },

    get selectedCategory(): TreatmentCategoryState | null {
      if (this.treatmentCategories.length === 0) {
        return null;
      }
      return (
        this.treatmentCategories.find((c) => c.id === this.selectedCategoryId) ??
        this.treatmentCategories[0]
      );
    },

    get treatmentTotalBilling(): number {
      if (!this.selectedCategory) {
        return 0;
      }
      return this.selectedCategory.items.reduce(
        (sum: number, item: TreatmentItem) => sum + item.count * item.unitPrice,
        0,
      );
    },

    get treatmentHospitalShare(): number {
      if (!this.selectedCategory) {
        return 0;
      }
      return Math.round(this.treatmentTotalBilling * this.selectedCategory.hospitalRatio);
    },

    get treatmentTherapistShare(): number {
      if (!this.selectedCategory) {
        return 0;
      }
      return Math.round(this.treatmentTotalBilling * this.selectedCategory.therapistRatio);
    },

    get treatmentPerPersonAmount(): number {
      const count = this.treatmentTherapists.length;
      if (count === 0) {
        return 0;
      }
      return Math.round(this.treatmentTherapistShare / count);
    },

    get treatmentSplitRatio(): number {
      const count = this.treatmentTherapists.length;
      if (count <= 1) {
        return 1;
      }
      return +(1 / count).toFixed(2);
    },

    /**
     * Check if a date or schedule day-number is a weekend.
     * Accepts a YYYY-MM-DD string (treatment-incentive flow) or a day-of-month
     * number (nurse schedule grid — resolved through cycleDates lookup).
     */
    isWeekend(value: string | number): boolean {
      if (typeof value === "number") {
        const ds = this.dayToDateStr(value);
        if (ds) {
          const dow = new Date(`${ds}T00:00:00`).getDay();
          return dow === 0 || dow === 6;
        }
        const date = new Date(this.period.year, this.period.month - 1, value);
        return date.getDay() === 0 || date.getDay() === 6;
      }
      if (!value) {
        return false;
      }
      const d = new Date(value);
      const day = d.getDay();
      return day === 0 || day === 6;
    },

    /**
     * 한국 공휴일 여부 — schedule grid 헤더에서 빨간색·툴팁으로 표시할 때 사용.
     * `value`가 숫자면 cycleDates의 해당 인덱스 날짜를, 문자열이면 YYYY-MM-DD로 직접 조회.
     */
    isHoliday(value: string | number): boolean {
      if (typeof value === "number") {
        const ds = this.dayToDateStr(value);
        return ds ? isHoliday(ds) : false;
      }
      return value ? isHoliday(value) : false;
    },

    /** 공휴일명 (툴팁/라벨용). 평일이면 빈 문자열. */
    holidayName(value: string | number): string {
      if (typeof value === "number") {
        const idx = this.daysInMonth.indexOf(value);
        const ds = idx >= 0 ? this.cycleDates[idx] : "";
        return ds ? getHolidayName(ds) : "";
      }
      return value ? getHolidayName(value) : "";
    },

    getFilteredTherapists(): TeamMemberInfo[] {
      const q = this.therapistSearch.trim().toLowerCase();
      if (!q) {
        return this.teamMembers;
      }
      return this.teamMembers.filter((m) => {
        const name = (m.name || "").toLowerCase();
        const num = (m.employeeNumber || "").toLowerCase();
        return name.includes(q) || num.includes(q);
      });
    },

    toggleTherapist(employeeId: string) {
      const idx = this.treatmentTherapists.indexOf(employeeId);
      if (idx >= 0) {
        this.treatmentTherapists.splice(idx, 1);
      } else if (this.treatmentTherapists.length < 2) {
        this.treatmentTherapists.push(employeeId);
      }
    },

    resetTreatmentForm() {
      for (const cat of this.treatmentCategories) {
        for (const item of cat.items) {
          item.count = 0;
        }
      }
      this.treatmentTherapists = [];
      this.treatmentWorkDate = "";
      this.treatmentNotes = "";
      this.therapistSearch = "";
    },

    get canSubmitTreatment(): boolean {
      return (
        this.treatmentWorkDate !== "" &&
        this.treatmentTherapists.length > 0 &&
        this.treatmentTotalBilling > 0
      );
    },

    async submitTreatmentIncentive() {
      if (!this.canSubmitTreatment) {
        return;
      }
      this.treatmentSaving = true;

      const workDate = new Date(this.treatmentWorkDate);
      const year = workDate.getFullYear();
      const month = workDate.getMonth() + 1;
      const cat = this.selectedCategory;
      if (!cat) {
        return;
      }

      const breakdown = cat.items
        .filter((item: TreatmentItem) => item.count > 0)
        .map((item: TreatmentItem) => ({
          code: item.code,
          name: item.name,
          unitPrice: item.unitPrice,
          count: item.count,
        }));

      // Hospital ratio derived from the category's pre-computed shares.
      // Falls back to 0.5 when the category hasn't surfaced one yet.
      const hospitalRatio =
        this.treatmentTotalBilling > 0
          ? this.treatmentHospitalShare / this.treatmentTotalBilling
          : 0.5;

      try {
        // Single endpoint owns the fan-out into N FixedIncentive rows
        // plus the sessionId assignment. Replaces the previous
        // per-therapist `createIncentive` loop.
        await this.sdk.fixedIncentive.upsertTreatmentSession({
          year,
          month,
          workDate,
          category: "rehabilitation",
          incentiveType: cat.label,
          categoryId: cat.id,
          hospitalRatio,
          notes: this.treatmentNotes || undefined,
          breakdown,
          therapists: this.treatmentTherapists,
        });

        this.resetTreatmentForm();
        await this.loadTreatmentRecords();
        await this.loadDashboard();
        showAlert(_t(this, "team_mgr.treatment_registered"));
      } catch (e: unknown) {
        showError(
          `${_t(this, "team_mgr.register_failed")}: ${e instanceof Error ? e.message : _t(this, "team_mgr.unknown_error")}`,
        );
      }
      this.treatmentSaving = false;
    },

    treatmentMemberName(employeeId: string): string {
      const member = this.teamMembers.find((m) => m.employeeId === employeeId);
      return member ? member.name : "";
    },

    getMonthlySummary(): {
      totalCount: number;
      totalAmount: number;
      pendingCount: number;
      pendingIds: string[];
    } {
      let totalCount = 0;
      let totalAmount = 0;
      let pendingCount = 0;
      const pendingIds: string[] = [];
      for (const rec of this.treatmentRecords) {
        totalCount += 1;
        totalAmount += Number(rec.totalAmount) || 0;
        if (rec.status === "pending") {
          pendingCount += 1;
          pendingIds.push(rec.id as string);
        }
      }
      return { totalCount, totalAmount, pendingCount, pendingIds };
    },

    // Group treatment records by sessionId — therapists who worked together
    // (same date, same category) appear as a single row.
    get treatmentDetailPivot(): {
      itemNames: string[];
      rows: {
        key: string;
        workDate: string;
        categoryName: string;
        therapistsLabel: string;
        counts: Record<string, number>;
        status: string;
        recordIds: string[];
      }[];
    } {
      // Aggregate by sessionId (or fallback to id when sessionId is missing)
      const groupMap = new Map<
        string,
        {
          key: string;
          workDate: string;
          categoryName: string;
          therapists: string[];
          counts: Record<string, number>;
          status: string;
          recordIds: string[];
        }
      >();
      const itemSet = new Set<string>();

      for (const rec of this.treatmentRecords) {
        // biome-ignore lint/suspicious/noExplicitAny: record shape varies
        const r = rec as any;
        const sessionKey = r.sessionId || r.id;
        const member = this.teamMembers.find((m) => m.employeeId === r.employeeId);
        const empNumber = r.employeeNumber || member?.employeeNumber || "";
        const therapistLabel = empNumber ? `${r.employeeName}(${empNumber})` : r.employeeName;

        let group = groupMap.get(sessionKey);
        if (!group) {
          group = {
            key: sessionKey,
            workDate: r.workDate,
            categoryName: r.incentiveType ?? "",
            therapists: [],
            counts: {},
            status: r.status,
            recordIds: [],
          };
          groupMap.set(sessionKey, group);

          // Breakdown is shared across all therapists in the session — only
          // count it once per session.
          const breakdown = r.metadata?.breakdown as
            | { code: string; name: string; count: number }[]
            | undefined;
          if (Array.isArray(breakdown) && breakdown.length > 0) {
            for (const b of breakdown) {
              if (!b.count) {
                continue;
              }
              itemSet.add(b.name);
              group.counts[b.name] = b.count;
            }
          } else {
            itemSet.add("-");
            group.counts["-"] = r.unitCount ?? 0;
          }
        }

        if (!group.therapists.includes(therapistLabel)) {
          group.therapists.push(therapistLabel);
        }
        group.recordIds.push(r.id);
      }

      const itemNames = Array.from(itemSet).sort();
      const rows = Array.from(groupMap.values())
        .map((g) => ({
          key: g.key,
          workDate: g.workDate,
          categoryName: g.categoryName,
          therapistsLabel: g.therapists.join(", "),
          counts: g.counts,
          status: g.status,
          recordIds: g.recordIds,
        }))
        .sort((a, b) => (a.workDate < b.workDate ? 1 : -1));
      return { itemNames, rows };
    },

    // Helper: rows filtered to a specific category label (for tab counts)
    treatmentDetailRowsForCategory(categoryLabel: string) {
      return this.treatmentDetailPivot.rows.filter((r) => r.categoryName === categoryLabel);
    },

    // Currently selected detail category (resolves selectedDetailCategoryId → category state)
    get selectedDetailCategory(): TreatmentCategoryState | null {
      if (this.treatmentCategories.length === 0) {
        return null;
      }
      return (
        this.treatmentCategories.find((c) => c.id === this.selectedDetailCategoryId) ??
        this.treatmentCategories[0]
      );
    },

    // Rows for the currently selected detail category
    get selectedDetailRows() {
      const cat = this.selectedDetailCategory;
      if (!cat) {
        return [];
      }
      return this.treatmentDetailPivot.rows.filter((r) => r.categoryName === cat.label);
    },

    // Item names for the currently selected detail category — pulled from the
    // category definition so item ordering and presence match the form.
    get selectedDetailItemNames(): string[] {
      const cat = this.selectedDetailCategory;
      if (!cat) {
        return [];
      }
      // Restrict to items that actually appear in the filtered rows so empty
      // columns are hidden.
      const present = new Set<string>();
      for (const row of this.selectedDetailRows) {
        for (const name of Object.keys(row.counts)) {
          present.add(name);
        }
      }
      return cat.items.map((i) => i.name).filter((n) => present.has(n));
    },

    async bulkApproveTreatmentRecords() {
      const { pendingIds } = this.getMonthlySummary();
      if (pendingIds.length === 0) {
        return;
      }
      try {
        await this.sdk.teamManager.approveIncentives(pendingIds);
        await this.loadTreatmentRecords();
        await this.loadDashboard();
      } catch (e: unknown) {
        showError(
          `${_t(this, "team_mgr.approve_failed")}: ${e instanceof Error ? e.message : _t(this, "team_mgr.unknown_error")}`,
        );
      }
    },

    // --- Reject Modal ---
    openRejectModal(type: string, ids: string[]) {
      this.rejectTarget = { type, ids };
      this.rejectReason = "";
      this.showRejectModal = true;
    },

    async submitReject() {
      if (!this.rejectReason.trim()) {
        return;
      }
      try {
        if (this.rejectTarget.type === "leave") {
          await this.sdk.teamManager.rejectLeaveRequests(this.rejectTarget.ids, this.rejectReason);
          await this.loadLeaveRequests();
        } else if (this.rejectTarget.type === "overtime") {
          await this.sdk.teamManager.rejectOvertime(this.rejectTarget.ids, this.rejectReason);
          await this.loadOvertimeRecords();
        } else if (this.rejectTarget.type === "overtime_report") {
          await this.sdk.teamManager.rejectOvertimeReport(this.rejectTarget.ids, this.rejectReason);
          await this.loadOvertimeRecords();
        } else if (this.rejectTarget.type === "document") {
          await this.sdk.teamManager.rejectDocuments(this.rejectTarget.ids, this.rejectReason);
          this.selectedDocumentIds = [];
          await this.loadDocuments();
        } else if (this.rejectTarget.type === "certificate") {
          await this.sdk.teamManager.rejectCertificates(this.rejectTarget.ids, this.rejectReason);
          this.selectedCertificateIds = [];
          await this.loadCertificates();
        } else if (this.rejectTarget.type.startsWith("doc_mgmt:")) {
          const source = this.rejectTarget.type.split(":")[1] as "certificate" | "document";
          for (const id of this.rejectTarget.ids) {
            await this.sdk.teamManager.rejectDocumentManagement(id, source, this.rejectReason);
          }
          await this.loadDocMgmt();
        }
        await this.loadDashboard();
        this.showRejectModal = false;
        this.rejectReason = "";
      } catch (e: unknown) {
        showError(
          `${_t(this, "team_mgr.reject_failed")}: ${e instanceof Error ? e.message : _t(this, "team_mgr.unknown_error")}`,
        );
      }
    },

    // --- Bulk select helpers ---
    toggleAllLeave(event: Event) {
      const checked = (event.target as HTMLInputElement).checked;
      this.selectedLeaveIds = checked
        ? this.leaveRequests.filter((r) => r.status === "pending").map((r) => r.id as string)
        : [];
    },

    toggleAllOvertime(event: Event) {
      const checked = (event.target as HTMLInputElement).checked;
      this.selectedOvertimeIds = checked
        ? this.overtimeRecords.filter((r) => r.status === "pending").map((r) => r.id as string)
        : [];
    },

    toggleAllIncentives(event: Event) {
      const checked = (event.target as HTMLInputElement).checked;
      this.selectedIncentiveIds = checked
        ? this.incentives.filter((r) => r.status === "pending").map((r) => r.id as string)
        : [];
    },

    // --- Shift Management ---
    async loadShifts() {
      try {
        const result = await this.sdk.shift.list();
        this.shifts = result?.items || [];
      } catch {
        this.shifts = [];
      }
    },

    openAddShift() {
      this.editingShiftId = "";
      this.shiftForm = {
        code: "",
        name: "",
        description: "",
        type: "day",
        startTime: "09:00",
        endTime: "18:00",
        workHours: 8,
        breakHours: 1,
        overtimeRate: 1.5,
        nightDifferentialRate: undefined,
        weekendDifferentialRate: undefined,
        holidayDifferentialRate: undefined,
        applicableDays: {
          monday: true,
          tuesday: true,
          wednesday: true,
          thursday: true,
          friday: true,
          saturday: false,
          sunday: false,
        },
      };
      this.showShiftModal = true;
    },

    openEditShift(shift: Record<string, unknown>) {
      this.editingShiftId = shift.id as string;
      const days = (shift.applicableDays as Record<string, boolean>) || {};
      this.shiftForm = {
        code: (shift.code as string) || "",
        name: (shift.name as string) || "",
        description: (shift.description as string) || "",
        type: (shift.type as string) || "day",
        startTime: ((shift.startTime as string) || "09:00:00").slice(0, 5),
        endTime: ((shift.endTime as string) || "18:00:00").slice(0, 5),
        workHours: Number(shift.workHours) || 8,
        breakHours: Number(shift.breakHours) || 1,
        overtimeRate: Number(shift.overtimeRate) || 1.5,
        nightDifferentialRate: shift.nightDifferentialRate
          ? Number(shift.nightDifferentialRate)
          : undefined,
        weekendDifferentialRate: shift.weekendDifferentialRate
          ? Number(shift.weekendDifferentialRate)
          : undefined,
        holidayDifferentialRate: shift.holidayDifferentialRate
          ? Number(shift.holidayDifferentialRate)
          : undefined,
        applicableDays: {
          monday: days.monday ?? true,
          tuesday: days.tuesday ?? true,
          wednesday: days.wednesday ?? true,
          thursday: days.thursday ?? true,
          friday: days.friday ?? true,
          saturday: days.saturday ?? false,
          sunday: days.sunday ?? false,
        },
      };
      this.showShiftModal = true;
    },

    async saveShift() {
      if (!(this.shiftForm.code && this.shiftForm.name)) {
        return;
      }
      this.shiftSaving = true;
      try {
        // FIXME(타입 복구 중 발견): shift create/update 입력은 ShiftType enum을 기대하지만 scheduler UI form은 문자열 값을 보관함. 입력 스키마 정렬 후 any 제거.
        // biome-ignore lint/suspicious/noExplicitAny: SDK input type mismatch during getter migration
        const payload: any = { ...this.shiftForm };
        if (this.editingShiftId) {
          await this.sdk.shift.update({ id: this.editingShiftId, ...payload });
        } else {
          await this.sdk.shift.create(payload);
        }
        this.showShiftModal = false;
        await this.loadShifts();
      } catch (err) {
        showError(
          `${_t(this, "team_mgr.save_failed")}: ${err instanceof Error ? err.message : String(err)}`,
        );
      }
      this.shiftSaving = false;
    },

    async deleteShift(id: string) {
      if (!(await askConfirm(_t(this, "team_mgr.confirm_delete_shift")))) {
        return;
      }
      try {
        await this.sdk.shift.delete(id);
        await this.loadShifts();
      } catch (err) {
        showError(
          `${_t(this, "team_mgr.delete_failed")}: ${err instanceof Error ? err.message : String(err)}`,
        );
      }
    },

    shiftTypeLabel(type: string): string {
      const key = SHIFT_TYPE_KEYS[type];
      return key ? _t(this, key) : type;
    },

    dayLabel(day: string): string {
      const key = DAY_KEYS[day];
      return key ? _t(this, key) : day;
    },

    applicableDaysText(days: Record<string, boolean> | null): string {
      if (!days) {
        return "-";
      }
      const order = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
      return order
        .filter((d) => days[d])
        .map((d) => {
          const key = DAY_KEYS[d];
          return key ? _t(this, key) : d;
        })
        .join(" ");
    },

    // --- Formatters ---
    leaveTypeLabel(type: string): string {
      // Prefer the org-configured leave reason name when available so custom
      // leave types render as human-friendly labels instead of raw ids.
      const reasons = Alpine.store("hereby")?.currentUser?.leaveReasons as
        | { id: string; name: string }[]
        | undefined;
      const dynamicLabel = reasons?.find((reason) => reason.id === type)?.name;
      if (dynamicLabel) {
        return dynamicLabel;
      }

      const key = LEAVE_TYPE_KEYS[type];
      if (key) {
        return _t(this, key);
      }
      // 커스텀 휴가 유형 id(lr_…)인데 org 목록에 없으면 = 삭제된 유형 → "삭제"
      return type.startsWith("lr_") ? "삭제" : type;
    },

    statusLabel(status: string): string {
      const key = STATUS_KEYS[status];
      return key ? _t(this, key) : status;
    },

    categoryLabel(category: string): string {
      const key = CATEGORY_KEYS[category];
      return key ? _t(this, key) : category;
    },

    incentiveTypeLabel(type: string): string {
      const key = INCENTIVE_TYPE_KEYS[type];
      return key ? _t(this, key) : type;
    },

    formatDate(dateStr: string): string {
      if (!dateStr) {
        return "";
      }
      return Alpine.store("hereby").formatDateISO(dateStr);
    },

    todayDisplay(): string {
      const iso = Alpine.store("hereby").todayLocal?.() as string | undefined;
      if (!iso) {
        return "";
      }
      const d = new Date(`${iso}T00:00:00`);
      const dowKeys = [
        "team_mgr.day_sun",
        "team_mgr.day_mon",
        "team_mgr.day_tue",
        "team_mgr.day_wed",
        "team_mgr.day_thu",
        "team_mgr.day_fri",
        "team_mgr.day_sat",
      ];
      return `${d.getMonth() + 1}/${d.getDate()} (${_t(this, dowKeys[d.getDay()])})`;
    },

    formatKRW(value: number | string): string {
      const num = typeof value === "string" ? Number.parseFloat(value) : value;
      if (Number.isNaN(num)) {
        return "0";
      }
      return num.toLocaleString("ko-KR");
    },

    // --- Document Methods ---
    async loadDocuments() {
      try {
        const result = await this.sdk.teamManager.listDocuments({
          pagination: { page: this.documentPagination.page, limit: 20 },
        });
        this.documents = result.items || [];
        this.documentPagination.totalCount = result.totalCount;
        this.documentPagination.totalPages = result.totalPages;
        this.documentPagination.page = result.page;
      } catch {
        this.documents = [];
      }
    },

    toggleDocumentSelect(id: string) {
      const idx = this.selectedDocumentIds.indexOf(id);
      if (idx >= 0) {
        this.selectedDocumentIds.splice(idx, 1);
      } else {
        this.selectedDocumentIds.push(id);
      }
    },

    toggleAllDocuments() {
      const pending = this.documents.filter((d) => d.status === "processing");
      if (this.selectedDocumentIds.length === pending.length) {
        this.selectedDocumentIds = [];
      } else {
        this.selectedDocumentIds = pending.map((d) => d.id);
      }
    },

    async approveDocuments() {
      if (this.selectedDocumentIds.length === 0) {
        return;
      }
      try {
        await this.sdk.teamManager.approveDocuments(this.selectedDocumentIds);
        this.selectedDocumentIds = [];
        await this.loadDocuments();
      } catch (e: unknown) {
        showError(`${_t(this, "team_mgr.approve_failed")}: ${e instanceof Error ? e.message : e}`);
      }
    },

    async rejectDocuments() {
      if (this.selectedDocumentIds.length === 0) {
        return;
      }
      this.rejectTarget = { type: "document", ids: [...this.selectedDocumentIds] };
      this.rejectReason = "";
      this.showRejectModal = true;
    },

    documentCategoryLabel(category: string): string {
      const keys: Record<string, string> = {
        medical: "team_mgr.doc_category_medical",
        certification: "team_mgr.doc_category_certification",
        overtime: "team_mgr.doc_category_overtime",
        contract: "team_mgr.doc_category_contract",
        other: "leave.other",
      };
      const key = keys[category];
      return key ? _t(this, key) : category;
    },

    // --- Certificate Methods ---
    async loadCertificates() {
      try {
        const result = await this.sdk.teamManager.listCertificates({
          pagination: { page: this.certificatePagination.page, limit: 20 },
        });
        this.certificates = result.items || [];
        this.certificatePagination.totalCount = result.totalCount;
        this.certificatePagination.totalPages = result.totalPages;
        this.certificatePagination.page = result.page;
      } catch {
        this.certificates = [];
      }
    },

    toggleCertificateSelect(id: string) {
      const idx = this.selectedCertificateIds.indexOf(id);
      if (idx >= 0) {
        this.selectedCertificateIds.splice(idx, 1);
      } else {
        this.selectedCertificateIds.push(id);
      }
    },

    toggleAllCertificates() {
      const pending = this.certificates.filter((c) => c.status === "pending");
      if (this.selectedCertificateIds.length === pending.length) {
        this.selectedCertificateIds = [];
      } else {
        this.selectedCertificateIds = pending.map((c) => c.id);
      }
    },

    async approveCertificates() {
      if (this.selectedCertificateIds.length === 0) {
        return;
      }
      try {
        await this.sdk.teamManager.approveCertificates(this.selectedCertificateIds);
        this.selectedCertificateIds = [];
        await this.loadCertificates();
      } catch (e: unknown) {
        showError(`${_t(this, "team_mgr.approve_failed")}: ${e instanceof Error ? e.message : e}`);
      }
    },

    async rejectCertificates() {
      if (this.selectedCertificateIds.length === 0) {
        return;
      }
      this.rejectTarget = { type: "certificate", ids: [...this.selectedCertificateIds] };
      this.rejectReason = "";
      this.showRejectModal = true;
    },

    printCertificate(html: string) {
      const w = window.open("", "_blank");
      if (w) {
        w.document.write(html);
        w.document.close();
      }
    },

    // --- Unified Document Management Methods ---
    async loadDocMgmt() {
      try {
        const result = await this.sdk.teamManager.listDocumentManagement(
          this.docMgmtFilter,
          this.docMgmtKindFilter,
        );
        this.docMgmtRows = (result || []) as typeof this.docMgmtRows;
      } catch {
        this.docMgmtRows = [];
      }
    },

    async approveDocMgmt(row: { id: string; source: "certificate" | "document" }) {
      try {
        await this.sdk.teamManager.approveDocumentManagement(row.id, row.source);
        await this.loadDocMgmt();
      } catch (e: unknown) {
        showError(`승인 실패: ${e instanceof Error ? e.message : "알 수 없는 오류"}`);
      }
    },

    rejectDocMgmt(row: { id: string; source: "certificate" | "document" }) {
      this.rejectTarget = { type: `doc_mgmt:${row.source}`, ids: [row.id] };
      this.rejectReason = "";
      this.showRejectModal = true;
    },

    viewDocMgmt(row: {
      downloadUrl: string | null;
      certificateHtml: string | null;
      status: string;
      rejectionReason: string | null;
    }) {
      if (row.status === "cancelled") {
        return;
      }
      // Open file or certificate HTML in new window
      if (row.downloadUrl) {
        window.open(row.downloadUrl, "_blank", "noopener");
      } else if (row.certificateHtml) {
        const w = window.open("", "_blank");
        if (w) {
          w.document.write(row.certificateHtml);
          w.document.close();
        }
      } else if (row.status === "rejected" && row.rejectionReason) {
        showAlert(`반려 사유: ${row.rejectionReason}`);
      }
    },

    docMgmtStatusLabel(status: string): string {
      const map: Record<string, string> = {
        pending: "대기",
        approved: "승인",
        rejected: "반려",
        cancelled: "취소",
      };
      return map[status] || status;
    },

    docMgmtKindLabel(kind: string): string {
      return kind === "request" ? "신청" : kind === "submission" ? "제출" : kind;
    },

    // ============================================================
    // ===== Nurse Scheduler methods (merged from nurse-scheduler) =====
    // ============================================================

    get hasNurseAccess() {
      return this.isNurse || this.isNurseManager || this.canReadScheduler;
    },

    /** 「근무 설정」 — 조직 전역 근무 규칙 패널. 병동 밖 설정이라 설정 권한으로 가린다.
     *  병동 페이지 관리자(PM-01)는 이 권한이 없으므로 버튼이 아예 안 보인다. */
    get canUseOrgSchedulerSettings(): boolean {
      const store = Alpine.store("hereby");
      return (
        this.isNurseManager &&
        (!!store?.currentUser?.isSystemAdmin || !!store?.hasPermission?.("setting:update"))
      );
    },

    /** 「출근 현황」 — 근무표 위에 실제 출퇴근을 겹쳐 본다. 근태 조회 권한이 따로 있다. */
    get canSeeAttendanceOverlay(): boolean {
      const store = Alpine.store("hereby");
      return (
        this.isNurseManager &&
        (!!store?.currentUser?.isSystemAdmin || !!store?.hasPermission?.("attendance:read"))
      );
    },

    /** 병동을 골라 볼 수 있는가 — 조직 전체 조회 권한이 있으면 조회 전용이어도 고른다.
     *  일반 간호사(isNurse)는 자기 병동만 보므로 고르는 대신 이름만 보인다. */
    get canBrowseWards(): boolean {
      return this.isNurseManager || this.canReadScheduler;
    },

    /** 버튼을 끄는 이유 — 끈 버튼 옆에 왜 못 누르는지 남긴다(권한 없음과 인력 없음을 구분) */
    get noEditReason(): string {
      return this.isNurseManager ? "" : "병동 인력을 바꿀 권한이 없어요. 관리자에게 요청하세요.";
    },

    /** 직원 카드의 「스케줄에서 보기」로 건너온 경우 — 그 줄로 스크롤하고 5초 강조한다.
     *  앱 셸이 sessionStorage 에 남긴 값을 한 번만 꺼내 쓰고 지운다(새로고침 때 되살아나지 않게). */
    applyPendingFocusEmployee() {
      let pending = "";
      try {
        pending = sessionStorage.getItem("admin:scheduler:focusEmployee") || "";
        if (pending) {
          sessionStorage.removeItem("admin:scheduler:focusEmployee");
        }
      } catch {
        return;
      }
      if (!pending) {
        return;
      }
      if (!this.nurses.some((n) => n.employeeId === pending)) {
        // 배정은 있는데 근무표 줄이 없다 — 비활성·퇴직이라 인력에서 빠진 경우.
        // 아무 말 없이 도착하면 「왜 강조가 없지」로 남으므로 이유를 한 줄 알린다(NAV-03).
        Alpine.store("toast")?.info?.(
          "이 근무표에 그 직원 줄이 없어요. 비활성이거나 다른 병동일 수 있어요.",
        );
        return;
      }
      this.focusEmployeeId = pending;
      if (schedulerFocusTimer) {
        clearTimeout(schedulerFocusTimer);
      }
      // 줄이 그려진 뒤에 움직인다 — 바로 스크롤하면 뒤이은 재렌더가 위치를 되돌린다
      setTimeout(() => {
        document
          .querySelector(`tr[data-emp-row="${pending}"]`)
          ?.scrollIntoView({ block: "center", behavior: "smooth" });
      }, 250);
      schedulerFocusTimer = setTimeout(() => {
        this.focusEmployeeId = "";
        schedulerFocusTimer = undefined;
      }, 5000);
    },

    async checkNurseStatus() {
      this.isNurse = false;
      this.myWardIds = [];
      if (!this.sdk) {
        return;
      }
      try {
        const userId = Alpine.store("hereby").currentUser?.id as string | undefined;
        if (!userId) {
          return;
        }
        const profile = await this.sdk.nurseScheduler.getProfile(userId);
        if (profile) {
          this.isNurse = true;
          this.myWardIds = profile.allWardIds ?? (profile.wardId ? [profile.wardId] : []);
          if (profile.isHeadNurse) {
            this.isNurseManager = true;
          }
        }
      } catch {
        // Not a nurse or no profile
      }
    },

    async loadWards() {
      // 탭 전환으로 직접 들어와 init 배치를 건너뛴 경우 — 스케줄러 목록도 함께 채운다.
      const [wardList, groupList] = await Promise.all([
        this.fetchWardList(),
        this.scheduleGroups.length === 0 ? this.fetchScheduleGroups() : Promise.resolve(null),
      ]);
      if (groupList) {
        this.applyScheduleGroupList(groupList);
      }
      this.applyWardList(wardList);
    },

    /** 조직의 스케줄러 목록 조회만. 실패하면 null — 병동 기본 스케줄러 폴백(wardId 만 보냄)으로 동작. */
    async fetchScheduleGroups(): Promise<ScheduleGroup[] | null> {
      if (!this.sdk) {
        return null;
      }
      try {
        const result = await this.sdk.nurseScheduler.listScheduleGroups();
        return (result.items || []).map((g) => ({
          id: g.id,
          wardId: g.wardId,
          wardName: g.wardName,
          name: g.name,
          code: g.code ?? null,
          location: g.location ?? null,
          isActive: g.isActive,
          isDefault: g.isDefault,
        }));
      } catch {
        return null;
      }
    },

    applyScheduleGroupList(list: ScheduleGroup[] | null) {
      this.scheduleGroups = list ?? [];
      this.syncScheduleGroupSelection();
    },

    /** 한 병동의 스케줄러 — 활성 먼저, 기본 먼저(서버 순서를 그 안에서 유지). */
    scheduleGroupsForWard(wardId: string): ScheduleGroup[] {
      if (!wardId) {
        return [];
      }
      const rank = (g: ScheduleGroup) => (g.isActive ? 0 : 2) + (g.isDefault ? 0 : 1);
      return this.scheduleGroups
        .filter((g: ScheduleGroup) => g.wardId === wardId)
        .sort((a: ScheduleGroup, b: ScheduleGroup) => rank(a) - rank(b));
    },

    /** 병동의 기본 스케줄러 id(없으면 첫 스케줄러, 그것도 없으면 ""). */
    defaultScheduleGroupId(wardId: string): string {
      const groups = this.scheduleGroupsForWard(wardId);
      return (groups.find((g: ScheduleGroup) => g.isDefault) ?? groups[0])?.id ?? "";
    },

    scheduleGroupName(groupId: string | null | undefined): string {
      return scheduleGroupLabel(this.scheduleGroups.find((g: ScheduleGroup) => g.id === groupId));
    },

    /** 선택 스케줄러가 선택 병동 것이 아니면(병동을 바꿨거나 아직 없음) 그 병동의 기본으로 맞춘다. */
    syncScheduleGroupSelection() {
      const current = this.scheduleGroups.find(
        (g: ScheduleGroup) => g.id === this.selectedScheduleGroupId,
      );
      if (current && current.wardId === this.selectedWardId) {
        return;
      }
      this.selectedScheduleGroupId = this.defaultScheduleGroupId(this.selectedWardId);
    },

    /** 인력 조회 범위 — 스케줄러가 잡혔으면 그 단위, 목록을 못 받았으면 병동 전체로 폴백. */
    profileScope(): { scheduleGroupId?: string; wardId?: string } {
      return this.selectedScheduleGroupId
        ? { scheduleGroupId: this.selectedScheduleGroupId }
        : { wardId: this.selectedWardId };
    },

    /** 병동 목록 조회만 (역할 필터 없음). 실패하면 null — applyWardList 가 빈 목록으로 처리. */
    async fetchWardList(): Promise<{ id: string; name: string; code: string }[] | null> {
      if (!this.sdk) {
        return null;
      }
      try {
        const result = await this.sdk.nurseScheduler.listWards({ isActive: true });
        return (result.items || []).map((w) => ({ id: w.id, name: w.name, code: w.code }));
      } catch {
        return null;
      }
    },

    /** 조회된 병동 목록에 역할 필터(일반 간호사 = 소속 병동만) 를 적용하고 기본 선택을 잡는다.
     *  checkNurseStatus 가 끝난 뒤 불러야 myWardIds 가 반영된다. */
    applyWardList(list: { id: string; name: string; code: string }[] | null) {
      if (!list) {
        this.wards = [];
        return;
      }
      let wardList = list;
      if (!this.isNurseManager && this.myWardIds.length > 0) {
        const allowed = new Set(this.myWardIds);
        wardList = wardList.filter((w) => allowed.has(w.id));
      }
      this.wards = wardList;
      if (this.wards.length > 0 && !this.selectedWardId) {
        this.selectedWardId = this.pickInitialWard();
      }
    },

    /* ── 마지막 선택(병동·년·월) 기억 — URL 해시 + localStorage (온보딩 9/16 요청) ── */

    /** 기억한 병동이 지금 목록(활성·미삭제·내 권한 안)에 있으면 그것, 아니면 첫 병동. */
    pickInitialWard(): string {
      const remembered = this.restoreWardId;
      if (remembered && this.wards.some((w) => w.id === remembered)) {
        return remembered;
      }
      return this.wards[0]?.id ?? "";
    },

    schedulerSelectionKey(): string {
      const orgId = Alpine.store("hereby").currentUser?.organizationId as string | undefined;
      return lastSelectionKey("admin", "scheduler", orgId);
    },

    readSchedulerSelection(): { wardId?: string; year?: number; month?: number } | null {
      const router = Alpine.store("router");
      const sub = router.tab === "scheduler" ? router.schedulerSub : "";
      const m = /^([0-9a-f-]{36}):(\d{4})-(\d{1,2})$/i.exec(sub);
      if (m) {
        return { wardId: m[1], year: Number(m[2]), month: Number(m[3]) };
      }
      return readLastSelection(this.schedulerSelectionKey());
    },

    /** 병동·년·월 셀렉트가 바뀔 때(loadTabData 진입)마다 — localStorage 와 해시를 함께 갱신. */
    rememberSchedulerSelection() {
      if (!this.selectedWardId) {
        return;
      }
      const value = {
        wardId: this.selectedWardId,
        year: this.period.year,
        month: this.period.month,
      };
      writeLastSelection(this.schedulerSelectionKey(), value);
      const router = Alpine.store("router");
      if (router.tab === "scheduler") {
        router.navigateTab("scheduler", `${value.wardId}:${value.year}-${value.month}`, {
          replace: true,
        });
      }
      this.syncScheduleGroupSelection();
    },

    async loadHospitalTotals() {
      if (!this.sdk) {
        return;
      }
      try {
        // biome-ignore lint/suspicious/noExplicitAny: SDK returns generic
        const allProfiles = await fetchAllPages<any>(
          (page, limit) => this.sdk.nurseScheduler.listProfiles({}, { page, limit }),
          500,
        );
        // 간호등급의 간호사 수 — 사람마다 판단한다. 직군 코드가 있으면 「간호사」(nurse)일 때만 세고
        // (요양보호사 · 간호조무사 · 병동지원은 병동에 배정돼 있어도 빠진다 — POL: 간호사 이외 인력을 간호사 수로
        // 환산하지 않는다), 직군이 비어 있으면(아직 분류 전) 예전처럼 센다. 한 명만 분류돼도 미분류 간호사가
        // 한꺼번에 빠지던 문제를 막는다. 퇴사 · 비활성 프로필은 병동 비율과 같은 기준(isCountedNurseStatus)으로 뺀다.
        const uniqueNurses = new Set(
          allProfiles
            .filter((p) => p.workType !== "off_duty" && isCountedNurseStatus(p.employee?.status))
            .filter((p) => !p.employee?.jobCategory || p.employee.jobCategory === "nurse")
            .map((p) => p.employeeId),
        );
        this.hospitalNurseCount = uniqueNurses.size;

        // 병동별 오늘 환자수 — 병동 수만큼 직렬 왕복하지 않고 한 배치로 모아 쏜다.
        const today = Alpine.store("hereby").todayLocal();
        const counts = await Promise.all(
          this.wards.map(async (ward) => {
            try {
              const rec = await this.sdk.nurseScheduler.getDailyPatientCount(ward.id, today);
              return [ward.id, rec?.patientCount ?? 0] as const;
            } catch {
              return [ward.id, 0] as const;
            }
          }),
        );
        const perWard: Record<string, number> = {};
        let totalPatients = 0;
        for (const [wardId, count] of counts) {
          perWard[wardId] = count;
          totalPatients += count;
        }
        this.hospitalPatientByWard = perWard;
        this.hospitalPatientCount = totalPatients;
      } catch {
        this.hospitalNurseCount = 0;
        this.hospitalPatientCount = 0;
        this.hospitalPatientByWard = {};
      }
    },

    async updateWardPatientCount(wardId: string, count: number) {
      if (!this.sdk) {
        return;
      }
      try {
        const today = Alpine.store("hereby").todayLocal();
        await this.sdk.nurseScheduler.upsertDailyPatientCount({
          wardId,
          date: today,
          patientCount: count,
        });
        this.hospitalPatientByWard = { ...this.hospitalPatientByWard, [wardId]: count };
        this.hospitalPatientCount = Object.values(this.hospitalPatientByWard).reduce(
          (a, b) => a + b,
          0,
        );
        if (wardId === this.selectedWardId) {
          this.todayPatientCount = count;
          this.dailyPatientCounts.set(today, count);
        }
      } catch {
        // Error
      }
    },

    async loadWardSettings() {
      if (!(this.sdk && this.selectedWardId)) {
        return;
      }
      try {
        // 병동 설정과 근무 유형 목록은 독립 — 같은 배치로.
        const [ws] = await Promise.all([
          this.sdk.nurseScheduler.getWardSettings(this.selectedWardId),
          this.loadAvailableShifts(),
        ]);
        this.wardBedCount = ws?.bedCount ?? null;
        if (ws?.bedCount != null) {
          this.wardSettingsForm.bedCount = ws.bedCount;
        }
      } catch {
        this.wardBedCount = null;
        this.wardStaffingRatios = null;
      }
    },

    async loadAvailableShifts() {
      if (!this.sdk) {
        return;
      }
      try {
        // 스케줄러 연동 ON 그룹의 활성 근무 유형만 노출 (필터 연동).
        const result = await this.sdk.shift.list({ schedulerOnly: true });
        this.availableShifts = (result.items || []).map((s) => ({
          id: s.id,
          code: s.code,
          codeColor: s.codeColor ?? undefined,
          name: s.name,
          type: s.type ?? "",
          startTime: s.startTime?.slice(0, 5) || "",
          endTime: s.endTime?.slice(0, 5) || "",
        }));
      } catch {
        this.availableShifts = [];
      }
    },

    async saveWardBedCount() {
      if (!(this.sdk && this.selectedWardId)) {
        return;
      }
      try {
        await this.sdk.nurseScheduler.updateWardSettings({
          wardId: this.selectedWardId,
          settings: { bedCount: this.wardSettingsForm.bedCount },
        });
        this.wardBedCount = this.wardSettingsForm.bedCount;
      } catch {
        // Error
      }
    },

    async saveStaffingSettings() {
      if (!this.sdk) {
        return;
      }
      // 빈 입력(NaN)·0 = 미설정 → null 로 보내 키 삭제(서버 null-unset 규약).
      const patientRatioOrNull = (v: number) => (Number.isFinite(v) && v > 0 ? v : null);
      try {
        await this.sdk.nurseScheduler.updateConfig({
          staffingRatios: {
            day: this.wardSettingsForm.ratioDay,
            evening: this.wardSettingsForm.ratioEvening,
            night: this.wardSettingsForm.ratioNight,
          },
          aideRatio: this.wardSettingsForm.aideRatio,
          caregiverRatio: this.wardSettingsForm.caregiverRatio,
          aidePatientRatio: patientRatioOrNull(this.wardSettingsForm.aidePatientRatio),
          supportPatientRatio: patientRatioOrNull(this.wardSettingsForm.supportPatientRatio),
          rehabSupportPatientRatio: patientRatioOrNull(
            this.wardSettingsForm.rehabSupportPatientRatio,
          ),
        });
        this.wardStaffingRatios = {
          day: this.wardSettingsForm.ratioDay,
          evening: this.wardSettingsForm.ratioEvening,
          night: this.wardSettingsForm.ratioNight,
        };
        this.wardAideRatio = this.wardSettingsForm.aideRatio;
        this.wardCaregiverRatio = this.wardSettingsForm.caregiverRatio;
        this.staffingSettingsSaved = JSON.stringify(this.wardSettingsForm);
        this.basicJustSaved = true;
        Alpine.store("toast")?.success("수정이 완료되었습니다");
      } catch {
        // Error
      }
    },

    /** 교대 매핑 폼을 응답(shiftMappings)으로 채운다 — 빈 키는 "" 로 두어 select 가 「미설정」을 가리킨다. */
    fillShiftMappingsForm(sm: ScheduleGroupSettings["shiftMappings"] | undefined) {
      const empty = {
        day: "",
        evening: "",
        night: "",
        dayWeekend: "",
        eveningWeekend: "",
        nightWeekend: "",
      };
      this.shiftMappingsForm = {
        threeShift: { ...empty, ...(sm?.threeShift || {}) },
        dayDedicated: { day: "", dayWeekend: "", ...(sm?.dayDedicated || {}) },
        eveningDedicated: { evening: "", eveningWeekend: "", ...(sm?.eveningDedicated || {}) },
        nightDedicated: { night: "", nightWeekend: "", ...(sm?.nightDedicated || {}) },
      } as Record<string, Record<string, string>>;
    },

    /** 스케줄러 편성 설정(교대 매핑 · 교육 정책) 로드 — 모달 폼과 저장 기준선(dirty 판정)을 함께 맞춘다.
     *  그룹에 값이 없으면 서버가 병동 레거시 값을 이어 주므로 화면은 비어 보이지 않는다. */
    async loadScheduleGroupSettings(groupId: string) {
      if (!(this.sdk && groupId)) {
        return;
      }
      this.wsGroupLoading = true;
      try {
        const gs = await this.sdk.nurseScheduler.getScheduleGroupSettings(groupId);
        // 응답이 오는 사이 다른 스케줄러로 바뀌었으면 버린다(모달 select 빠른 전환).
        if (groupId !== this.wsGroupId && groupId !== this.selectedScheduleGroupId) {
          return;
        }
        this.fillShiftMappingsForm(gs?.shiftMappings ?? null);
        this.educationPolicyForm = gs?.educationPolicy || "work_counted";
        this.shiftMappingsSaved = JSON.stringify(this.shiftMappingsForm);
        this.educationPolicySaved = this.educationPolicyForm;
      } catch {
        // 실패하면 폼은 마지막 값 그대로 — 저장 버튼은 기준선 비교로 비활성 유지
      }
      this.wsGroupLoading = false;
    },

    /** 모달 안 스케줄러 전환 — 저장 안 한 매핑·정책이 있으면 확인 뒤 버리고 새 스케줄러 값을 읽는다. */
    async switchWsGroup(groupId: string) {
      if (!groupId || groupId === this.wsGroupId) {
        return;
      }
      const dirty =
        (this.shiftMappingsSaved &&
          JSON.stringify(this.shiftMappingsForm) !== this.shiftMappingsSaved) ||
        (this.educationPolicySaved && this.educationPolicyForm !== this.educationPolicySaved);
      if (dirty) {
        const ok = await askConfirm({
          message:
            "저장하지 않은 교대 매핑·교육 정책 변경이 있어요.\n다른 스케줄러로 바꾸면 변경 내용이 사라져요.",
          tone: "danger",
        });
        if (!ok) {
          return;
        }
      }
      this.wsGroupId = groupId;
      this.shiftsJustSaved = false;
      this.educationJustSaved = false;
      await this.loadScheduleGroupSettings(groupId);
    },

    async saveShiftMappings() {
      if (!(this.sdk && this.wsGroupId)) {
        return;
      }
      try {
        const cleanMap = (m: Record<string, string>) => {
          const result: Record<string, string> = {};
          for (const [k, v] of Object.entries(m)) {
            if (v) {
              result[k] = v;
            }
          }
          return Object.keys(result).length > 0 ? result : undefined;
        };
        await this.sdk.nurseScheduler.updateScheduleGroupSettings({
          scheduleGroupId: this.wsGroupId,
          settings: {
            shiftMappings: {
              threeShift: cleanMap(this.shiftMappingsForm.threeShift || {}),
              dayDedicated: cleanMap(this.shiftMappingsForm.dayDedicated || {}),
              eveningDedicated: cleanMap(this.shiftMappingsForm.eveningDedicated || {}),
              nightDedicated: cleanMap(this.shiftMappingsForm.nightDedicated || {}),
            },
          },
        });
        this.shiftMappingsSaved = JSON.stringify(this.shiftMappingsForm);
        this.shiftsJustSaved = true;
        Alpine.store("toast")?.success("수정이 완료되었습니다");
      } catch {
        // Error
      }
    },

    async reloadOrgConfig() {
      await this.loadSchedulerConfig();
      await this.loadAvailableShifts();
    },

    async saveEducationPolicy() {
      if (!(this.sdk && this.wsGroupId)) {
        return;
      }
      try {
        await this.sdk.nurseScheduler.updateScheduleGroupSettings({
          scheduleGroupId: this.wsGroupId,
          settings: {
            educationPolicy: this.educationPolicyForm as "work_counted" | "off",
          },
        });
        this.educationPolicySaved = this.educationPolicyForm;
        this.educationJustSaved = true;
        Alpine.store("toast")?.success("수정이 완료되었습니다");
      } catch {
        // Error
      }
    },

    /** 희망근무 탭 노출 토글 (병원 전체). 끄면 탭 히든 + 현재 탭이면 근무표로 이동. */
    async toggleSchedulerPreferenceTab() {
      if (!this.sdk) {
        return;
      }
      const next = !this.preferenceTabEnabled;
      try {
        await this.sdk.nurseScheduler.updateConfig({ preferenceTabEnabled: next });
        this.preferenceTabEnabled = next;
        if (!next && this.activeTab === "pref-input") {
          this.activeTab = "scheduler";
        }
        Alpine.store("toast")?.success("수정이 완료되었습니다");
      } catch {
        // Error
      }
    },

    async loadTodayPatientCount() {
      if (!(this.sdk && this.selectedWardId)) {
        return;
      }
      try {
        const today = Alpine.store("hereby").todayLocal();
        const record = await this.sdk.nurseScheduler.getDailyPatientCount(
          this.selectedWardId,
          today,
        );
        this.todayPatientCount = record?.patientCount ?? 0;
      } catch {
        this.todayPatientCount = 0;
      }
    },

    async saveTodayPatientCount() {
      if (!(this.sdk && this.selectedWardId)) {
        return;
      }
      try {
        const today = Alpine.store("hereby").todayLocal();
        await this.sdk.nurseScheduler.upsertDailyPatientCount({
          wardId: this.selectedWardId,
          date: today,
          patientCount: this.todayPatientCount,
        });
        this.dailyPatientCounts.set(today, this.todayPatientCount);
      } catch {
        // Error
      }
    },

    async loadMonthlyPatientCounts() {
      if (!(this.sdk && this.selectedWardId)) {
        return;
      }
      try {
        const cycle = this.getCycleDates(this.period.year, this.period.month);
        const startDate = this.cycleStartDate || cycle.startDate;
        const endDate = this.cycleEndDate || cycle.endDate;
        const records = await this.sdk.nurseScheduler.getDailyPatientCountRange(
          this.selectedWardId,
          startDate,
          endDate,
        );
        this.dailyPatientCounts = new Map();
        for (const r of records || []) {
          const d = toDateStr(r.date);
          this.dailyPatientCounts.set(d, r.patientCount);
        }
      } catch {
        this.dailyPatientCounts = new Map();
      }
    },

    /* 「병동 관리 및 설정」 팝업 — 인력 설정·교대 매핑·교육 정책·주기 설정 탭.
       병동 목록·편집은 admin 병동 관리 페이지(wards.html) 전용이라 여기서 로드하지 않는다. */
    async openWardManagement(tab = "basic") {
      this.wardSettingsInitTab = tab;
      this.showWardSettings = true;
      // 교대 매핑·교육 정책은 스케줄러 단위 — 병동 관리 페이지 호스트(wards.html)처럼 목록이
      // 아직 없으면 여기서 채우고, 편집 대상은 선택 스케줄러(없으면 첫 스케줄러)로 잡는다.
      if (this.scheduleGroups.length === 0) {
        this.applyScheduleGroupList(await this.fetchScheduleGroups());
      }
      this.wsGroupId = this.selectedScheduleGroupId || this.scheduleGroups[0]?.id || "";
      this.shiftsJustSaved = false;
      this.educationJustSaved = false;
      await Promise.allSettled([
        this.loadWardSettings(),
        this.reloadOrgConfig(),
        this.loadScheduleGroupSettings(this.wsGroupId),
      ]);
    },

    async openPatientCountPanel() {
      this.pcWardId = this.selectedWardId;
      this.pcYear = this.period.year;
      this.pcMonth = this.period.month;
      this.showPatientCount = true;
      await this.loadPatientCountPanel();
    },

    /* Close the patient count modal and DISCARD all unsaved edits.
       Only edits committed via pcSaveAll() persist. */
    closePatientCountPanel() {
      this.showPatientCount = false;
      this.pcEditing = new Map();
      // 값과 함께 출처도 버린다 — 안 버리면 다음에 열었을 때 저장 안 된 「자동채움」이 남는다.
      this.pcPendingSources = new Map();
    },

    /* 환자수 팝업의 「병동 관리 ›」 — 병동 목록·편집은 admin 병동 관리 페이지에만 있다.
       이 스코프의 switchTab()은 근무표 하위 탭 전환용이라 admin 탭을 바꾸지 못한다 →
       라우터 스토어로 해시를 바꾸면 index.html 루트 x-effect가 admin switchTab()을 부른다.
       탭이 바뀌면 스케줄러(x-if)가 언마운트되므로 팝업 상태를 먼저 정리한다.
       attemptClose("goToWardsPage")로 불러 미저장 편집이 있으면 저장확인을 거친다. */
    goToWardsPage() {
      this.closePatientCountPanel();
      Alpine.store("router").navigateTab("wards");
    },

    async loadPatientCountPanel() {
      if (!(this.sdk && this.pcWardId)) {
        return;
      }
      this.pcLoading = true;
      try {
        const dim = new Date(this.pcYear, this.pcMonth, 0).getDate();
        const pad = (n: number) => String(n).padStart(2, "0");
        const startDate = `${this.pcYear}-${pad(this.pcMonth)}-01`;
        const endDate = `${this.pcYear}-${pad(this.pcMonth)}-${pad(dim)}`;
        const records =
          (await this.sdk.nurseScheduler.getDailyPatientCountRange(
            this.pcWardId,
            startDate,
            endDate,
          )) || [];
        if (!this.pcCounts) {
          this.pcCounts = new Map();
        }
        if (!this.pcEditing) {
          this.pcEditing = new Map();
        }
        for (const r of records) {
          const d = toDateStr(r.date);
          const mapKey = `${this.pcWardId}|${d}`;
          this.pcCounts.set(mapKey, r.patientCount);
          this.pcSources.set(mapKey, r.source ?? WardPatientCountSource.MEASURED);
          if (!this.pcEditing.has(mapKey)) {
            this.pcEditing.set(mapKey, r.patientCount);
          }
        }
        for (let d = 1; d <= dim; d++) {
          const dateKey = `${this.pcYear}-${pad(this.pcMonth)}-${pad(d)}`;
          const mapKey = `${this.pcWardId}|${dateKey}`;
          if (!this.pcEditing.has(mapKey)) {
            this.pcEditing.set(mapKey, 0);
          }
        }

        let prevY = this.pcYear;
        let prevM = this.pcMonth - 1;
        if (prevM < 1) {
          prevM = 12;
          prevY--;
        }
        const prevDim = new Date(prevY, prevM, 0).getDate();
        const prevStart = `${prevY}-${pad(prevM)}-01`;
        const prevEnd = `${prevY}-${pad(prevM)}-${pad(prevDim)}`;
        const prevRecords =
          (await this.sdk.nurseScheduler.getDailyPatientCountRange(
            this.pcWardId,
            prevStart,
            prevEnd,
          )) || [];
        this.pcPrevCounts = new Map();
        for (const r of prevRecords) {
          const d = toDateStr(r.date);
          this.pcPrevCounts.set(d, r.patientCount);
        }

        try {
          const ws = await this.sdk.nurseScheduler.getWardSettings(this.pcWardId);
          this.pcBedCount = ws?.bedCount ?? null;
        } catch {
          this.pcBedCount = null;
        }

        // 일괄 입력 기본값 = 전월 평균. 전월 데이터가 없으면 빈칸으로 두고
        // 사용자가 직접 넣게 한다 — 병상 수로 대체하지 않는다(정원≠환자수).
        this.pcBulkValue = this.pcPrevAvgValue();

        await this.pcLoadShiftCounts();
      } catch {
        // Error
      }
      this.pcLoading = false;
    },

    /**
     * 표시 중인 달의 일별 D/E/N 배치 인원을 채운다 — 비율·위험 판정의 분자.
     *
     * 근무표는 달력 월이 아니라 주기(예: 07.25~08.24) 단위라 한 달을 덮으려면
     * 전월 주기까지 두 개를 합쳐야 한다. 실패해도 팝업은 그대로 뜨고 비율만
     * 안 나오게 조용히 넘어간다(환자수 입력이 본업이라 막으면 안 된다).
     */
    async pcLoadShiftCounts() {
      if (!(this.sdk && this.pcWardId)) {
        return;
      }
      const periods = [
        { year: this.pcYear, month: this.pcMonth },
        this.pcMonth === 1
          ? { year: this.pcYear - 1, month: 12 }
          : { year: this.pcYear, month: this.pcMonth - 1 },
      ];
      const working = new Set<string>([
        "day",
        "evening",
        "night",
        "day_education",
        "evening_education",
        "night_education",
      ]);
      // 매번 처음부터 다시 센다 — 같은 달을 두 번 열면(저장 후 재로드 포함)
      // 기존 맵에 누적돼 인원이 2배로 잡힌다.
      const counts = new Map<string, { day: number; evening: number; night: number }>();
      for (const p of periods) {
        try {
          const list = await this.sdk.nurseScheduler.listSchedules(
            { wardId: this.pcWardId, year: p.year, month: p.month },
            { page: 1, limit: 1 },
          );
          const sched = (list.items || [])[0];
          if (!sched) {
            continue;
          }
          const full = await this.sdk.nurseScheduler.getSchedule(sched.id);
          for (const e of full.entries || []) {
            // 연동 근무(shift_custom)는 shiftId를 시작시각 기준으로 D/E/N에 환산 —
            // 그리드가 쓰는 shiftSlot()과 같은 규칙이라 두 화면 숫자가 갈리지 않는다.
            let slot: "day" | "evening" | "night" | null = null;
            if (e.assignmentType === "shift_custom" && e.shiftId) {
              slot = this.shiftSlot(e.shiftId);
            } else if (working.has(e.assignmentType)) {
              slot = e.assignmentType.startsWith("day")
                ? "day"
                : e.assignmentType.startsWith("evening")
                  ? "evening"
                  : "night";
            }
            if (!slot) {
              continue;
            }
            const key = `${this.pcWardId}|${toDateStr(e.date)}`;
            const cur = counts.get(key) ?? { day: 0, evening: 0, night: 0 };
            cur[slot]++;
            counts.set(key, cur);
          }
        } catch {
          // 한 주기를 못 읽어도 나머지는 계속 — 비율은 읽힌 날만 나온다.
        }
      }
      this.pcShiftCounts = counts;
    },

    /** 그날 배치된 D/E/N 인원. 근무표가 없는 날은 null(= 비율 산출 불가). */
    pcActualStaff(day: number): { day: number; evening: number; night: number } | null {
      return this.pcShiftCounts.get(this.pcMapKey(day)) ?? null;
    },

    /**
     * 병동 설정 팝업(위에 스택)에서 저장한 내용을 환자수 관리 팝업에 되반영한다.
     * 병상 수(병동별)와 병동 목록이 바뀔 수 있어 둘 다 다시 읽는다 —
     * 인력 비율은 저장 시 saveStaffingSettings 가 메모리를 갱신하므로 자동 반영된다.
     */
    async pcRefreshWardInfo() {
      if (!(this.sdk && this.showPatientCount && this.pcWardId)) {
        return;
      }
      await this.loadWards().catch(() => undefined);
      try {
        const ws = await this.sdk.nurseScheduler.getWardSettings(this.pcWardId);
        this.pcBedCount = ws?.bedCount ?? null;
      } catch {
        this.pcBedCount = null;
      }
    },

    /** 그날 해당 근무조에 배치된 인원. 근무표 없는 날은 0(마크업 산술에서 NaN 방지). */
    pcStaffCount(day: number, slot: "day" | "evening" | "night"): number {
      return this.pcActualStaff(day)?.[slot] ?? 0;
    },

    /** 그 근무조가 필요 인원에 못 미치는가 — 셀에서 부족한 조만 red로 칠하는 데 쓴다. */
    pcShiftShort(day: number, slot: "day" | "evening" | "night"): boolean {
      const need = this.pcRequiredStaff(day);
      const have = this.pcActualStaff(day);
      return !!(need && have) && have[slot] < need[slot];
    },

    /** 그날 필요한 D/E/N 인원 — 병동 설정의 「간호사 1인당 환자 비율」 기준. */
    pcRequiredStaff(day: number): { day: number; evening: number; night: number } | null {
      const ratios = this.wardStaffingRatios;
      const patients = this.pcGetEditing(day);
      if (!(ratios && patients > 0)) {
        return null;
      }
      return {
        day: Math.ceil(patients / ratios.day),
        evening: Math.ceil(patients / ratios.evening),
        night: Math.ceil(patients / ratios.night),
      };
    },

    /** 위험 = 어느 한 근무조라도 필요 인원에 못 미치는 날(삭감 위험). */
    pcIsRisk(day: number): boolean {
      if (this.pcIsFuture(day)) {
        return false;
      }
      const need = this.pcRequiredStaff(day);
      const have = this.pcActualStaff(day);
      if (!(need && have)) {
        return false;
      }
      return have.day < need.day || have.evening < need.evening || have.night < need.night;
    },

    /**
     * 병상 대비 환자 — 정원(병상 수) 대비 실제 재원 수준.
     * 분자는 월 집계와 같은 기준(실측 입력일만)이라 미입력·병상수 채움은 빠진다.
     * 100%를 넘으면 물리적으로 불가능하므로 입력 오류 신호로 읽는다.
     */
    pcOccupancy(): { avgPatients: number; beds: number | null; rate: number | null } {
      const { avgPatients, days } = this.pcMonthSummary();
      const beds = this.pcBedCount;
      return {
        avgPatients: days > 0 ? avgPatients : 0,
        beds,
        rate: beds && days > 0 ? (avgPatients / beds) * 100 : null,
      };
    },

    /** 병상 대비 환자 툴팁 — 산식·집계 대상일, 100% 초과 시 확인 안내. */
    pcOccupancyTooltip(): string {
      const { avgPatients, beds, rate } = this.pcOccupancy();
      const { days } = this.pcMonthSummary();
      const lines = ["병상 수(정원) 대비 실제 재원 수준입니다."];
      if (rate != null && beds != null) {
        lines.push(
          `가동률 = 평균 환자 ${avgPatients.toFixed(1)}명 ÷ 병상 ${beds}개 = ${Math.round(rate)}%`,
        );
      } else if (beds == null) {
        lines.push("병동 설정에 병상 수가 없어 가동률을 계산할 수 없습니다");
      } else {
        lines.push("집계할 실측 데이터가 없어 가동률을 계산할 수 없습니다");
      }
      lines.push(`집계 대상: ${days}일 (실제 환자수를 입력한 날만)`);
      if (rate != null && rate > 100) {
        lines.push("100%를 넘습니다 — 환자수 입력 오류일 수 있으니 해당 일자를 확인하세요.");
      }
      return lines.join("\n");
    },

    /**
     * 월 집계 「간호사 비율」 툴팁 — 어떤 날을 세었고 무엇으로 나눴는지 밝힌다.
     * 지표 바의 오늘 비율과 값이 다른 이유(기간·모수)가 여기서 드러나야 한다.
     */
    pcRatioTooltip(): string {
      const s = this.pcMonthSummary();
      const lines = ["간호사 1명이 맡는 환자 수입니다. (평균 환자수 ÷ 평균 근무 간호사수)"];
      if (s.ratio != null) {
        lines.push(
          `${this.pcMonth}월: 평균 환자 ${s.avgPatients.toFixed(1)}명 ÷ 평균 근무 ${s.avgNurses.toFixed(1)}명 = 1:${s.ratio.toFixed(1)}`,
        );
      } else {
        lines.push(`${this.pcMonth}월: 집계할 실측 데이터가 없어 계산할 수 없습니다`);
      }
      lines.push(`집계에 쓴 날: ${s.days}일 (실제 환자수를 입력한 날만)`);
      const excluded = [
        s.missing > 0 ? `미입력 ${s.missing}일` : "",
        s.estimate > 0 ? `병상수 채움 ${s.estimate}일` : "",
      ].filter(Boolean);
      if (excluded.length > 0) {
        lines.push(`제외한 날: ${excluded.join(" · ")}`);
      }
      lines.push("간호사수는 배정 인원이 아니라 그날 근무로 배정된 D/E/N 인원입니다.");
      return lines.join("\n");
    },

    /** 날짜 셀 툴팁 — 근무조별 배치/필요를 그대로 보여준다(줄바꿈 → 멀티라인 버블). */
    pcDayTitle(day: number): string {
      const issue = this.pcValueIssue(day);
      const patients = this.pcGetEditing(day);
      const need = this.pcRequiredStaff(day);
      const have = this.pcActualStaff(day);
      const head = issue ? `⚠ 확인 필요 — ${issue}\n` : "";
      if (!have) {
        return `${head}${this.pcMonth}월 ${day}일 — 근무표가 없어 비율을 계산할 수 없습니다`;
      }
      if (!need) {
        return `${head}${this.pcMonth}월 ${day}일 — 환자수를 입력하면 필요 인원이 계산됩니다`;
      }
      const line = (label: string, k: "day" | "evening" | "night") =>
        `${label} ${have[k]}/${need[k]}${have[k] < need[k] ? " (부족)" : ""}`;
      return [
        `${head}${this.pcMonth}월 ${day}일 · 환자 ${patients}명`,
        line("주간(D)", "day"),
        line("저녁(E)", "evening"),
        line("야간(N)", "night"),
      ].join("\n");
    },

    /**
     * 표시 중인 달의 집계 — 신고용 월 지표.
     * 환자수가 입력된 날만 분모로 센다(미입력일을 0으로 세면 평균이 실제보다 낮아진다).
     */
    pcMonthSummary(): {
      days: number;
      missing: number;
      estimate: number;
      risk: number;
      avgPatients: number;
      avgNurses: number;
      ratio: number | null;
    } {
      let days = 0;
      let patients = 0;
      let nurses = 0;
      let nurseDays = 0;
      let risk = 0;
      const dim = this.pcGetDaysInMonth();
      for (let d = 1; d <= dim; d++) {
        if (this.pcIsFuture(d)) {
          continue;
        }
        if (!(this.pcHasSaved(d) || this.pcIsChanged(d))) {
          continue;
        }
        // 병상수 자동채움은 실측이 아니다 — 신고 근거가 못 되므로 집계 분모에서 뺀다.
        if (this.pcIsEstimate(d)) {
          continue;
        }
        const pc = this.pcGetEditing(d);
        if (pc <= 0) {
          continue;
        }
        days++;
        patients += pc;
        const have = this.pcActualStaff(d);
        if (have) {
          nurses += have.day + have.evening + have.night;
          nurseDays++;
        }
        if (this.pcIsRisk(d)) {
          risk++;
        }
      }
      const avgPatients = days > 0 ? patients / days : 0;
      const avgNurses = nurseDays > 0 ? nurses / nurseDays : 0;
      return {
        days,
        missing: this.pcCountMissing(),
        estimate: this.pcCountEstimate(),
        risk,
        avgPatients,
        avgNurses,
        ratio: avgNurses > 0 ? avgPatients / avgNurses : null,
      };
    },

    pcChangePeriod(delta: number) {
      this.pcMonth += delta;
      if (this.pcMonth > 12) {
        this.pcMonth = 1;
        this.pcYear++;
      } else if (this.pcMonth < 1) {
        this.pcMonth = 12;
        this.pcYear--;
      }
      this.loadPatientCountPanel();
    },

    pcGoToToday() {
      const now = new Date();
      const targetYear = now.getFullYear();
      const targetMonth = now.getMonth() + 1;
      if (this.pcYear === targetYear && this.pcMonth === targetMonth) {
        return;
      }
      this.pcYear = targetYear;
      this.pcMonth = targetMonth;
      this.loadPatientCountPanel();
    },

    pcGetDaysInMonth(): number {
      return new Date(this.pcYear, this.pcMonth, 0).getDate();
    },

    pcGetFirstDayOfWeek(): number {
      return new Date(this.pcYear, this.pcMonth - 1, 1).getDay();
    },

    pcDateKey(day: number): string {
      const pad = (n: number) => String(n).padStart(2, "0");
      return `${this.pcYear}-${pad(this.pcMonth)}-${pad(day)}`;
    },

    /* Map key for pcCounts/pcEditing — scoped per ward so switching wards
       preserves each ward's pending edits separately. Format: "wardId|YYYY-MM-DD". */
    pcMapKey(day: number): string {
      return `${this.pcWardId}|${this.pcDateKey(day)}`;
    },

    pcIsToday(day: number): boolean {
      return this.pcDateKey(day) === Alpine.store("hereby").todayLocal();
    },

    pcIsFuture(day: number): boolean {
      return this.pcDateKey(day) > Alpine.store("hereby").todayLocal();
    },

    pcHasSaved(day: number): boolean {
      return this.pcCounts.has(this.pcMapKey(day));
    },

    pcGetEditing(day: number): number {
      return this.pcEditing.get(this.pcMapKey(day)) ?? 0;
    },

    pcSetEditing(day: number, value: number) {
      const key = this.pcMapKey(day);
      this.pcEditing.set(key, value);
      // 사람이 직접 고친 값은 실측이다 — 병상수로 채워졌던 날도 손대는 순간 실측으로 승격.
      this.pcPendingSources.set(key, WardPatientCountSource.MEASURED);
    },

    pcGetSaved(day: number): number | null {
      return this.pcCounts.get(this.pcMapKey(day)) ?? null;
    },

    pcIsChangedByKey(key: string): boolean {
      const saved = this.pcCounts.get(key);
      const editing = this.pcEditing.get(key) ?? 0;
      if (saved === undefined) {
        return editing > 0;
      }
      return saved !== editing;
    },

    pcIsChanged(day: number): boolean {
      return this.pcIsChangedByKey(this.pcMapKey(day));
    },

    pcCountChanged(): number {
      // Count pending edits across ALL months (preserved via loadPatientCountPanel)
      let count = 0;
      for (const key of this.pcEditing.keys()) {
        if (this.pcIsChangedByKey(key)) {
          count++;
        }
      }
      return count;
    },

    /* 닫기 가드용 dirty 판정 — 전 월(月) 통틀어 미저장 편집이 하나라도 있으면 true.
       변경이 없으면 attemptClose가 확인창 없이 바로 닫는다. */
    patientCountDirty(): boolean {
      return this.pcCountChanged() > 0;
    },

    pcCountMissing(): number {
      const today = Alpine.store("hereby").todayLocal();
      let count = 0;
      const dim = this.pcGetDaysInMonth();
      for (let d = 1; d <= dim; d++) {
        const dateKey = this.pcDateKey(d);
        if (dateKey > today) {
          continue;
        }
        // 달력 셀의 「미입력」 판정과 같은 조건 — 저장값도 없고 입력 중인 값도 없을 때만.
        // 저장분(pcCounts)만 보면 병상수 채우기·직접 입력한 미저장 값이 미입력으로 남아,
        // 셀은 이미 채워졌는데 하단 라벨은 「미입력 10일」이라 서로 어긋난다.
        if (!(this.pcHasSaved(d) || this.pcIsChanged(d))) {
          count++;
        }
      }
      return count;
    },

    /** 전월 실측 평균(반올림). 전월 입력이 없으면 null — 일괄 입력의 기본값 후보다. */
    pcPrevAvgValue(): number | null {
      if (this.pcPrevCounts.size === 0) {
        return null;
      }
      let sum = 0;
      for (const v of this.pcPrevCounts.values()) {
        sum += v;
      }
      return Math.round(sum / this.pcPrevCounts.size);
    },

    /**
     * 일괄 입력 값이 아직 기본값(전월 평균) 그대로인가 — 툴팁 노출 조건.
     * 사용자가 숫자를 고치면 그 값은 더 이상 전월 평균이 아니므로 툴팁을 감춘다.
     */
    pcBulkIsDefault(): boolean {
      const avg = this.pcPrevAvgValue();
      return avg != null && this.pcBulkValue === avg;
    },

    /**
     * 미입력일에 입력자가 확정한 값을 한 번에 넣는다.
     *
     * 시스템이 값을 만들지 않는다는 게 병상수 채우기와의 결정적 차이다 — 그래서
     * 집계에 포함(BULK)하되, 하루하루 센 실측과 구분되도록 출처는 남긴다.
     * 미래 날짜는 제외한다: 셀 입력칸도 미래일은 disabled 라 손입력과 기준을 맞춘다.
     */
    pcApplyBulk() {
      const value = this.pcBulkValue;
      if (value == null || value < 0) {
        return;
      }
      const dim = this.pcGetDaysInMonth();
      for (let d = 1; d <= dim; d++) {
        if (this.pcIsFuture(d)) {
          continue;
        }
        const mapKey = this.pcMapKey(d);
        if (!this.pcCounts.has(mapKey)) {
          this.pcEditing.set(mapKey, value);
          this.pcPendingSources.set(mapKey, WardPatientCountSource.BULK);
        }
      }
    },

    /**
     * 아직 저장하지 않은 자동 입력 일수(일괄·병상수) — 되돌리기 버튼의 노출 조건이자 대상.
     * 이미 저장된 값은 삭제 API가 없어 되돌릴 수 없으므로 세지 않는다.
     */
    pcCountPendingEstimate(): number {
      const dim = this.pcGetDaysInMonth();
      let n = 0;
      for (let d = 1; d <= dim; d++) {
        // 미래일은 제외 — 하단 「병상수 채움 N일」(pcCountEstimate)과 같은 기준이어야
        // 버튼 툴팁과 라벨의 숫자가 갈리지 않는다.
        if (this.pcIsFuture(d)) {
          continue;
        }
        if (this.pcIsAutoSource(this.pcPendingSources.get(this.pcMapKey(d)))) {
          n++;
        }
      }
      return n;
    },

    /**
     * 병상수로 채운 미저장 값을 되돌린다 — 잘못 눌렀을 때의 탈출구.
     * 저장값이 있던 날은 그 값으로, 없던 날은 미입력(0)으로 복원한다.
     */
    pcResetFilled() {
      const dim = this.pcGetDaysInMonth();
      for (let d = 1; d <= dim; d++) {
        const key = this.pcMapKey(d);
        if (!this.pcIsAutoSource(this.pcPendingSources.get(key))) {
          continue;
        }
        this.pcPendingSources.delete(key);
        this.pcEditing.set(key, this.pcCounts.get(key) ?? 0);
      }
    },

    /** 손으로 하루하루 넣은 값이 아닌 출처(일괄·병상수 채움)인가. */
    pcIsAutoSource(src: WardPatientCountSource | undefined): boolean {
      return src === WardPatientCountSource.BULK || src === WardPatientCountSource.ESTIMATED;
    },

    /** 그날 값의 출처 — 저장 대기분 우선(화면은 지금 상태를 보여야 한다). */
    pcSourceOf(day: number): WardPatientCountSource {
      const key = this.pcMapKey(day);
      return (
        this.pcPendingSources.get(key) ?? this.pcSources.get(key) ?? WardPatientCountSource.MEASURED
      );
    },

    /** 병상수로 채운 추정치인가 — 집계에서 빠지는 유일한 출처다(레거시 데이터 포함). */
    pcIsEstimate(day: number): boolean {
      return this.pcSourceOf(day) === WardPatientCountSource.ESTIMATED;
    },

    /** 일괄 입력으로 넣은 값인가 — 집계에는 들어가되 셀에 흔적을 남긴다. */
    pcIsBulk(day: number): boolean {
      return this.pcSourceOf(day) === WardPatientCountSource.BULK;
    },

    /**
     * 값 자체가 의심스러운 날 — 사유 문자열, 이상 없으면 null.
     * 물리적으로 불가능한 값(병상 초과)과 자릿수 실수(전월 평균 대비 급변)를 잡는다.
     */
    pcValueIssue(day: number): string | null {
      if (this.pcIsFuture(day)) {
        return null;
      }
      const v = this.pcGetEditing(day);
      if (v <= 0) {
        return null;
      }
      if (this.pcBedCount != null && v > this.pcBedCount) {
        return `병상 수(${this.pcBedCount}개)보다 많습니다`;
      }
      const avg = this.pcPrevAvgValue();
      if (avg && Math.abs(v - avg) / avg > 0.5) {
        return `전월 평균(${avg}명)과 차이가 큽니다`;
      }
      return null;
    },

    /** 확인이 필요한 날 수 — 집계 바에 노출한다. */
    pcCountIssues(): number {
      const dim = this.pcGetDaysInMonth();
      let n = 0;
      for (let d = 1; d <= dim; d++) {
        if (this.pcValueIssue(d)) {
          n++;
        }
      }
      return n;
    },

    /** 표시 중인 달의 자동채움 일수 — 신고 전에 실측으로 바꿔야 할 날. */
    pcCountEstimate(): number {
      const dim = this.pcGetDaysInMonth();
      let n = 0;
      for (let d = 1; d <= dim; d++) {
        if (!this.pcIsFuture(d) && this.pcIsEstimate(d)) {
          n++;
        }
      }
      return n;
    },

    async pcSaveAll() {
      if (!this.sdk) {
        return;
      }
      // 저장하면 집계·신고 근거가 바뀌므로 한 번 확인받는다. 확인이 필요한 값
      // (병상 수 초과·전월 대비 급변)이 섞여 있으면 그 사실을 함께 알린다.
      const changed = this.pcCountChanged();
      const issues = this.pcCountIssues();
      const lines = [`변경한 ${changed}건을 저장할까요?`];
      if (issues > 0) {
        lines.push(`확인이 필요한 값이 ${issues}일 포함돼 있어요.`);
      }
      const ok = await askConfirm({
        message: lines.join("\n"),
        emphasis: lines[0],
        confirmLabel: "저장",
      });
      if (!ok) {
        return;
      }
      this.pcSaving = true;
      // Save pending edits across ALL wards AND months (preserved via loadPatientCountPanel)
      const changedKeys: string[] = [];
      for (const key of this.pcEditing.keys()) {
        if (this.pcIsChangedByKey(key)) {
          changedKeys.push(key);
        }
      }
      const today = Alpine.store("hereby").todayLocal();
      let allSaved = true;
      for (const mapKey of changedKeys) {
        // mapKey format: "wardId|YYYY-MM-DD"
        const sep = mapKey.indexOf("|");
        if (sep < 0) {
          continue;
        }
        const savedWardId = mapKey.slice(0, sep);
        const dateKey = mapKey.slice(sep + 1);
        const value = this.pcEditing.get(mapKey) ?? 0;
        const source =
          this.pcPendingSources.get(mapKey) ??
          this.pcSources.get(mapKey) ??
          WardPatientCountSource.MEASURED;
        try {
          await this.sdk.nurseScheduler.upsertDailyPatientCount({
            wardId: savedWardId,
            date: dateKey,
            patientCount: value,
            source,
          });
          this.pcCounts.set(mapKey, value);
          this.pcSources.set(mapKey, source);
          this.pcPendingSources.delete(mapKey);
          // Only sync the parent scheduler's daily map if the saved ward matches
          if (savedWardId === this.selectedWardId) {
            this.dailyPatientCounts.set(dateKey, value);
            if (dateKey === today) {
              this.todayPatientCount = value;
            }
          }
        } catch {
          allSaved = false;
        }
      }
      this.pcSaving = false;
      // 저장 성공 시 팝업 닫기. 실패 항목이 있으면 유지('변경 N건'으로 남음).
      if (allSaved) {
        this.closePatientCountPanel();
      }
    },

    pcPrevMonthLabel(): string {
      let prevY = this.pcYear;
      let prevM = this.pcMonth - 1;
      if (prevM < 1) {
        prevM = 12;
        prevY--;
      }
      return `${prevY}${_t(this, "common.year_suffix")} ${prevM}${_t(this, "common.month_suffix")}`;
    },

    pcPrevAvg(): string {
      if (this.pcPrevCounts.size === 0) {
        return "-";
      }
      let sum = 0;
      for (const v of this.pcPrevCounts.values()) {
        sum += v;
      }
      return (sum / this.pcPrevCounts.size).toFixed(1);
    },

    pcPrevMax(): number {
      let max = 0;
      for (const v of this.pcPrevCounts.values()) {
        if (v > max) {
          max = v;
        }
      }
      return max;
    },

    pcPrevMin(): number {
      if (this.pcPrevCounts.size === 0) {
        return 0;
      }
      let min = Number.MAX_SAFE_INTEGER;
      for (const v of this.pcPrevCounts.values()) {
        if (v < min) {
          min = v;
        }
      }
      return min;
    },

    pcGetPrevSameDay(day: number): number | null {
      let prevY = this.pcYear;
      let prevM = this.pcMonth - 1;
      if (prevM < 1) {
        prevM = 12;
        prevY--;
      }
      const pad = (n: number) => String(n).padStart(2, "0");
      const key = `${prevY}-${pad(prevM)}-${pad(day)}`;
      return this.pcPrevCounts.get(key) ?? null;
    },

    getMinRequired(day: number, shiftType: string): number | null {
      if (!this.wardStaffingRatios) {
        return null;
      }
      const dateStr = this.dayToDateStr(day);
      const pc = this.dailyPatientCounts.get(dateStr) ?? this.wardBedCount;
      if (pc == null) {
        return null;
      }
      const ratio =
        shiftType === "day"
          ? this.wardStaffingRatios.day
          : shiftType === "evening"
            ? this.wardStaffingRatios.evening
            : this.wardStaffingRatios.night;
      return Math.ceil(pc / ratio);
    },

    getPatientCount(day: number): number | null {
      const dateStr = this.dayToDateStr(day);
      if (!dateStr) {
        return null;
      }
      return this.dailyPatientCounts.get(dateStr) ?? null;
    },

    onPatientCountClick(day: number) {
      this.editingPatientDay = day;
      this.editingPatientValue = this.getPatientCount(day) ?? this.wardBedCount ?? 0;
    },

    movePatientEdit(delta: number) {
      if (this.editingPatientDay === null) {
        return;
      }
      const currentDay = this.editingPatientDay;
      const idx = this.daysInMonth.indexOf(currentDay);
      const newIdx = idx + delta;
      if (newIdx < 0 || newIdx >= this.daysInMonth.length) {
        return;
      }
      const currentValue = this.editingPatientValue;
      const nextDay = this.daysInMonth[newIdx];
      const nextValue = this.getPatientCount(nextDay) ?? this.wardBedCount ?? 0;
      this.editingPatientDay = nextDay;
      this.editingPatientValue = nextValue;
      this.savePatientCountDirect(currentDay, currentValue);
    },

    async savePatientCountDirect(day: number, value: number) {
      if (!(this.sdk && this.selectedWardId)) {
        return;
      }
      const idx = this.daysInMonth.indexOf(day);
      const dateStr = idx >= 0 ? this.cycleDates[idx] : "";
      if (!dateStr) {
        return;
      }
      try {
        await this.sdk.nurseScheduler.upsertDailyPatientCount({
          wardId: this.selectedWardId,
          date: dateStr,
          patientCount: value,
        });
        this.dailyPatientCounts.set(dateStr, value);
        const today = Alpine.store("hereby").todayLocal();
        if (dateStr === today) {
          this.todayPatientCount = value;
        }
      } catch {
        // Error
      }
    },

    async savePatientCount(day: number) {
      if (this.editingPatientDay === null || this.editingPatientDay !== day) {
        return;
      }
      if (!(this.sdk && this.selectedWardId)) {
        return;
      }
      const idx = this.daysInMonth.indexOf(day);
      const dateStr = idx >= 0 ? this.cycleDates[idx] : "";
      if (!dateStr) {
        return;
      }
      try {
        await this.sdk.nurseScheduler.upsertDailyPatientCount({
          wardId: this.selectedWardId,
          date: dateStr,
          patientCount: this.editingPatientValue,
        });
        this.dailyPatientCounts.set(dateStr, this.editingPatientValue);
        const today = Alpine.store("hereby").todayLocal();
        if (dateStr === today) {
          this.todayPatientCount = this.editingPatientValue;
        }
      } catch {
        // Error
      }
      this.editingPatientDay = null;
    },

    cancelPatientCountEdit() {
      this.editingPatientDay = null;
    },

    /**
     * 간호인력확보수준 등급 — 기준표는 @hereby/core/constants 의 정책 상수가 정본.
     * 종별(organizationType)에 따라 일반병동(A·1~6등급)/요양병원(1~6등급) 표가 갈린다.
     */
    getNursingGrade(): {
      rank: number;
      tone: NursingGradeTone | null;
      label: string;
      ratio: number | null;
    } {
      const nurseCount = this.hospitalNurseCount;
      const patientCount = this.hospitalPatientCount;
      if (!(nurseCount && patientCount)) {
        return { rank: 0, tone: null, label: "-", ratio: null };
      }
      const ratio = patientCount / nurseCount;
      const grade = resolveNursingGrade(ratio, this.organizationType);
      if (!grade) {
        return { rank: 0, tone: null, label: "-", ratio: null };
      }
      const gl = _t(this, "nurse.grade_label");
      return { rank: grade.rank, tone: grade.tone, label: `${grade.grade}${gl}`, ratio };
    },

    getNursingGradeClass(): string {
      const { tone } = this.getNursingGrade();
      if (!tone) {
        return "ds-label--gray";
      }
      if (tone === "good") {
        return "ds-label--green";
      }
      if (tone === "warn") {
        return "ds-label--yellow";
      }
      return "ds-label--red";
    },

    getStaffingSummary(): {
      totalNurses: number;
      activeNurses: number;
      patientCount: number;
      surplus: number | null;
      required: {
        day: { nurses: number; aides: number; caregivers: number };
        evening: { nurses: number; aides: number; caregivers: number };
        night: { nurses: number; aides: number; caregivers: number };
      } | null;
    } {
      const totalNurses = this.nurses.length;
      // 배정 인원 — 재직 · 휴직만(퇴사한 간호사 프로필이 남아 있어도 인력으로 세지 않는다)
      const activeNurses = this.nurses.filter(
        (n) => n.workType !== "off_duty" && isCountedNurseStatus(n.employeeStatus),
      ).length;
      const patientCount =
        this.todayPatientCount > 0 ? this.todayPatientCount : (this.wardBedCount ?? 0);
      if (!(this.wardStaffingRatios && patientCount)) {
        return { totalNurses, activeNurses, patientCount, surplus: null, required: null };
      }
      const ratios = this.wardStaffingRatios;
      const aideRatio = this.wardAideRatio;
      const caregiverRatio = this.wardCaregiverRatio;
      const calcShift = (ratio: number) => {
        const nurses = Math.ceil(patientCount / ratio);
        const aides = Math.ceil(nurses * aideRatio);
        const caregivers = Math.ceil(patientCount * caregiverRatio);
        return { nurses, aides, caregivers };
      };
      const required = {
        day: calcShift(ratios.day),
        evening: calcShift(ratios.evening),
        night: calcShift(ratios.night),
      };
      const surplus = activeNurses - required.day.nurses;
      return { totalNurses, activeNurses, patientCount, surplus, required };
    },

    /**
     * 오늘 근무 배정된 간호사 수. 근무표가 로드된 주기 밖(다른 달을 보는 중)이면 null.
     */
    todayWorkingNurses(): number | null {
      const today = Alpine.store("hereby").todayLocal();
      if (!this.cycleDates.includes(today)) {
        return null;
      }
      return this.workingNurseCountByDate(today);
    },

    /**
     * 선택한 병동 기준 간호사 1명당 환자 수 — 분모는 **오늘 근무 배정 인원**이다.
     *
     * 배정 인원(getStaffingSummary().activeNurses)을 쓰면 3교대 병동에서
     * 「간호사 1명당 환자 0.2명」 같은 성립 불가 수치가 나온다(배정 213명 ÷ 환자 35).
     * 환자수 관리 팝업의 일별 비율도 같은 기준(근무 배정)이라 두 화면이 일치한다.
     */
    getWardNurseRatio(): number | null {
      const nurses = this.todayWorkingNurses();
      const { patientCount } = this.getStaffingSummary();
      if (!(nurses && patientCount)) {
        return null;
      }
      return patientCount / nurses;
    },

    /**
     * 간호사 비율 툴팁 — 두 기준(병동/병원 전체)이 각각 어떤 수에서 나왔는지 밝힌다.
     * 줄바꿈이 있으면 tooltip.ts가 멀티라인 버블로 자동 전환한다(클래스 지정 불필요).
     */
    nurseRatioTooltip(): string {
      const lines = ["간호사 1명이 맡는 환자 수입니다. (환자수 ÷ 근무 간호사수)"];
      const wardRatio = this.getWardNurseRatio();
      const working = this.todayWorkingNurses();
      const { patientCount } = this.getStaffingSummary();
      if (wardRatio != null) {
        lines.push(
          `병동: 환자 ${patientCount}명 ÷ 오늘 근무 ${working}명 = 1:${wardRatio.toFixed(1)}`,
        );
      } else if (working) {
        // 환자수 0 = 대부분 "오늘 환자수 미입력 + 병상수 미설정"이라 계산할 모수가 없다.
        // 값을 0으로 채워 1:0.0을 보여주면 데이터가 있는 것처럼 읽히므로 사유를 밝힌다.
        lines.push("병동: 오늘 환자수·병상수가 없어 계산할 수 없습니다");
      } else {
        lines.push("병동: 오늘 근무로 배정된 간호사가 없어 계산할 수 없습니다");
      }
      const hospitalRatio = this.getNursingGrade().ratio;
      if (hospitalRatio != null) {
        // 병원 전체는 근무 배정이 아니라 배정 인원(등록 간호사) 기준 — 등급 산정 기준이라
        // 그대로 두되, 기준이 다르다는 사실을 반드시 밝힌다(두 값이 안 맞아 보이는 이유).
        lines.push(
          `병원 전체: 환자 ${this.hospitalPatientCount}명 ÷ 등록 간호사 ${this.hospitalNurseCount}명 = 1:${hospitalRatio.toFixed(1)}`,
        );
        lines.push("병동은 오늘 근무 인원, 병원 전체는 등록 인원 기준이라 값이 다릅니다.");
      }
      lines.push("간호등급은 병원 전체 비율로 산정합니다.");
      return lines.join("\n");
    },

    async loadSchedule() {
      if (!(this.sdk && this.selectedWardId)) {
        return;
      }
      this.loading = true;
      try {
        const cycle = this.getCycleDates(this.period.year, this.period.month);
        this.cycleStartDate = cycle.startDate;
        this.cycleEndDate = cycle.endDate;
        const pad = (n: number) => String(n).padStart(2, "0");
        const [sy, sm, sd] = cycle.startDate.split("-").map(Number);
        const [ey, em, ed] = cycle.endDate.split("-").map(Number);
        const start = new Date(sy, sm - 1, sd);
        const end = new Date(ey, em - 1, ed);
        const dates: string[] = [];
        const dayNums: number[] = [];
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          const ds = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
          dates.push(ds);
          dayNums.push(d.getDate());
        }

        // 한 배치 — 그리드에 필요한 읽기는 전부 독립이라 같은 왕복에 싣는다.
        //  · 휴일 캐시(달력 빨간날·isHoliday 바인딩용, 연도 경계면 두 해) — 예전엔 다른 읽기보다
        //    먼저 따로 await 했지만, 날짜 상태(cycleDates)를 아래에서 배치가 끝난 뒤 세팅하므로
        //    렌더 시점에 캐시가 채워져 있다는 보장은 그대로다.
        //  · 간호사 프로필 · 월 환자수 · 희망근무 — 환자수/희망근무 실패는 삼켜서 그리드를 비우지 않는다.
        //  · 근무표 — `list(limit 1)` 로 id 를 얻고 `get(id)` 로 entries 를 다시 받던 두 왕복을
        //    findByPeriod 한 번으로 대체 (entries 포함).
        //  · 인력 · 근무표는 스케줄러 단위(profileScope) — 한 병동에 스케줄러가 여럿이면 각각 따로 뜬다.
        const [profiles, sched] = await Promise.all([
          fetchAllPages<NurseProfileItem>(
            (page, limit) =>
              this.sdk.nurseScheduler.listProfiles(this.profileScope(), { page, limit }),
            500,
          ),
          this.sdk.nurseScheduler.findScheduleByPeriod({
            wardId: this.selectedWardId,
            scheduleGroupId: this.selectedScheduleGroupId || undefined,
            year: this.period.year,
            month: this.period.month,
          }),
          ensureHolidayYears(this.sdk, [sy, ey]),
          this.loadMonthlyPatientCounts().catch(() => undefined),
          this.loadPreferencesForCurrentMonth().catch(() => undefined),
        ]);
        this.cycleDates = dates;
        this.daysInMonth = dayNums;
        this.rebuildDayDateStrMap();
        this.nurses = profiles.map((p: NurseProfileItem) => this.toNurseRow(p));
        this.rebuildCellIndexMaps();
        this.applyPendingFocusEmployee();

        if (sched) {
          this.scheduleId = sched.id;
          this.scheduleStatus = sched.status;
          if (sched.startDate) {
            this.cycleStartDate = toDateStr(sched.startDate);
          }
          if (sched.endDate) {
            this.cycleEndDate = toDateStr(sched.endDate);
          }
          if (sched.status === "generating" && !this.generationPoller) {
            this.generating = true;
            this.loading = false;
            this.startGenerationPolling();
            return;
          }

          this.entries = new Map();
          for (const e of sched.entries || []) {
            const dateStr = toDateStr(e.date);
            // leave_custom → 휴가 유형 id, shift_custom → 근무 유형 shift id를 셀 값으로 보관 (라벨/색 해석용)
            const cellValue =
              e.assignmentType === "leave_custom" && e.customLeaveReasonId
                ? e.customLeaveReasonId
                : e.assignmentType === "shift_custom" && e.shiftId
                  ? e.shiftId
                  : e.assignmentType;
            this.entries.set(`${e.employeeId}:${dateStr}`, cellValue);
          }
          // 준수율 검증은 그리드를 막지 않는다 — 뒤따라 오면 채우고, 그 사이 월/병동이 바뀌어
          // 다른 근무표가 떠 있으면 버린다 (scheduleId 로 대조). 실패는 null 로 둔다.
          this.complianceRate = null;
          this.siblingScheduleCount = 0;
          const validatedId = sched.id;
          this.sdk.nurseScheduler
            .validateSchedule(validatedId)
            .then((validation) => {
              if (this.scheduleId === validatedId) {
                this.complianceRate = validation?.complianceRate ?? null;
                // 최소 인원은 병동 합산 — 다른 스케줄러 근무표가 섞였으면 화면에 그 사실을 밝힌다.
                this.siblingScheduleCount = validation?.siblingScheduleIds?.length ?? 0;
              }
            })
            .catch(() => undefined);
          // 겸임 중복 근무도 그리드를 막지 않고 뒤따라 표시한다.
          this.refreshDupConflicts();

          // metadata 는 Record<string, unknown> — 생성기가 남긴 warnings(string[]) 만 골라 쓴다.
          const metaWarnings = sched.metadata?.warnings;
          if (Array.isArray(metaWarnings)) {
            this.warnings = metaWarnings.map((w) => {
              const [date, ...rest] = String(w).split(": ");
              return { date, message: rest.join(": ") };
            });
          } else {
            this.warnings = [];
          }
        } else {
          this.scheduleId = null;
          this.scheduleStatus = "";
          this.entries = new Map();
          this.warnings = [];
          this.complianceRate = null;
          this.siblingScheduleCount = 0;
          this.refreshDupConflicts();
        }
      } catch {
        this.nurses = [];
        this.entries = new Map();
      }
      // 희망근무(loadPreferencesForCurrentMonth)는 위 Phase 1 에서 병렬로 이미 로드됨.
      // 출근현황 토글이 켜진 상태에서 월/병동을 바꾸면 새 cycleDates·nurses·entries 에 맞춰
      // attendance overlay 도 다시 계산되어야 한다. 안 그러면 토글이 켜져 있어도 빈 화면.
      if (this.showAttendance) {
        await this.loadAttendanceOverlay();
      }
      this.loading = false;
    },

    /** 프로필 응답 → 그리드/인력 행. 세 탭(근무표·희망근무 입력·인력)이 같은 모양을 쓴다. */
    toNurseRow(p: NurseProfileItem): NurseRow {
      return {
        employeeId: p.employeeId,
        employeeNumber: p.employee?.employeeNumber || "",
        name: `${p.employee?.lastName || ""}${p.employee?.firstName || ""}`,
        workType: p.workType || "",
        wardId: p.ward?.id || p.wardId || "",
        wardName: p.ward?.name || "",
        scheduleGroupId: p.scheduleGroupId || p.scheduleGroup?.id || "",
        scheduleGroupName: p.scheduleGroup?.name || "",
        isHeadNurse: !!p.isHeadNurse,
        profileId: p.id,
        totalWardCount: p.totalWardCount ?? 1,
        employeeStatus: p.employee?.status,
        pendingReservation: p.pendingReservation ?? null,
      };
    },

    /** 대기 예약 표시 문구 「병동 · 스케줄러」 — 스케줄러 이름이 없으면(예전 예약) 병동만. */
    reservationTargetLabel(r: PendingReservation | null | undefined): string {
      if (!r) {
        return "";
      }
      return r.toScheduleGroupName ? `${r.toWardName} · ${r.toScheduleGroupName}` : r.toWardName;
    },

    // === 겸임 중복 근무 (entry.conflicts) ===

    /** 배정 저장·근무표 로드 뒤 다시 받는다. 응답 사이에 근무표가 바뀌었으면 버린다(scheduleId 대조). */
    async refreshDupConflicts() {
      const sid = this.scheduleId;
      if (!(this.sdk && sid)) {
        this.dupConflicts = [];
        this.dupConflictByCell = {};
        this.updateDupConflictStyles();
        return;
      }
      try {
        const result = await this.sdk.nurseScheduler.getScheduleConflicts(sid);
        if (this.scheduleId !== sid) {
          return;
        }
        const items: DupConflict[] = result?.items ?? [];
        const byCell: Record<string, DupConflict[]> = {};
        for (const c of items) {
          const key = `${c.employeeId}:${c.date}`;
          const list = byCell[key] ?? [];
          list.push(c);
          byCell[key] = list;
        }
        this.dupConflicts = items;
        this.dupConflictByCell = byCell;
      } catch {
        // 조회 실패 — 이전 표시를 유지한다(경고는 보조 정보라 그리드를 막지 않는다)
      }
      this.updateDupConflictStyles();
    },

    /** 다른 근무표 쪽 배정 라벨 — 연동 근무 유형(shift_custom)은 근무 유형 이름으로. */
    dupAssignmentLabel(c: DupConflict): string {
      if (c.otherAssignmentType === "shift_custom" && c.otherShiftId) {
        const shift = this.availableShifts.find((sh: { id: string }) => sh.id === c.otherShiftId);
        if (shift) {
          return shift.name || shift.code || "근무";
        }
      }
      return getAssignmentLabels(this)[c.otherAssignmentType] || c.otherAssignmentType;
    },

    /** 셀 툴팁 — 줄바꿈이 있어 DS 멀티라인 툴팁이 된다. 여러 근무표와 겹치면 한 줄씩. */
    dupConflictTitle(list: DupConflict[]): string {
      const lines = list.map(
        (c) =>
          `${c.otherWardName} · ${c.otherScheduleGroupName || "기본"}: ${this.dupAssignmentLabel(c)}`,
      );
      return ["겸임 중복 근무", ...lines].join("\n");
    },

    /** 목록 모달용 행 — 이름·날짜 순. */
    get dupConflictRows(): Array<{
      key: string;
      name: string;
      date: string;
      other: string;
      assignment: string;
    }> {
      const nameOf = (empId: string) =>
        this.nurses.find((n: NurseRow) => n.employeeId === empId)?.name || "";
      return this.dupConflicts
        .map((c: DupConflict) => ({
          key: `${c.employeeId}:${c.date}:${c.otherScheduleId}`,
          name: nameOf(c.employeeId),
          date: c.date,
          other: `${c.otherWardName} · ${c.otherScheduleGroupName || "기본"}`,
          assignment: this.dupAssignmentLabel(c),
        }))
        .sort((a, b) => a.name.localeCompare(b.name, "ko") || a.date.localeCompare(b.date));
    },

    /** 셀 표시 — 선택 하이라이트와 같은 방식으로 셀별 CSS 규칙을 생성한다(6천 셀에 바인딩을 얹지 않는다).
     *  좌하단 앰버 삼각형(::after — ::before 는 선택 테두리가 쓴다) + 셀 title(멀티라인 DS 툴팁).
     *  title 은 렌더 뒤 DOM 에 직접 얹는다 — 그리드가 다시 그려지면(loadSchedule) 이 함수도 다시 돈다. */
    updateDupConflictStyles() {
      let el = document.getElementById("sched-dup-styles");
      if (!el) {
        el = document.createElement("style");
        el.id = "sched-dup-styles";
        document.head.appendChild(el);
      }
      const byCell = this.dupConflictByCell as Record<string, DupConflict[]>;
      const keys = Object.keys(byCell);
      const rules: string[] = [];
      for (const key of keys) {
        rules.push(
          `td[data-ck="${key}"]::after{content:"";position:absolute;left:0;bottom:0;width:0;height:0;border-bottom:8px solid var(--color-hr-amber-500);border-right:8px solid transparent;pointer-events:none;z-index:4}`,
        );
      }
      el.textContent = rules.join("\n");
      // title — Alpine 이 셀을 그린 다음 프레임에 얹는다(loadSchedule 직후엔 아직 td 가 없을 수 있다).
      requestAnimationFrame(() => {
        for (const td of document.querySelectorAll<HTMLElement>("td[data-ck][data-dup-tip]")) {
          const ck = td.dataset.ck || "";
          if (!byCell[ck]) {
            td.removeAttribute("title");
            td.removeAttribute("data-tip-title");
            td.removeAttribute("aria-label");
            td.removeAttribute("data-dup-tip");
          }
        }
        for (const key of keys) {
          const td = document.querySelector<HTMLElement>(`td[data-ck="${key}"]`);
          if (td) {
            td.setAttribute("title", this.dupConflictTitle(byCell[key]));
            td.dataset.dupTip = "1";
          }
        }
      });
    },

    /** 현재 ward/year/month 의 submitted 희망근무를 employeeId 로 인덱싱.
     *  토글 OFF면 보이진 않지만 미리 로드해두면 모드 전환이 즉시. */
    async loadPreferencesForCurrentMonth() {
      if (!(this.sdk && this.selectedWardId)) {
        this.prefByEmployee = {};
        this.rebuildPrefCellIndex();
        return;
      }
      try {
        const result = await this.sdk.nurseScheduler.listPreferences(
          {
            wardId: this.selectedWardId,
            year: this.period.year,
            month: this.period.month,
            status: "submitted",
          },
          { page: 1, limit: 500 },
        );
        const map: typeof this.prefByEmployee = {};
        for (const p of result.items || []) {
          map[p.employeeId] = {
            employeeId: p.employeeId,
            vacationDates: p.vacationDates || [],
            restrictedDates: p.restrictedDates || [],
            unavailableDates: p.unavailableDates || [],
            preferredDates: p.preferredDates || [],
            notes: p.notes ?? undefined,
          };
        }
        this.prefByEmployee = map;
        this.rebuildPrefCellIndex();
      } catch (e: unknown) {
        // biome-ignore lint/suspicious/noConsole: 디버깅용
        console.error("Failed to load preferences:", e);
        this.prefByEmployee = {};
        this.rebuildPrefCellIndex();
      }
    },

    /** 특정 셀(emp, date)에 대한 희망근무 매칭 결과 — O(1) 룩업.
     *  실제 매칭 로직·우선순위는 rebuildPrefCellIndex 에서 1회 계산해 둔다.
     *  그리드 셀당 ~20회 호출되므로 여기서 배열 스캔을 하면 안 된다. */
    getPrefForCell(employeeId: string, dateStr: string): PrefCellResult {
      return this.prefCellIndex.get(`${employeeId}:${dateStr}`) ?? EMPTY_PREF;
    },

    /** 셀 native title(hover 툴팁) 문자열 — @mouseenter 에서 lazy 계산.
     *  셀마다 :title 바인딩(effect)을 두지 않으려고 분리: 12k개 effect 생성 비용 제거. */
    prefCellTitle(employeeId: string, dateStr: string): string {
      const p = this.getPrefForCell(employeeId, dateStr);
      if (!p.type) {
        return "";
      }
      if (p.type === "vacation") {
        return "휴가 희망";
      }
      if (p.type === "restricted") {
        return `시프트 제한: ${p.restrictedShifts.join(",")} / ${p.reason}`;
      }
      if (p.type === "preferred") {
        return `원하는 시프트: ${p.preferredShifts.join(",")}${p.reason ? ` / ${p.reason}` : ""}`;
      }
      return `근무 불가: ${p.reason}`;
    },

    /** prefByEmployee 로부터 (employeeId:dateStr) → PrefCellResult 인덱스를 1회 구축.
     *  우선순위: unavailable > preferred > restricted > vacation. 역순으로 채워
     *  높은 우선순위가 덮어쓰게 한다. (getPrefForCell 의 원래 return 순서와 동일) */
    rebuildPrefCellIndex() {
      const index = new Map<string, PrefCellResult>();
      for (const employeeId of Object.keys(this.prefByEmployee)) {
        const pref = this.prefByEmployee[employeeId];
        const set = (dateStr: string, result: PrefCellResult) => {
          index.set(`${employeeId}:${dateStr}`, result);
        };
        // 낮은 우선순위부터: vacation → restricted → preferred → unavailable
        for (const vd of pref.vacationDates) {
          // 레거시(string) | 신규({date, leaveTypeId}) 혼용.
          const dateStr = typeof vd === "string" ? vd : vd.date;
          const vacationLeaveId = typeof vd === "string" ? undefined : vd.leaveTypeId;
          set(dateStr, {
            type: "vacation",
            reason: "휴가 희망",
            restrictedShifts: [],
            preferredShifts: [],
            vacationLeaveId,
          });
        }
        for (const r of pref.restrictedDates) {
          set(r.date, {
            type: "restricted",
            reason: r.reason || "시프트 제한",
            restrictedShifts: r.restrictedShifts || [],
            preferredShifts: [],
          });
        }
        for (const p of pref.preferredDates) {
          if (p.preferredShifts.length > 0) {
            set(p.date, {
              type: "preferred",
              reason: p.reason || "원하는 시프트",
              restrictedShifts: [],
              preferredShifts: p.preferredShifts,
            });
          }
        }
        for (const u of pref.unavailableDates) {
          set(u.date, {
            type: "unavailable",
            reason: u.reason || "근무 불가",
            restrictedShifts: [],
            preferredShifts: [],
          });
        }
      }
      this.prefCellIndex = index;
    },

    /** 이 간호사가 메모 또는 휴가/제한/불가/원함 항목을 하나라도 갖고 있는지. */
    hasNotablePref(employeeId: string): boolean {
      const pref = this.prefByEmployee[employeeId];
      if (!pref) {
        return false;
      }
      return (
        !!pref.notes ||
        pref.restrictedDates.length > 0 ||
        pref.unavailableDates.length > 0 ||
        pref.vacationDates.length > 0 ||
        pref.preferredDates.length > 0
      );
    },

    /** 이름 옆 📝 노출 조건 — '메모(텍스트)' 가 있을 때만.
     *  표 자체가 시프트/날짜/타입을 다 보여주므로, popover 가 추가해주는 가치는 '텍스트' 뿐.
     *  → notes 가 있거나 / 날짜별 reason 텍스트가 하나라도 있을 때 노출. */
    hasNurseMemo(employeeId: string): boolean {
      const pref = this.prefByEmployee[employeeId];
      if (!pref) {
        return false;
      }
      if (pref.notes?.trim()) {
        return true;
      }
      const hasReason = (arr: { reason?: string }[]) => arr.some((d) => d.reason?.trim());
      return (
        hasReason(pref.restrictedDates) ||
        hasReason(pref.unavailableDates) ||
        hasReason(pref.preferredDates)
      );
    },

    /** 간호사 셀(간호사 란) 코너 삼각형용 — '월 단위 메모'(notes)만 본다.
     *  날짜별 사유는 각 근무 셀 삼각형(hasCellMemo)이 담당하므로 여기서 제외. */
    hasNurseNote(employeeId: string): boolean {
      return !!this.prefByEmployee[employeeId]?.notes?.trim();
    },

    /** 근무 셀 코너 삼각형용 — 해당 (간호사·날짜)에 사용자가 입력한 메모(사유) 유무.
     *  주의: getPrefForCell().reason 은 rebuildPrefCellIndex 에서 '원하는 시프트' 등
     *  기본 라벨로 채워지므로(빈 값 아님) 셀 유형 판별엔 못 쓴다. 원본 날짜 항목의
     *  reason(=메모(선택) 입력값)만 본다. hasNurseMemo 의 날짜별 로직과 동일 기준. */
    hasCellMemo(employeeId: string, dateStr: string): boolean {
      const pref = this.prefByEmployee[employeeId];
      if (!pref) {
        return false;
      }
      const hasReasonOn = (arr: { date: string; reason?: string }[]) =>
        arr.some((d) => d.date === dateStr && d.reason?.trim());
      return (
        hasReasonOn(pref.restrictedDates) ||
        hasReasonOn(pref.unavailableDates) ||
        hasReasonOn(pref.preferredDates)
      );
    },

    /** 한 간호사의 날짜별 사유 텍스트만 모아 시간순으로 정렬. (popover 의 '날짜별 사유' 영역용)
     *  labels: 셀에 표시되는 선택된 시프트(D/E/N) 또는 카테고리(불/휴) 라벨 + pref-corner 색상 변형.
     *  className 은 그리드 셀과 동일한 톤(.pref-corner--*)으로 매핑해 시각 일관성 유지. */
    nurseMemoEntries(employeeId: string): {
      date: string;
      reason: string;
      kind: string;
      labels: { text: string; className: string }[];
    }[] {
      const pref = this.prefByEmployee[employeeId];
      if (!pref) {
        return [];
      }
      const preferredLabel = (s: string): { text: string; className: string } => {
        const k = s.toLowerCase();
        if (k === "day") {
          return { text: "D", className: "pref-corner--preferred-day" };
        }
        if (k === "evening") {
          return { text: "E", className: "pref-corner--preferred-evening" };
        }
        if (k === "night") {
          return { text: "N", className: "pref-corner--preferred-night" };
        }
        if (k === "off") {
          return { text: "휴", className: "pref-corner--vacation" };
        }
        return { text: s, className: "pref-corner--preferred" };
      };
      const restrictedLabel = (s: string): { text: string; className: string } => {
        const k = s.toLowerCase();
        if (k === "day") {
          return { text: "D", className: "pref-corner--restricted" };
        }
        if (k === "evening") {
          return { text: "E", className: "pref-corner--restricted" };
        }
        if (k === "night") {
          return { text: "N", className: "pref-corner--restricted" };
        }
        return { text: s, className: "pref-corner--restricted" };
      };
      const out: {
        date: string;
        reason: string;
        kind: string;
        labels: { text: string; className: string }[];
      }[] = [];
      for (const r of pref.preferredDates) {
        if (r.reason?.trim()) {
          out.push({
            date: r.date,
            reason: r.reason,
            kind: "선호",
            labels: (r.preferredShifts || []).map(preferredLabel),
          });
        }
      }
      for (const r of pref.restrictedDates) {
        if (r.reason?.trim()) {
          out.push({
            date: r.date,
            reason: r.reason,
            kind: "제한",
            labels: (r.restrictedShifts || []).map(restrictedLabel),
          });
        }
      }
      for (const r of pref.unavailableDates) {
        if (r.reason?.trim()) {
          out.push({
            date: r.date,
            reason: r.reason,
            kind: "불가",
            labels: [{ text: "불", className: "pref-corner--unavailable" }],
          });
        }
      }
      out.sort((a, b) => a.date.localeCompare(b.date));
      return out;
    },

    getPrefForEmployee(employeeId: string) {
      return this.prefByEmployee[employeeId] || null;
    },

    /** 이름 옆 📝 클릭 시 상세 모달 열기 (legacy — 새 인라인 popover 로 대체됨, 일단 보존). */
    openPrefDetailModal(employeeId: string) {
      this.prefDetailEmployeeId = employeeId;
      this.showPrefDetailModal = true;
    },

    /** 이름 옆 📝 → 인라인 메모 popover 오픈.
     *  클릭한 버튼 위치 기준으로 anchor. 월 단위 notes 와 날짜별 사유를 한 화면에 모아 보여줌. */
    openNurseMemoPopover(employeeId: string, ev: MouseEvent) {
      const nurse = this.nurses.find((n) => n.employeeId === employeeId);
      const pref = this.prefByEmployee[employeeId];
      const btn =
        (ev.currentTarget as HTMLElement | null) ||
        ((ev.target as HTMLElement | null)?.closest("button") ?? null);
      const rect = btn?.getBoundingClientRect();
      this.nurseMemoPopover = {
        visible: true,
        employeeId,
        employeeName: nurse?.name ?? "",
        notesDraft: pref?.notes ?? "",
        anchorX: rect ? rect.right : ev.clientX,
        anchorY: rect ? rect.bottom : ev.clientY,
      };
    },

    closeNurseMemoPopover() {
      this.nurseMemoPopover.visible = false;
    },

    /** popover 의 textarea 값을 서버에 반영 — notes 만 업데이트, 날짜별 데이터는 그대로. */
    async saveNurseMemo() {
      if (!this.sdk) {
        return;
      }
      const empId = this.nurseMemoPopover.employeeId;
      if (!empId) {
        return;
      }
      const existing = this.prefByEmployee[empId];
      const trimmed = this.nurseMemoPopover.notesDraft.trim();
      try {
        await this.sdk.nurseScheduler.upsertPreferenceByManager({
          employeeId: empId,
          year: this.period.year,
          month: this.period.month,
          wardId: this.selectedWardId,
          vacationDates: existing?.vacationDates ?? [],
          // biome-ignore lint/suspicious/noExplicitAny: enum stored as string
          restrictedDates: (existing?.restrictedDates ?? []) as any,
          unavailableDates: existing?.unavailableDates ?? [],
          // biome-ignore lint/suspicious/noExplicitAny: enum stored as string
          preferredDates: (existing?.preferredDates ?? []) as any,
          notes: trimmed || undefined,
        });
      } catch (e) {
        // biome-ignore lint/suspicious/noConsole: 디버깅용
        console.error("Failed to save nurse memo:", e);
      }
      this.closeNurseMemoPopover();
      await this.loadPreferencesForCurrentMonth();
    },

    /** 메모 popover 외부 클릭 시 닫기 — 다른 popover 패턴과 동일. */
    handleNurseMemoClickOutside(event: MouseEvent) {
      if (!this.nurseMemoPopover.visible) {
        return;
      }
      const target = event.target as HTMLElement | null;
      if (!target) {
        return;
      }
      if (target.closest("[data-nurse-memo-popover]")) {
        return;
      }
      if (target.closest("[data-nurse-memo-trigger]")) {
        return;
      }
      this.closeNurseMemoPopover();
    },

    // === 희망근무 입력 (pref-input 서브탭) ===

    /** 현재 ward + 월의 간호사 목록 + 희망근무 둘 다 로드.
     *  schedule 탭과 달리 NurseSchedule(근무표 자체)는 필요 없음 — 간호사 명단만. */
    async loadPrefInputData() {
      if (!(this.sdk && this.selectedWardId)) {
        return;
      }
      this.loading = true;
      try {
        // 간호사 명단 — 선택 스케줄러 단위(희망근무 자체는 병동 단위로 저장되지만 명단은 근무표와 같은 범위)
        const items = await fetchAllPages<NurseProfileItem>(
          (page, limit) =>
            this.sdk.nurseScheduler.listProfiles(this.profileScope(), { page, limit }),
          500,
        );
        this.nurses = items.map((p: NurseProfileItem) => this.toNurseRow(p));
        // 한 달치 cycleDates 도 미리 계산 (그리드 표시용) — schedule 탭과 동일 규칙.
        this.computeCycleDatesForCurrentMonth();
      } catch (e) {
        // biome-ignore lint/suspicious/noConsole: 디버깅용
        console.error("Failed to load pref-input nurses:", e);
        this.nurses = [];
      }
      // submitted 희망근무 인덱싱은 기존 헬퍼 재사용.
      await this.loadPreferencesForCurrentMonth();
      this.loading = false;
    },

    /** 한 달치 날짜 배열 — schedule 탭의 cycleDates 와 별개로 단순 1~말일 */
    computeCycleDatesForCurrentMonth() {
      const y = this.period.year;
      const m = this.period.month;
      const lastDay = new Date(y, m, 0).getDate();
      const dates: string[] = [];
      const nums: number[] = [];
      for (let d = 1; d <= lastDay; d++) {
        dates.push(`${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
        nums.push(d);
      }
      this.cycleDates = dates;
      this.daysInMonth = nums;
      this.rebuildDayDateStrMap();
    },

    /** 리스트 뷰: 간호사 한 줄당 상태 + 입력 건수 + 마지막 수정 */
    get prefInputRows() {
      return this.nurses.map((n) => {
        const pref = this.prefByEmployee[n.employeeId];
        const count = pref
          ? pref.vacationDates.length +
            pref.restrictedDates.length +
            pref.unavailableDates.length +
            pref.preferredDates.length
          : 0;
        const status: "submitted" | "missing" = pref && count > 0 ? "submitted" : "missing";
        return {
          employeeId: n.employeeId,
          name: n.name,
          employeeNumber: n.employeeNumber,
          workType: n.workType,
          isHeadNurse: n.isHeadNurse,
          status,
          count,
        };
      });
    },

    /** 미입력자 수 — 토스트/배지용. 옆의 「총」과 같은 모수(재직 + 휴직)만 센다. */
    get prefInputMissingCount() {
      const counted = new Set(
        this.nurses.filter((n) => isCountedNurseStatus(n.employeeStatus)).map((n) => n.employeeId),
      );
      return this.prefInputRows.filter((r) => r.status === "missing" && counted.has(r.employeeId))
        .length;
    },

    /**
     * 명단 인원 — 총 = 재직(ACTIVE) + 휴직(ON_LEAVE), 퇴사(TERMINATED)는 따로 센다. 비활성은 어디에도 없다.
     * 퇴사자는 지난 근무표 때문에 명단(nurses)에 그대로 남아 있어 nurses.length 를 쓰면 부풀려진다.
     */
    get nurseHeadcount(): { total: number; terminated: number } {
      let total = 0;
      let terminated = 0;
      for (const n of this.nurses) {
        if (isCountedNurseStatus(n.employeeStatus)) {
          total++;
        } else if (n.employeeStatus === "TERMINATED") {
          terminated++;
        }
      }
      return { total, terminated };
    },

    /** popover 저장 가능 여부 — 단일 진실. UI 의 :disabled 가 이 값만 보면 됨. */
    get canSavePrefInput(): boolean {
      const p = this.prefInputPopover;
      if (!p.category) {
        return false;
      }
      if (p.category === "wish") {
        // 희망: 시프트 한 개 선택 필요 (D/E/N/OFF)
        return p.shifts.length > 0;
      }
      if (p.category === "vacation") {
        // 휴가는 단순 날짜 마킹 — 추가 입력 없음
        return true;
      }
      // 제약
      if (p.type === "unavailable") {
        // 전체 — 시프트 선택 없이도 저장 가능
        return true;
      }
      if (p.type === "restricted") {
        // 특정 시프트 — 시프트 한 개 선택 필요
        return p.shifts.length > 0;
      }
      return false;
    },

    // ─────────── 희망근무 그리드 셀 선택 (근무표와 같은 패턴) ───────────

    /** key = `${employeeId}:${dateStr}` */
    prefCellKeyAt(rowIdx: number, colIdx: number): string | null {
      const nurse = this.nurses[rowIdx];
      const dateStr = this.cycleDates[colIdx];
      return nurse && dateStr ? `${nurse.employeeId}:${dateStr}` : null;
    },

    /** 두 좌표 사이의 모든 셀 key 집합 (bounding box). */
    prefKeysInRange(
      a: { rowIdx: number; colIdx: number },
      b: { rowIdx: number; colIdx: number },
    ): Set<string> {
      const minR = Math.min(a.rowIdx, b.rowIdx);
      const maxR = Math.max(a.rowIdx, b.rowIdx);
      const minC = Math.min(a.colIdx, b.colIdx);
      const maxC = Math.max(a.colIdx, b.colIdx);
      const out = new Set<string>();
      for (let r = minR; r <= maxR; r++) {
        for (let c = minC; c <= maxC; c++) {
          const k = this.prefCellKeyAt(r, c);
          if (k) {
            out.add(k);
          }
        }
      }
      return out;
    },

    /** mousedown: 단순/Shift/Cmd 분기. 근무표 onCellMouseDown 패턴 그대로.
     *  단순 클릭은 isDragging=true 로 시작 → mouseenter 에서 range 확장.
     *  prefDragHappened = false 로 리셋 — mouseenter 가 발생해야 true 가 됨 (실제 드래그 판정용). */
    onPrefCellMouseDown(event: MouseEvent, rowIdx: number, colIdx: number) {
      if (event.button !== 0) {
        return;
      }
      const key = this.prefCellKeyAt(rowIdx, colIdx);
      if (!key) {
        return;
      }
      event.preventDefault();
      this.prefDragHappened = false;
      let openOnUp: { rowIdx: number; colIdx: number } | null = null;
      if (event.shiftKey && this.prefLastClickedCell) {
        this.prefSelectedCells = this.prefKeysInRange(this.prefLastClickedCell, { rowIdx, colIdx });
        // Shift 범위 선택도 popover 즉시 — 근무표 탭과 동일 (mouseup 의 isDragging 분기로는 안 잡혀서 따로 마킹).
        openOnUp = { rowIdx, colIdx };
      } else if (event.metaKey || event.ctrlKey) {
        const next = new Set(this.prefSelectedCells);
        if (next.has(key)) {
          next.delete(key);
        } else {
          next.add(key);
        }
        this.prefSelectedCells = next;
        // Ctrl/Meta 토글은 popover 안 열기 — 개별 선택 추가/제거용.
      } else {
        this.prefSelectedCells = new Set([key]);
        this.prefDragStartCell = { rowIdx, colIdx };
        this.prefIsDragging = true;
      }
      this.prefCursorCell = { rowIdx, colIdx };
      this.prefLastClickedCell = { rowIdx, colIdx };
      this.prefSelectionVersion++;
      this.updatePrefSelectionStyles();
      // Shift-click 경로 — mouseup 의 prefIsDragging 분기를 못 타므로 여기서 직접 popover 오픈.
      if (openOnUp) {
        const nurse = this.nurses[openOnUp.rowIdx];
        const dateStr = this.cycleDates[openOnUp.colIdx];
        if (nurse && dateStr) {
          this.openPrefInputPopover(nurse.employeeId, dateStr, event);
        }
      }
    },

    /** mouseenter: 드래그 중이면 range 확장 (RAF 배칭 — 근무표와 동일 패턴).
     *  prefDragHappened = true 표시해서 mouseup 이 click 인지 drag 인지 판단 가능하게. */
    onPrefCellMouseEnter(rowIdx: number, colIdx: number) {
      if (!(this.prefIsDragging && this.prefDragStartCell)) {
        return;
      }
      // 시작 셀에 머물러있으면 드래그 아님
      if (rowIdx !== this.prefDragStartCell.rowIdx || colIdx !== this.prefDragStartCell.colIdx) {
        this.prefDragHappened = true;
      }
      this.prefPendingDragTo = { rowIdx, colIdx };
      if (this.prefDragRafPending) {
        return;
      }
      this.prefDragRafPending = true;
      requestAnimationFrame(() => {
        this.prefDragRafPending = false;
        if (this.prefPendingDragTo && this.prefDragStartCell) {
          this.prefSelectedCells = this.prefKeysInRange(
            this.prefDragStartCell,
            this.prefPendingDragTo,
          );
          this.prefSelectionVersion++;
          this.updatePrefSelectionStyles();
        }
      });
    },

    /** mouseup: window 레벨 (그리드 밖에서 손 떼도 동작). 근무표 onCellMouseUp 패턴 그대로.
     *  - 드래그 종료: 마지막 range 확정 + 스타일 전환 (블루 bg → 블랙 보더)
     *  - 단순 클릭(드래그 없음 + 모디파이어 없음): popover 자동 오픈
     *  - anchor: 드래그면 끝점(pendingDragTo), 클릭이면 lastClickedCell */
    onPrefCellMouseUp() {
      if (!this.prefIsDragging) {
        return;
      }
      const wasDrag = this.prefDragHappened;
      // popover anchor 셀 결정 (reset 전에 캡처)
      const anchorCell =
        wasDrag && this.prefPendingDragTo ? this.prefPendingDragTo : this.prefLastClickedCell;
      this.prefIsDragging = false;
      if (this.prefPendingDragTo && this.prefDragStartCell) {
        this.prefSelectedCells = this.prefKeysInRange(
          this.prefDragStartCell,
          this.prefPendingDragTo,
        );
        this.prefPendingDragTo = null;
      }
      this.prefDragStartCell = null;
      this.prefSelectionVersion++;
      this.updatePrefSelectionStyles();
      if (!anchorCell) {
        return;
      }
      const nurse = this.nurses[anchorCell.rowIdx];
      const dateStr = this.cycleDates[anchorCell.colIdx];
      if (!(nurse && dateStr)) {
        return;
      }
      // anchor 좌표: 셀 우하단 (openPrefInputPopover 가 ev.currentTarget 의 getBoundingClientRect 사용)
      const td = document.querySelector(
        `td[data-pck="${nurse.employeeId}:${dateStr}"]`,
      ) as HTMLElement | null;
      const rect = td?.getBoundingClientRect();
      this.openPrefInputPopover(nurse.employeeId, dateStr, {
        clientX: rect ? rect.right : 0,
        clientY: rect ? rect.bottom : 0,
        currentTarget: td,
        target: td,
      } as unknown as MouseEvent);
    },

    /** 선택 표시 CSS 동적 생성 — 근무표 updateSelectionStyles 와 동일 패턴.
     *  bounding edge 만 굵은 보더로 그려서 셀 그룹이 하나의 사각형으로 보이게. */
    updatePrefSelectionStyles() {
      let el = document.getElementById("pref-selection-styles");
      if (!el) {
        el = document.createElement("style");
        el.id = "pref-selection-styles";
        document.head.appendChild(el);
      }
      if (this.prefSelectedCells.size === 0) {
        el.textContent = "";
        return;
      }
      // 드래그 중에도 outline — Excel 패턴 (updateSelectionStyles 와 동일).
      const rules: string[] = [];
      for (const key of this.prefSelectedCells) {
        const [empId, dateStr] = key.split(":");
        const rIdx = this.nurses.findIndex((n) => n.employeeId === empId);
        const cIdx = this.cycleDates.indexOf(dateStr);
        if (rIdx < 0 || cIdx < 0) {
          continue;
        }
        const hasTop = !this.prefSelectedCells.has(this.prefCellKeyAt(rIdx - 1, cIdx) ?? "");
        const hasBottom = !this.prefSelectedCells.has(this.prefCellKeyAt(rIdx + 1, cIdx) ?? "");
        const hasLeft = !this.prefSelectedCells.has(this.prefCellKeyAt(rIdx, cIdx - 1) ?? "");
        const hasRight = !this.prefSelectedCells.has(this.prefCellKeyAt(rIdx, cIdx + 1) ?? "");
        // 근무표 탭과 동일 — 이미 pref 가 있는 셀은 자기 틴트를 유지하고, 빈 셀에만 selection bg.
        // (근무표는 .sched-cell 오버레이가 shift 색을 덮어서 자연스럽게 처리되지만,
        //  pref 는 td 자체가 pref-tint-* 배경이므로 명시적으로 분기.)
        const hasPref = this.getPrefForCell(empId, dateStr).type !== null;
        // 선택 셀의 자체 회색 보더(border-r/b)는 무조건 투명 — outline 이 분리선 역할.
        // 첫행/마지막행/wrapper overflow:hidden 에 outline 이 짤리지 않도록 inset:0 사용.
        const tdStyle: string[] = [
          "border-right-color:transparent!important",
          "border-bottom-color:transparent!important",
        ];
        if (!hasPref) {
          tdStyle.push("background:var(--ds-selection-tint-12)!important");
        }
        rules.push(`td[data-pck="${key}"]{${tdStyle.join(";")}}`);
        const borders = [
          `border-top:${hasTop ? "2px solid var(--color-neutral-900)" : "0"}`,
          `border-bottom:${hasBottom ? "2px solid var(--color-neutral-900)" : "0"}`,
          `border-left:${hasLeft ? "2px solid var(--color-neutral-900)" : "0"}`,
          `border-right:${hasRight ? "2px solid var(--color-neutral-900)" : "0"}`,
        ].join(";");
        rules.push(
          `td[data-pck="${key}"]::before{content:"";position:absolute;inset:0 -1px -1px 0;${borders};pointer-events:none;z-index:20}`,
        );
      }
      el.textContent = rules.join("\n");
    },

    clearPrefSelection() {
      this.prefSelectedCells = new Set();
      this.prefCursorCell = null;
      this.prefLastClickedCell = null;
      this.prefIsDragging = false;
      this.prefDragStartCell = null;
      this.prefSelectionVersion++;
      this.updatePrefSelectionStyles();
    },

    /** 그리드 밖(셀 아닌 영역) 또는 popover 밖 클릭 시 선택 해제.
     *  pref-input 탭 전체에 @click.window 로 부착됨. */
    handlePrefClickOutside(event: MouseEvent) {
      if (this.activeTab !== "pref-input") {
        return;
      }
      if (this.prefSelectedCells.size === 0 && !this.prefInputPopover.visible) {
        return;
      }
      const target = event.target as HTMLElement | null;
      if (!target) {
        return;
      }
      // 셀 클릭 → mousedown 이 처리, 무시
      if (target.closest("td[data-pck]")) {
        return;
      }
      // popover 클릭 → 무시
      if (target.closest("[data-pref-popover]")) {
        return;
      }
      this.clearPrefSelection();
      if (this.prefInputPopover.visible) {
        this.closePrefInputPopover();
      }
    },

    /** 셀 클릭 → popover 열기. mouseEvent 의 화면 좌표로 anchor. */
    openPrefInputPopover(employeeId: string, dateStr: string, ev: MouseEvent) {
      const nurse = this.nurses.find((n) => n.employeeId === employeeId);
      const cell = this.getPrefForCell(employeeId, dateStr);
      const raw = cell.type;
      const dataType: "" | "preferred" | "restricted" | "unavailable" | "vacation" = raw ?? "";
      // ⚠️ 빈 셀은 아무것도 선택하지 않은 상태로 연다(예전엔 '희망/preferred' 를 미리
      //    넣어 뒀다). 한 화면 픽커에서는 "무언가 선택됨"이 곧 "나머지는 잠김"이라,
      //    시프트 없는 preferred 를 미리 넣으면 어느 버튼과도 매칭되지 않는 반쪽 상태가 되어
      //    모든 버튼이 disabled(pointer-events:none)로 잠긴다 — 클릭이 아예 먹지 않는다.
      const category: "" | "wish" | "constraint" | "vacation" =
        dataType === "preferred"
          ? "wish"
          : dataType === "vacation"
            ? "vacation"
            : dataType
              ? "constraint"
              : "";
      const type: "" | "preferred" | "restricted" | "unavailable" | "vacation" = dataType;
      // anchor 는 셀의 우하단 코너 — 클릭 좌표가 아니라 셀 rect 기준이라야
      // 같은 셀 내 어딜 클릭해도 popover 가 일정한 위치(우하단)에서 펼쳐짐.
      const td = (ev.currentTarget as HTMLElement) || (ev.target as HTMLElement).closest("td");
      const rect = td?.getBoundingClientRect();
      this.prefInputPopover = {
        visible: true,
        employeeId,
        employeeName: nurse?.name || "",
        date: dateStr,
        category,
        type,
        shifts:
          dataType === "preferred"
            ? [...cell.preferredShifts]
            : dataType === "restricted"
              ? [...cell.restrictedShifts]
              : [],
        reason: cell.reason || "",
        anchorX: rect ? rect.right : ev.clientX,
        anchorY: rect ? rect.bottom : ev.clientY,
        // 첫 페인트용 임시값 — 다음 프레임에 실측해 뷰포트 안으로 보정한다.
        left: (rect ? rect.right : ev.clientX) + 4,
        top: (rect ? rect.bottom : ev.clientY) + 4,
      };
      requestAnimationFrame(() => this._clampPrefPopover(0));
    },

    /**
     * 희망근무 팝오버를 뷰포트 안으로 잠근다 — 셀이 화면 오른쪽·아래 끝에 있어도 잘리지 않게.
     *
     * 예전에는 style 안에서 높이를 상수(360px)로 가정했는데, 카테고리마다 높이가 다르고
     * (희망·제약엔 시프트 줄이 있고 휴가엔 없다) 하한도 없어 좁은 창에서는 좌표가 음수가 됐다.
     * 근무표 우클릭 메뉴와 같은 방식으로 렌더된 실제 크기를 재서 보정한다.
     */
    _clampPrefPopover(attempt: number) {
      if (!this.prefInputPopover.visible) {
        return;
      }
      const el = document.querySelector<HTMLElement>("[data-pref-popover]");
      if (!el) {
        return;
      }
      const rect = el.getBoundingClientRect();
      // 아직 마운트 전이면 0 — 잘못된 값으로 잠그지 않고 몇 프레임만 다시 시도한다.
      if (rect.height === 0) {
        if (attempt < 5) {
          requestAnimationFrame(() => this._clampPrefPopover(attempt + 1));
        }
        return;
      }
      const gap = 8;
      const maxLeft = Math.max(gap, window.innerWidth - rect.width - gap);
      const maxTop = Math.max(gap, window.innerHeight - rect.height - gap);
      this.prefInputPopover.left = Math.min(
        Math.max(gap, this.prefInputPopover.anchorX + 4),
        maxLeft,
      );
      this.prefInputPopover.top = Math.min(
        Math.max(gap, this.prefInputPopover.anchorY + 4),
        maxTop,
      );
    },

    closePrefInputPopover() {
      this.prefInputPopover.visible = false;
      // 선택 유지 — 사용자가 다시 popover 열거나 입력 반복 가능. 외부 클릭/ESC 가 별도로 선택 해제.
    },

    /**
     * 한 화면 픽커의 현재 선택 키. 없으면 "".
     *
     * 희망/불가/휴가를 탭으로 나누지 않고 한 번에 보여주므로(2026-07), 화면에서는
     * "여러 그룹의 버튼 중 정확히 하나"가 선택된 상태다. 저장 구조(category/type/shifts)는
     * 그대로 두고 여기서 키 하나로 환산한다 — 저장·삭제·집계 로직을 건드리지 않기 위해서다.
     *   wish:day|evening|night · block:all · block:day|evening|night · vacation
     */
    get prefInputPick(): string {
      const p = this.prefInputPopover;
      // 시프트가 비어 있으면 아직 "선택 없음" — 여기서 wish:/block: 같은 반쪽 키를
      // 돌려주면 어느 버튼과도 안 맞아 전부 잠긴다(위 openPrefInputPopover 주석 참고).
      if (p.type === "preferred") {
        return p.shifts[0] ? `wish:${p.shifts[0]}` : "";
      }
      if (p.type === "restricted") {
        return p.shifts[0] ? `block:${p.shifts[0]}` : "";
      }
      if (p.type === "unavailable") {
        return "block:all";
      }
      if (p.type === "vacation") {
        return "vacation";
      }
      return "";
    },

    /**
     * 이 셀에 "저장된" 입력이 있는지 — 삭제 버튼 노출 조건.
     *
     * ⚠️ 폼의 현재 선택(prefInputPick)을 조건으로 쓰면 안 된다. 픽커는 같은 버튼을 다시
     * 눌러 선택을 해제할 수 있는데, 그때 폼은 비어도 셀에는 값이 그대로 남아 있다.
     * 폼 상태로 판단하면 그 순간 삭제 버튼이 사라져 저장된 값을 지울 방법이 없어진다.
     */
    get prefInputHasExisting(): boolean {
      const p = this.prefInputPopover;
      if (!(p.employeeId && p.date)) {
        return false;
      }
      return !!this.getPrefForCell(p.employeeId, p.date).type;
    },

    /**
     * 픽커 클릭 — 같은 버튼을 다시 누르면 해제, 다른 버튼을 누르면 그것으로 교체.
     * 한 셀에는 하나만 들어가지만 나머지 버튼을 잠그지는 않는다(바로 갈아탈 수 있어야 한다).
     */
    setPrefInputPick(key: string) {
      const p = this.prefInputPopover;
      if (this.prefInputPick === key) {
        p.category = "";
        p.type = "";
        p.shifts = [];
        return;
      }
      if (key === "vacation") {
        p.category = "vacation";
        p.type = "vacation";
        p.shifts = [];
      } else if (key === "block:all") {
        p.category = "constraint";
        p.type = "unavailable";
        p.shifts = [];
      } else if (key.startsWith("block:")) {
        p.category = "constraint";
        p.type = "restricted";
        p.shifts = [key.slice("block:".length)];
      } else if (key.startsWith("wish:")) {
        p.category = "wish";
        p.type = "preferred";
        p.shifts = [key.slice("wish:".length)];
      }
    },

    /** popover 저장 → 선택된 모든 셀에 동일 입력을 일괄 적용.
     *  단일 셀(popover 만 열린 경우) 도 같은 경로 — selection 이 자기 자신만 포함.
     *  employee 별로 묶어서 한 번씩 upsert (server 는 employee+year+month 단위 키). */
    async savePrefInputPopover() {
      if (!this.sdk) {
        return;
      }
      const { type, shifts, reason } = this.prefInputPopover;
      if (!type) {
        return;
      }
      // 선택 셀 vs popover 의 단일 셀 — 둘 다 합집합으로 처리 (popover 의 anchor 셀이 selection 밖이면 추가).
      const targetKeys = new Set<string>(this.prefSelectedCells);
      const anchorKey = `${this.prefInputPopover.employeeId}:${this.prefInputPopover.date}`;
      if (this.prefInputPopover.employeeId && this.prefInputPopover.date) {
        targetKeys.add(anchorKey);
      }
      if (targetKeys.size === 0) {
        return;
      }

      // employee 별로 (date 목록) 묶기
      const byEmployee = new Map<string, string[]>();
      for (const key of targetKeys) {
        const [empId, dateStr] = key.split(":");
        if (!byEmployee.has(empId)) {
          byEmployee.set(empId, []);
        }
        byEmployee.get(empId)?.push(dateStr);
      }

      this.pushPrefUndo();
      this.closePrefInputPopover();
      try {
        for (const [employeeId, dates] of byEmployee) {
          const existing = this.prefByEmployee[employeeId];
          const vacationDates = [...(existing?.vacationDates ?? [])].filter(
            (d) => !dates.includes(typeof d === "string" ? d : d.date),
          );
          const restrictedDates = [...(existing?.restrictedDates ?? [])].filter(
            (r) => !dates.includes(r.date),
          );
          const unavailableDates = [...(existing?.unavailableDates ?? [])].filter(
            (u) => !dates.includes(u.date),
          );
          const preferredDates = [...(existing?.preferredDates ?? [])].filter(
            (p) => !dates.includes(p.date),
          );
          for (const date of dates) {
            if (type === "restricted") {
              restrictedDates.push({ date, restrictedShifts: [...shifts], reason });
            } else if (type === "unavailable") {
              unavailableDates.push({ date, reason });
            } else if (type === "vacation") {
              // 휴가는 유형 없이 날짜만 저장한다(희망근무는 D/E/N + 휴가 여부까지만 받는다).
              // 읽기는 {date, leaveTypeId} 형태도 계속 받아 준다 — 과거 데이터 호환.
              vacationDates.push(date);
            } else if (type === "preferred") {
              preferredDates.push({
                date,
                preferredShifts: [...shifts],
                reason: reason || undefined,
              });
            }
          }
          await this.sdk.nurseScheduler.upsertPreferenceByManager({
            employeeId,
            year: this.period.year,
            month: this.period.month,
            wardId: this.selectedWardId,
            vacationDates,
            // biome-ignore lint/suspicious/noExplicitAny: enum stored as string
            restrictedDates: restrictedDates as any,
            unavailableDates,
            // biome-ignore lint/suspicious/noExplicitAny: enum stored as string
            preferredDates: preferredDates as any,
            notes: existing?.notes ?? undefined,
          });
        }
      } catch (e) {
        // biome-ignore lint/suspicious/noConsole: 디버깅용
        console.error("Failed to upsert preference:", e);
      }
      // 성공/실패 무관 — 서버 진실로 재동기화 (부분 실패시에도 성공분은 반영).
      await this.loadPreferencesForCurrentMonth();
    },

    /** popover 삭제 → 선택된 모든 셀의 pref 제거 (save 와 같은 다중 적용 패턴). */
    async deletePrefInputForDate() {
      if (!this.sdk) {
        return;
      }
      const targetKeys = new Set<string>(this.prefSelectedCells);
      const anchorKey = `${this.prefInputPopover.employeeId}:${this.prefInputPopover.date}`;
      if (this.prefInputPopover.employeeId && this.prefInputPopover.date) {
        targetKeys.add(anchorKey);
      }
      if (targetKeys.size === 0) {
        this.closePrefInputPopover();
        return;
      }
      const byEmployee = new Map<string, string[]>();
      for (const key of targetKeys) {
        const [empId, dateStr] = key.split(":");
        if (!byEmployee.has(empId)) {
          byEmployee.set(empId, []);
        }
        byEmployee.get(empId)?.push(dateStr);
      }
      this.pushPrefUndo();
      this.closePrefInputPopover();
      try {
        for (const [employeeId, dates] of byEmployee) {
          const existing = this.prefByEmployee[employeeId];
          if (!existing) {
            continue;
          }
          const vacationDates = (existing.vacationDates ?? []).filter(
            (d) => !dates.includes(typeof d === "string" ? d : d.date),
          );
          const restrictedDates = (existing.restrictedDates ?? []).filter(
            (r) => !dates.includes(r.date),
          );
          const unavailableDates = (existing.unavailableDates ?? []).filter(
            (u) => !dates.includes(u.date),
          );
          const preferredDates = (existing.preferredDates ?? []).filter(
            (p) => !dates.includes(p.date),
          );
          await this.sdk.nurseScheduler.upsertPreferenceByManager({
            employeeId,
            year: this.period.year,
            month: this.period.month,
            wardId: this.selectedWardId,
            vacationDates,
            // biome-ignore lint/suspicious/noExplicitAny: enum stored as string
            restrictedDates: restrictedDates as any,
            unavailableDates,
            // biome-ignore lint/suspicious/noExplicitAny: enum stored as string
            preferredDates: preferredDates as any,
            notes: existing.notes ?? undefined,
          });
        }
      } catch (e) {
        // biome-ignore lint/suspicious/noConsole: 디버깅용
        console.error("Failed to delete preference date:", e);
      }
      await this.loadPreferencesForCurrentMonth();
    },

    // === 희망근무(pref-input) undo/redo — 근무표 undo/redo 와 동일 키 (ctrl+z, ctrl+shift+z) ===
    clonePrefSnapshot() {
      return JSON.parse(JSON.stringify(this.prefByEmployee)) as Record<string, unknown>;
    },

    pushPrefUndo() {
      this.prefUndoStack.push(this.clonePrefSnapshot());
      if (this.prefUndoStack.length > 50) {
        this.prefUndoStack.shift();
      }
      this.prefRedoStack.length = 0;
    },

    /** 스냅샷(target) 으로 현재 prefByEmployee 를 맞춤 — 차이나는 employee 만 서버에 upsert.
     *  pref 도메인은 employee 단위 upsert 라서 셀 diff 가 아니라 employee 단위로 비교. */
    async syncPrefSnapshot(target: Record<string, unknown>) {
      if (!(this.sdk && this.selectedWardId)) {
        return;
      }
      // biome-ignore lint/suspicious/noExplicitAny: snapshot shape mirrors prefByEmployee
      const targetMap = target as Record<string, any>;
      // biome-ignore lint/suspicious/noExplicitAny: current map shape
      const curMap = this.prefByEmployee as Record<string, any>;
      const allEmpIds = new Set<string>([...Object.keys(curMap), ...Object.keys(targetMap)]);
      const changed: string[] = [];
      for (const empId of allEmpIds) {
        if (JSON.stringify(curMap[empId] ?? null) !== JSON.stringify(targetMap[empId] ?? null)) {
          changed.push(empId);
        }
      }
      for (const empId of changed) {
        const t = targetMap[empId];
        await this.sdk.nurseScheduler.upsertPreferenceByManager({
          employeeId: empId,
          year: this.period.year,
          month: this.period.month,
          wardId: this.selectedWardId,
          vacationDates: t?.vacationDates ?? [],
          // biome-ignore lint/suspicious/noExplicitAny: enum stored as string
          restrictedDates: (t?.restrictedDates ?? []) as any,
          unavailableDates: t?.unavailableDates ?? [],
          // biome-ignore lint/suspicious/noExplicitAny: enum stored as string
          preferredDates: (t?.preferredDates ?? []) as any,
          notes: t?.notes ?? undefined,
        });
      }
    },

    async prefUndo() {
      const prev = this.prefUndoStack.pop();
      if (!prev) {
        return;
      }
      this.prefRedoStack.push(this.clonePrefSnapshot());
      try {
        await this.syncPrefSnapshot(prev);
        await this.loadPreferencesForCurrentMonth();
        this.showToast("되돌리기 완료");
      } catch (e) {
        // biome-ignore lint/suspicious/noConsole: 디버깅용
        console.error("Failed to undo preference:", e);
        await this.loadPreferencesForCurrentMonth();
      }
    },

    async prefRedo() {
      const next = this.prefRedoStack.pop();
      if (!next) {
        return;
      }
      this.prefUndoStack.push(this.clonePrefSnapshot());
      try {
        await this.syncPrefSnapshot(next);
        await this.loadPreferencesForCurrentMonth();
        this.showToast("다시 실행 완료");
      } catch (e) {
        // biome-ignore lint/suspicious/noConsole: 디버깅용
        console.error("Failed to redo preference:", e);
        await this.loadPreferencesForCurrentMonth();
      }
    },

    /** 희망근무 탭 키보드: ctrl/cmd+z = undo, ctrl/cmd+shift+z = redo, ctrl/cmd+y = redo,
     *  Delete/Backspace = 선택 셀 비우기 (popover 열려있어도 동작 — 셀 선택 자체는 popover 와 독립).
     *  pref-input 래퍼에 @keydown.window 로 부착. INPUT/TEXTAREA 포커스시 무시 (사유 필드 입력 보호). */
    handlePrefKeyDown(event: KeyboardEvent) {
      if (this.activeTab !== "pref-input") {
        return;
      }
      const tag = (event.target as HTMLElement).tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") {
        return;
      }
      // Delete/Backspace 는 popover 표시 여부와 무관하게 동작 — popover 열려있으면 먼저 닫고 삭제.
      if (event.key === "Delete" || event.key === "Backspace") {
        if (this.prefSelectedCells.size === 0) {
          return;
        }
        event.preventDefault();
        if (this.prefInputPopover.visible) {
          this.closePrefInputPopover();
        }
        this.clearPrefSelected();
        return;
      }
      // 나머지 단축키 (undo/redo) 는 popover 열려있을 땐 무시 — popover 자체 인터랙션이 우선.
      if (this.prefInputPopover.visible) {
        return;
      }
      const key = event.key.toLowerCase();
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && key === "z") {
        event.preventDefault();
        this.prefRedo();
        return;
      }
      if ((event.ctrlKey || event.metaKey) && key === "z") {
        event.preventDefault();
        this.prefUndo();
        return;
      }
      if ((event.ctrlKey || event.metaKey) && key === "y") {
        event.preventDefault();
        this.prefRedo();
        return;
      }
    },

    /** 선택된 모든 셀(prefSelectedCells)의 pref 를 제거.
     *  키보드 Delete + popover 의 '삭제' 버튼이 공통으로 사용 — 한 employee 의 기존 pref 에서
     *  선택된 dates 만 빼고 다시 upsert. existing 없는 employee 는 스킵 (삭제할 게 없음). */
    async clearPrefSelected() {
      if (!this.sdk) {
        return;
      }
      const targetKeys = new Set<string>(this.prefSelectedCells);
      if (targetKeys.size === 0) {
        return;
      }
      const byEmployee = new Map<string, string[]>();
      for (const key of targetKeys) {
        const [empId, dateStr] = key.split(":");
        if (!byEmployee.has(empId)) {
          byEmployee.set(empId, []);
        }
        byEmployee.get(empId)?.push(dateStr);
      }
      this.pushPrefUndo();
      try {
        for (const [employeeId, dates] of byEmployee) {
          const existing = this.prefByEmployee[employeeId];
          if (!existing) {
            continue;
          }
          const vacationDates = (existing.vacationDates ?? []).filter(
            (d) => !dates.includes(typeof d === "string" ? d : d.date),
          );
          const restrictedDates = (existing.restrictedDates ?? []).filter(
            (r) => !dates.includes(r.date),
          );
          const unavailableDates = (existing.unavailableDates ?? []).filter(
            (u) => !dates.includes(u.date),
          );
          const preferredDates = (existing.preferredDates ?? []).filter(
            (p) => !dates.includes(p.date),
          );
          await this.sdk.nurseScheduler.upsertPreferenceByManager({
            employeeId,
            year: this.period.year,
            month: this.period.month,
            wardId: this.selectedWardId,
            vacationDates,
            // biome-ignore lint/suspicious/noExplicitAny: enum stored as string
            restrictedDates: restrictedDates as any,
            unavailableDates,
            // biome-ignore lint/suspicious/noExplicitAny: enum stored as string
            preferredDates: preferredDates as any,
            notes: existing.notes ?? undefined,
          });
        }
      } catch (e) {
        // biome-ignore lint/suspicious/noConsole: 디버깅용
        console.error("Failed to clear preferences:", e);
      }
      await this.loadPreferencesForCurrentMonth();
    },

    async loadMyPreference() {
      if (!this.sdk) {
        return;
      }
      this.loading = true;
      try {
        const pref = await this.sdk.nurseScheduler.getMyPreference(this.prefYear, this.prefMonth);
        if (pref) {
          this.prefId = pref.id;
          this.prefStatus = pref.status;
          // vacationDates 는 레거시(string)|신규({date, leaveTypeId}) 혼용 — 표시는 날짜만.
          this.prefVacationDates = (pref.vacationDates || []).map((vd) =>
            typeof vd === "string" ? vd : vd.date,
          );
          this.prefUnavailableDates = pref.unavailableDates || [];
          this.prefRestrictedDates = pref.restrictedDates || [];
          this.prefNotes = pref.notes || "";
        } else {
          this.prefId = null;
          this.prefStatus = "";
          this.prefVacationDates = [];
          this.prefUnavailableDates = [];
          this.prefRestrictedDates = [];
          this.prefNotes = "";
        }
      } catch {
        this.prefId = null;
      }
      this.loading = false;
    },

    async mySchedChangeMonth(delta: number) {
      let m = this.mySchedMonth + delta;
      let y = this.mySchedYear;
      while (m > 12) {
        m -= 12;
        y++;
      }
      while (m < 1) {
        m += 12;
        y--;
      }
      this.mySchedMonth = m;
      this.mySchedYear = y;
      this.prefYear = y;
      this.prefMonth = m;
      await Promise.allSettled([this.loadMySchedule(), this.loadMyPreference()]);
    },

    async mySchedToday() {
      const now = new Date();
      this.mySchedYear = now.getFullYear();
      this.mySchedMonth = now.getMonth() + 1;
      this.prefYear = this.mySchedYear;
      this.prefMonth = this.mySchedMonth;
      await Promise.allSettled([this.loadMySchedule(), this.loadMyPreference()]);
    },

    get mySchedMode(): "input" | "view" {
      const now = new Date();
      const curY = now.getFullYear();
      const curM = now.getMonth() + 1;
      if (this.mySchedYear > curY || (this.mySchedYear === curY && this.mySchedMonth > curM)) {
        return "input";
      }
      return "view";
    },

    async loadMySchedule() {
      if (!this.sdk) {
        return;
      }
      try {
        const userId = Alpine.store("hereby").currentUser?.id as string | undefined;
        if (!userId) {
          return;
        }

        const year = this.mySchedYear;
        const month = this.mySchedMonth;
        const dim = new Date(year, month, 0).getDate();
        this.myScheduleLoaded = false;

        const myEntries = new Map<string, string>();
        if (this.selectedWardId) {
          const schedResult = await this.sdk.nurseScheduler.listSchedules(
            { wardId: this.selectedWardId, year, month },
            { page: 1, limit: 1 },
          );
          const sched = (schedResult.items || [])[0];
          if (sched) {
            const full = await this.sdk.nurseScheduler.getSchedule(sched.id);
            for (const e of full.entries || []) {
              if (e.employeeId === userId) {
                const dateStr = toDateStr(e.date);
                myEntries.set(dateStr, e.assignmentType);
              }
            }
          }
        }

        const fromDate = new Date(year, month - 1, 1);
        const toDate = new Date(year, month, 0, 23, 59, 59);
        const attResult = await this.sdk.attendance.list({
          filter: { employeeId: userId, dateRange: { from: fromDate, to: toDate } },
          pagination: { page: 1, limit: 100, sortBy: "timestamp", sortOrder: SortOrder.ASC },
        });
        // biome-ignore lint/suspicious/noExplicitAny: SDK returns generic
        const attItems = ((attResult as any)?.items || []) as any[];

        const attByDate = new Map<
          string,
          { checkIn: string | null; checkOut: string | null; hours: number | null }
        >();
        for (const att of attItems) {
          const raw = att.date || att.timestamp || att.createdAt || "";
          const d = raw ? toDateStr(raw) : "";
          if (!d) {
            continue;
          }
          const existing = attByDate.get(d) || { checkIn: null, checkOut: null, hours: null };
          if (att.checkIn || att.type === "check_in") {
            existing.checkIn = att.checkIn || att.timestamp || att.createdAt;
          }
          if (att.checkOut || att.type === "check_out") {
            existing.checkOut = att.checkOut || att.timestamp || att.createdAt;
          }
          if (att.totalHours != null) {
            existing.hours = att.totalHours;
          } else if (existing.checkIn && existing.checkOut) {
            existing.hours =
              (new Date(existing.checkOut).getTime() - new Date(existing.checkIn).getTime()) /
              3600000;
          }
          attByDate.set(d, existing);
        }

        const rows: typeof this.myScheduleEntries = [];
        for (let day = 1; day <= dim; day++) {
          const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          const shift = myEntries.get(dateStr) || "";
          const att = attByDate.get(dateStr);
          rows.push({
            date: dateStr,
            shift,
            checkIn: att?.checkIn || null,
            checkOut: att?.checkOut || null,
            actualHours: att?.hours ?? null,
          });
        }
        this.myScheduleEntries = rows;
        this.myScheduleLoaded = true;
      } catch {
        this.myScheduleEntries = [];
        this.myScheduleLoaded = true;
      }
    },

    async loadProfiles() {
      if (!this.sdk) {
        return;
      }
      this.loading = true;
      try {
        const filter = this.selectedWardId ? this.profileScope() : {};
        const result = await this.sdk.nurseScheduler.listProfiles(filter, {
          page: 1,
          limit: 500,
        });
        this.profileRows = (result.items || []).map((p: NurseProfileItem) => this.toNurseRow(p));
      } catch {
        this.profileRows = [];
      }
      this.loading = false;
    },

    /** day-of-month 번호 → cycleDates 의 "YYYY-MM-DD". O(1) (사전 계산된 맵).
     *  맵이 비어있으면(초기/갱신 전) indexOf 로 폴백. */
    dayToDateStr(day: number): string {
      const cached = this.dayDateStrMap.get(day);
      if (cached !== undefined) {
        return cached;
      }
      const idx = this.daysInMonth.indexOf(day);
      return idx >= 0 ? this.cycleDates[idx] : "";
    },

    /** cycleDates/daysInMonth 변경 후 호출 — dayToDateStr 룩업 맵을 다시 만든다. */
    rebuildDayDateStrMap() {
      const m = new Map<number, string>();
      for (let i = 0; i < this.daysInMonth.length; i++) {
        m.set(this.daysInMonth[i], this.cycleDates[i]);
      }
      this.dayDateStrMap = m;
    },

    getCell(employeeId: string, day: number): string {
      const dateStr = this.dayToDateStr(day);
      return this.entries.get(`${employeeId}:${dateStr}`) || "";
    },

    getCellLabel(employeeId: string, day: number): string {
      const type = this.getCell(employeeId, day);
      return getAssignmentLabels(this)[type] || "";
    },

    /** 셀 값이 커스텀 휴가 유형 id인지 */
    isCustomLeaveType(type: string): boolean {
      return !!type && schedulerLeaveReasons().some((r) => r.id === type);
    },

    /** 셀 값이 스케줄러 연동 근무 유형(shift id)인지 */
    isSchedulerShift(type: string): boolean {
      return !!type && this.availableShifts.some((s) => s.id === type);
    },

    /** 컨텍스트 메뉴 배정 목록에 노출할 커스텀 휴가 유형(스케줄러 연동) */
    schedulerCustomTypes(): { id: string; name: string; code?: string; color?: string }[] {
      return schedulerLeaveReasons();
    },

    /** 퀵배정/희망근무에 노출할 스케줄러 연동 근무 유형(코드·색·이름) */
    schedulerShiftTypes(): { id: string; code?: string; codeColor?: string; name: string }[] {
      return this.availableShifts.map((s) => ({
        id: s.id,
        code: s.code,
        codeColor: s.codeColor,
        name: s.name,
      }));
    },

    /** 근무 변경 팝오버(entry-edit)의 배정 옵션 — 연동 근무유형 + OFF + 연동 휴가유형 + 근무불가.
     *  color 있으면 인라인 배지색, 없으면 baseClass(shift-OFF/shift-UA)로 렌더. */
    entryEditOptions(): {
      value: string;
      label: string;
      sub: string;
      color?: string;
      baseClass: string;
    }[] {
      const opts: {
        value: string;
        label: string;
        sub: string;
        color?: string;
        baseClass: string;
      }[] = [];
      for (const s of this.schedulerShiftTypes()) {
        opts.push({
          value: s.id,
          label: s.code || s.name,
          sub: s.name,
          color: s.codeColor,
          baseClass: "",
        });
      }
      opts.push({ value: "off", label: "OFF", sub: "휴무", baseClass: "shift-OFF" });
      for (const r of this.schedulerCustomTypes()) {
        opts.push({
          value: r.id,
          label: r.code || r.name,
          sub: r.name,
          color: r.color,
          baseClass: "",
        });
      }
      opts.push({ value: "unavailable", label: "U", sub: "불가", baseClass: "shift-UA" });
      return opts;
    },

    /**
     * 셀 우클릭 메뉴 「배정」 헤더에 띄우는 개수.
     *
     * entryEditOptions() 와 구성이 정확히 같다(연동 근무유형 + OFF + 연동 휴가유형 + 불가)
     * — 그래서 직접 세지 않고 그대로 쓴다. 숫자를 따로 계산하면 목록에 항목이 하나
     * 늘거나 줄 때 헤더 숫자만 어긋난 채로 남는다.
     */
    assignOptionCount(): number {
      return this.entryEditOptions().length;
    },

    /** 커스텀 유형 코드 배지 인라인 스타일 */
    customTypeBadgeStyle(color?: string): string {
      const bg = color || "#B8B8B8";
      return `background-color:${bg};color:${leaveTextColor(bg)}`;
    },

    getCellClass(employeeId: string, day: number): string {
      const type = this.getCell(employeeId, day);
      if (ASSIGNMENT_CLASSES[type]) {
        return ASSIGNMENT_CLASSES[type];
      }
      // 연동 근무 유형 / 커스텀 휴가 유형 — 베이스(shift-OFF) + 색상은 getCellStyle 인라인
      if (this.isSchedulerShift(type)) {
        return "shift-OFF sched-cell--custom-shift";
      }
      return this.isCustomLeaveType(type) ? "shift-OFF sched-cell--custom-leave" : "";
    },

    /** 연동 근무 유형/커스텀 휴가 유형 셀의 인라인 배경·글자색 (코드 라벨 색상). 그 외엔 "" */
    getCellStyle(employeeId: string, day: number): string {
      const type = this.getCell(employeeId, day);
      if (this.isSchedulerShift(type)) {
        const s = this.availableShifts.find((x) => x.id === type);
        const bg = s?.codeColor || "#B8B8B8";
        return `background-color:${bg};color:${leaveTextColor(bg)}`;
      }
      if (!this.isCustomLeaveType(type)) {
        return "";
      }
      const r = schedulerLeaveReasons().find((x) => x.id === type);
      const bg = r?.color || "#B8B8B8";
      return `background-color:${bg};color:${leaveTextColor(bg)}`;
    },

    cycleDate(idx: number): string {
      return this.cycleDates[idx] || "";
    },

    getDayOfWeek(day: number): string {
      const keys = [
        "team_mgr.day_sun",
        "team_mgr.day_mon",
        "team_mgr.day_tue",
        "team_mgr.day_wed",
        "team_mgr.day_thu",
        "team_mgr.day_fri",
        "team_mgr.day_sat",
      ];
      const idx = this.daysInMonth.indexOf(day);
      const ds = idx >= 0 ? this.cycleDates[idx] : null;
      if (ds) {
        return _t(this, keys[new Date(`${ds}T00:00:00`).getDay()]);
      }
      const date = new Date(this.period.year, this.period.month - 1, day);
      return _t(this, keys[date.getDay()]);
    },

    getWorkTypeLabel(type: string): string {
      const key = WORK_TYPE_KEYS[type];
      return key ? _t(this, key) : type;
    },

    async updateNurseWorkType(profileId: string, workType: string, rowIdx: number) {
      if (!this.sdk) {
        return;
      }
      try {
        await this.sdk.nurseScheduler.updateProfile({ id: profileId, workType });
        if (this.nurses[rowIdx]) {
          this.nurses[rowIdx].workType = workType;
        }
      } catch {
        await this.loadSchedule();
      }
    },

    onWorkTypeMouseDown(event: MouseEvent, rowIdx: number) {
      if (!this.isNurseManager || event.button !== 0) {
        return;
      }
      event.preventDefault();
      this.wtCtxMenu.show = false;
      this.clearSelection();

      if (event.shiftKey && this.lastClickedWtRow !== null) {
        this.wtSelectRange(this.lastClickedWtRow, rowIdx);
      } else if (event.metaKey || event.ctrlKey) {
        const next = new Set(this.selectedWorkTypeRows);
        if (next.has(rowIdx)) {
          next.delete(rowIdx);
        } else {
          next.add(rowIdx);
        }
        this.selectedWorkTypeRows = next;
      } else {
        this.selectedWorkTypeRows = new Set([rowIdx]);
        this.lastClickedWtRow = rowIdx;
        this.wtDragStart = rowIdx;
        this.wtDragging = true;
      }
      this.wtCursorRow = rowIdx;
      if (!event.shiftKey) {
        this.lastClickedWtRow = rowIdx;
      }
      this.updateWorkTypeSelectionStyles();
    },

    onWorkTypeMouseEnter(rowIdx: number) {
      if (!(this.wtDragging && this.wtDragStart !== null)) {
        return;
      }
      this.wtDragEnd = rowIdx;
      this.wtSelectRange(this.wtDragStart, rowIdx);
      this.updateWorkTypeSelectionStyles();
    },

    onWorkTypeMouseUp() {
      const wasDragging = this.wtDragging;
      const dragEnd = this.wtDragEnd;
      this.wtDragging = false;
      this.wtDragEnd = null;
      // 드래그로 여러 행 선택 완료 → 마지막 행에 popover 즉시 표시.
      // mouseup 이 mousedown 과 다른 td 에서 발생하면 @click 이 안 뜸 — 직접 호출.
      if (wasDragging && dragEnd !== null && this.selectedWorkTypeRows.size > 1) {
        this.openWorkTypePickerAt(dragEnd);
      }
    },

    /** 근무형태 popover 를 특정 행의 cell 우하단에 anchor 해서 연다. */
    openWorkTypePickerAt(rowIdx: number, ev?: MouseEvent) {
      const td =
        (ev?.currentTarget as HTMLElement | null) ||
        ((ev?.target as HTMLElement | null)?.closest("td") ?? null) ||
        (document.querySelector(`td[data-wt-row="${rowIdx}"]`) as HTMLElement | null);
      const rect = td?.getBoundingClientRect();
      this.wtPickerAnchor = {
        x: rect ? rect.right : (ev?.clientX ?? window.innerWidth / 2),
        y: rect ? rect.bottom : (ev?.clientY ?? window.innerHeight / 2),
      };
      this.showWorkTypePicker = true;
    },

    /** 근무형태 popover 외부 클릭 시 닫기. wt 셀 클릭은 onWorkTypeClick 이 처리. */
    handleWorkTypePickerClickOutside(event: MouseEvent) {
      if (!this.showWorkTypePicker) {
        return;
      }
      const target = event.target as HTMLElement | null;
      if (!target) {
        return;
      }
      if (target.closest("td[data-wt-row]")) {
        return;
      }
      if (target.closest("[data-wt-popover]")) {
        return;
      }
      this.showWorkTypePicker = false;
    },

    wtSelectRange(from: number, to: number) {
      const minR = Math.min(from, to);
      const maxR = Math.max(from, to);
      const next = new Set<number>();
      for (let r = minR; r <= maxR; r++) {
        next.add(r);
      }
      this.selectedWorkTypeRows = next;
    },

    onWorkTypeContextMenu(event: MouseEvent, rowIdx: number) {
      if (!this.isNurseManager) {
        return;
      }
      this.cellCtxMenu.show = false;
      if (!this.selectedWorkTypeRows.has(rowIdx)) {
        this.selectedWorkTypeRows = new Set([rowIdx]);
        this.lastClickedWtRow = rowIdx;
        this.wtCursorRow = rowIdx;
        this.updateWorkTypeSelectionStyles();
      }
      this.wtCtxMenu = { show: true, x: event.clientX, y: event.clientY };
      const menuW = 176;
      const menuH = 220;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      this.wtCtxMenu.x = Math.min(event.clientX, vw - menuW - 8);
      this.wtCtxMenu.y =
        event.clientY + menuH > vh ? Math.max(8, event.clientY - menuH) : event.clientY;
    },

    onWorkTypeClick(event: MouseEvent, rowIdx: number) {
      if (!this.isNurseManager) {
        return;
      }
      // Ctrl/Meta 토글 (개별 선택 추가/제거) — popover 안 열고 선택만 유지.
      if (event.metaKey || event.ctrlKey) {
        return;
      }
      // 단일 클릭 또는 Shift 범위 선택 → 즉시 popover (다중 선택 시 N명 선택됨 라벨).
      this.openWorkTypePickerAt(rowIdx, event);
    },

    onWorkTypeKeyDown(event: KeyboardEvent) {
      if (this.selectedWorkTypeRows.size === 0) {
        return;
      }
      const maxRow = this.nurses.length - 1;
      const cursor = this.wtCursorRow ?? this.lastClickedWtRow ?? 0;

      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        this.openWorkTypePickerAt(cursor);
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        this.selectedWorkTypeRows = new Set();
        this.lastClickedWtRow = null;
        this.wtCursorRow = null;
        this.updateWorkTypeSelectionStyles();
        return;
      }

      if (event.key === "ArrowUp" || event.key === "ArrowDown") {
        event.preventDefault();
        const next =
          event.key === "ArrowUp" ? Math.max(0, cursor - 1) : Math.min(maxRow, cursor + 1);
        this.wtCursorRow = next;
        if (event.shiftKey) {
          const anchor = this.lastClickedWtRow ?? 0;
          this.wtSelectRange(anchor, next);
        } else {
          this.selectedWorkTypeRows = new Set([next]);
          this.lastClickedWtRow = next;
        }
        this.updateWorkTypeSelectionStyles();
        requestAnimationFrame(() => {
          const el = document.querySelector(`td[data-wt-row="${next}"]`);
          if (el) {
            el.scrollIntoView({ block: "nearest" });
          }
        });
        return;
      }

      if ((event.ctrlKey || event.metaKey) && event.key === "a") {
        event.preventDefault();
        const all = new Set<number>();
        for (let r = 0; r <= maxRow; r++) {
          all.add(r);
        }
        this.selectedWorkTypeRows = all;
        this.updateWorkTypeSelectionStyles();
        return;
      }
    },

    async applyWorkTypeToSelected(workType: string) {
      this.showWorkTypePicker = false;
      if (!this.sdk) {
        return;
      }
      for (const rowIdx of this.selectedWorkTypeRows) {
        const nurse = this.nurses[rowIdx];
        if (nurse && nurse.workType !== workType) {
          try {
            await this.sdk.nurseScheduler.updateProfile({
              id: nurse.profileId,
              workType,
            });
            nurse.workType = workType;
          } catch {
            // Continue with others
          }
        }
      }
      this.updateWorkTypeSelectionStyles();
    },

    isWorkTypeRowSelected(rowIdx: number): boolean {
      return this.selectedWorkTypeRows.has(rowIdx);
    },

    updateWorkTypeSelectionStyles() {
      let el = document.getElementById("wt-selection-styles");
      if (!el) {
        el = document.createElement("style");
        el.id = "wt-selection-styles";
        document.head.appendChild(el);
      }
      if (this.selectedWorkTypeRows.size === 0) {
        el.textContent = "";
        return;
      }
      const selectors = [...this.selectedWorkTypeRows]
        .map((r) => `td[data-wt-row="${r}"]`)
        .join(",");
      el.textContent = `${selectors}{background:var(--color-hr-blue-50)!important;box-shadow:inset 0 0 0 2px var(--color-hr-blue-500);z-index:6!important}`;
    },

    getStatusLabel(status: string): string {
      const keys: Record<string, string> = {
        draft: "nurse.schedule_status_draft",
        generating: "nurse.schedule_status_generating",
        ai_generated: "nurse.schedule_status_ai_generated",
        editing: "nurse.schedule_status_editing",
        confirmed: "nurse.schedule_status_confirmed",
        published: "nurse.schedule_status_published",
      };
      const key = keys[status];
      return key ? _t(this, key) : status;
    },

    getStatusClass(status: string): string {
      // ds-label 색 매핑 — scheduler.html의 상태 라벨이 ds-label 셸을 사용함.
      // editing은 템플릿에서 'ds-label--yellow'로 직접 처리, 나머지는 여기서 매핑.
      const classes: Record<string, string> = {
        draft: "ds-label--gray",
        generating: "ds-label--blue",
        ai_generated: "ds-label--blue",
        editing: "ds-label--yellow",
        confirmed: "ds-label--green",
        published: "ds-label--green",
      };
      return classes[status] || "ds-label--gray";
    },

    getDayCount(day: number, type: string): number {
      const dateStr = this.dayToDateStr(day);
      let count = 0;
      for (const nurse of this.nurses) {
        const v = this.entries.get(`${nurse.employeeId}:${dateStr}`);
        if (v === type) {
          count++;
        } else if (v && this.isSchedulerShift(v) && this.shiftSlot(v) === type) {
          // 연동 근무 유형(shift_custom) — 시작시간 기준 D/E/N 슬롯으로 인력계산에 집계
          count++;
        }
      }
      return count;
    },

    /** 연동 근무 유형(shift id)을 D/E/N 슬롯으로 분류 — 시작시간 기준(06~14 D, 14~22 E, 그 외 N). */
    shiftSlot(shiftId: string): "day" | "evening" | "night" {
      const s = this.availableShifts.find((x) => x.id === shiftId);
      const hh = Number.parseInt((s?.startTime || "").slice(0, 2), 10);
      if (Number.isNaN(hh)) {
        return "day";
      }
      if (hh >= 6 && hh < 14) {
        return "day";
      }
      if (hh >= 14 && hh < 22) {
        return "evening";
      }
      return "night";
    },

    /** 그날 실제 근무(any working shift) 간호사 수 — off/휴가/근무불가 제외. 환자수 비율용. */
    workingNurseCount(day: number): number {
      return this.workingNurseCountByDate(this.dayToDateStr(day));
    },

    /**
     * 해당 날짜에 실제 근무 배정된 간호사 수 — 근무표 셀 기준(배정 인원이 아니다).
     * 3교대 병동은 배정 인원 전원이 동시에 근무하지 않으므로, 간호사:환자 비율의 분모는
     * 반드시 이 값이어야 한다(배정 인원을 쓰면 1:0.2 같은 성립 불가 수치가 나온다).
     */
    workingNurseCountByDate(dateStr: string): number {
      const working = new Set([
        "day",
        "evening",
        "night",
        "day_education",
        "evening_education",
        "night_education",
      ]);
      let n = 0;
      for (const nurse of this.nurses) {
        const v = this.entries.get(`${nurse.employeeId}:${dateStr}`);
        if (!v) {
          continue;
        }
        if (this.isSchedulerShift(v) || working.has(v)) {
          n++;
        }
      }
      return n;
    },

    /** 해당 월(현재 주기)의 간호사-일 합 : 환자-일 합 — 환자수 관리 팝업 월 단위 집계용.
     *  환자수가 입력된 날만 합산(입력 안 된 날은 제외). */
    monthlyNursePatient(): { nurses: number; patients: number; days: number } {
      let nurses = 0;
      let patients = 0;
      let days = 0;
      for (const day of this.daysInMonth) {
        const pc = this.getPatientCount(day);
        if (pc == null) {
          continue;
        }
        nurses += this.workingNurseCount(day);
        patients += pc;
        days++;
      }
      return { nurses, patients, days };
    },

    /** 인력현황 셀 호버 툴팁 — D/E/N 각 항목을 줄바꿈으로 표기(부족 시 표시). */
    dayStaffingTitle(day: number): string {
      const line = (label: string, type: string) => {
        const c = this.getDayCount(day, type);
        const min = this.getMinRequired(day, type);
        // isUnderstaffed 와 동일 로직(count < (min ?? 2))을 이미 구한 c 로 판정 —
        // getDayCount 재호출 제거(툴팁 셀당 6→3 pass).
        const short = c < (min ?? 2) ? " (부족)" : "";
        return `${label} ${c}${min != null ? `/${min}` : ""}${short}`;
      };
      return `${line("주간(D)", "day")}\n${line("저녁(E)", "evening")}\n${line("야간(N)", "night")}`;
    },

    isUnderstaffed(day: number, type: string): boolean {
      const count = this.getDayCount(day, type);
      const minReq = this.getMinRequired(day, type);
      return count < (minReq ?? 2);
    },

    nurseRowIdx(employeeId: string): number {
      return this.nurses.findIndex((n) => n.employeeId === employeeId);
    },

    cellKeyAt(rowIdx: number, colIdx: number): string | null {
      const nurse = this.nurses[rowIdx];
      const dateStr = this.cycleDates[colIdx];
      if (!(nurse && dateStr)) {
        return null;
      }
      return `${nurse.employeeId}:${dateStr}`;
    },

    keysInRange(
      from: { rowIdx: number; colIdx: number },
      to: { rowIdx: number; colIdx: number },
    ): Set<string> {
      const minR = Math.min(from.rowIdx, to.rowIdx);
      const maxR = Math.max(from.rowIdx, to.rowIdx);
      const minC = Math.min(from.colIdx, to.colIdx);
      const maxC = Math.max(from.colIdx, to.colIdx);
      const keys = new Set<string>();
      for (let r = minR; r <= maxR; r++) {
        for (let c = minC; c <= maxC; c++) {
          const k = this.cellKeyAt(r, c);
          if (k) {
            keys.add(k);
          }
        }
      }
      return keys;
    },

    updateSelectionStyles() {
      let el = document.getElementById("sched-selection-styles");
      if (!el) {
        el = document.createElement("style");
        el.id = "sched-selection-styles";
        document.head.appendChild(el);
      }
      if (this.selectedCells.size === 0) {
        el.textContent = "";
        return;
      }
      // 드래그 중에도 outline 을 그려서 Excel 처럼 선택 영역이 즉시 사각형으로 보임.
      const rules: string[] = [];
      for (const key of this.selectedCells) {
        const [empId, dateStr] = key.split(":");
        const rIdx = this.nurseRowIdx(empId);
        const cIdx = this.cycleDates.indexOf(dateStr);
        if (rIdx < 0 || cIdx < 0) {
          continue;
        }
        const hasTop = !this.selectedCells.has(this.cellKeyAt(rIdx - 1, cIdx) ?? "");
        const hasBottom = !this.selectedCells.has(this.cellKeyAt(rIdx + 1, cIdx) ?? "");
        const hasLeft = !this.selectedCells.has(this.cellKeyAt(rIdx, cIdx - 1) ?? "");
        const hasRight = !this.selectedCells.has(this.cellKeyAt(rIdx, cIdx + 1) ?? "");
        // 선택 셀의 자체 회색 보더(border-r/b)는 무조건 투명 — outline 이 분리선 역할.
        // 첫행/마지막행/wrapper overflow:hidden 에 outline 이 짤리지 않도록 inset:0 사용.
        const tdStyle: string[] = [
          "background:var(--ds-selection-tint-12)!important",
          "border-right-color:transparent!important",
          "border-bottom-color:transparent!important",
        ];
        rules.push(`td[data-ck="${key}"]{${tdStyle.join(";")}}`);
        const borders = [
          `border-top:${hasTop ? "2px solid var(--color-neutral-900)" : "0"}`,
          `border-bottom:${hasBottom ? "2px solid var(--color-neutral-900)" : "0"}`,
          `border-left:${hasLeft ? "2px solid var(--color-neutral-900)" : "0"}`,
          `border-right:${hasRight ? "2px solid var(--color-neutral-900)" : "0"}`,
        ].join(";");
        rules.push(
          `td[data-ck="${key}"]::before{content:"";position:absolute;inset:0 -1px -1px 0;${borders};pointer-events:none;z-index:20}`,
        );
      }
      if (
        !this.isFillDragging &&
        this.isNurseManager &&
        this.scheduleStatus !== "published" &&
        this.scheduleStatus !== "confirmed"
      ) {
        let maxR = -1;
        let maxC = -1;
        for (const key of this.selectedCells) {
          const [empId, dateStr] = key.split(":");
          const r = this.nurseRowIdx(empId);
          const c = this.cycleDates.indexOf(dateStr);
          if (r > maxR || (r === maxR && c > maxC)) {
            maxR = r;
            maxC = c;
          }
        }
        if (maxR >= 0 && maxC >= 0) {
          const ck = this.cellKeyAt(maxR, maxC);
          if (ck) {
            rules.push(`td[data-ck="${ck}"] .fill-handle{display:block}`);
          }
        }
      }
      el.textContent = rules.join("\n");
    },

    clearSelection() {
      this.selectedCells = new Set();
      this.cursorCell = null;
      this.lastClickedCell = null;
      this.selectionVersion++;
      this.updateSelectionStyles();
    },

    /** nurses/cycleDates 변경 후 호출 — 이벤트 위임용 역방향 인덱스 재구축. */
    rebuildCellIndexMaps() {
      const emp = new Map<string, number>();
      for (let i = 0; i < this.nurses.length; i++) {
        emp.set(this.nurses[i].employeeId, i);
      }
      const col = new Map<string, number>();
      for (let i = 0; i < this.cycleDates.length; i++) {
        col.set(this.cycleDates[i], i);
      }
      this.empRowIndex = emp;
      this.dateColIndex = col;
    },

    /** data-ck("empId:dateStr") → {rowIdx, colIdx, employeeId, dateStr} 또는 null. */
    cellFromCk(ck: string): {
      rowIdx: number;
      colIdx: number;
      employeeId: string;
      dateStr: string;
    } | null {
      const i = ck.indexOf(":");
      if (i < 0) {
        return null;
      }
      const employeeId = ck.slice(0, i);
      const dateStr = ck.slice(i + 1);
      const rowIdx = this.empRowIndex.get(employeeId);
      const colIdx = this.dateColIndex.get(dateStr);
      if (rowIdx === undefined || colIdx === undefined) {
        return null;
      }
      return { rowIdx, colIdx, employeeId, dateStr };
    },

    // ── Delegated grid handlers (on <tbody>) — replace 5 per-cell listeners ──
    onGridMouseDown(event: MouseEvent) {
      const target = event.target as HTMLElement;
      const fill = target.closest(".fill-handle");
      if (fill) {
        const td = fill.closest("td[data-ck]") as HTMLElement | null;
        const cell = td?.dataset.ck ? this.cellFromCk(td.dataset.ck) : null;
        if (cell) {
          event.preventDefault();
          event.stopPropagation();
          this.onFillHandleMouseDown(cell.rowIdx, cell.colIdx);
        }
        return;
      }
      const td = target.closest("td[data-ck]") as HTMLElement | null;
      const cell = td?.dataset.ck ? this.cellFromCk(td.dataset.ck) : null;
      if (cell) {
        this.onCellMouseDown(event, cell.rowIdx, cell.colIdx);
      }
    },

    onGridMouseOver(event: MouseEvent) {
      const td = (event.target as HTMLElement).closest("td[data-ck]") as HTMLElement | null;
      const ck = td?.dataset.ck;
      if (!ck || ck === this.lastHoverCk) {
        return;
      }
      this.lastHoverCk = ck;
      const cell = this.cellFromCk(ck);
      if (!cell) {
        return;
      }
      this.onCellMouseEnter(cell.rowIdx, cell.colIdx);
      // lazy native title for the hovered cell (replaces a per-cell :title binding)
      if (td) {
        td.title = this.prefShow ? this.prefCellTitle(cell.employeeId, cell.dateStr) : "";
      }
    },

    // 더블클릭 → 근무 변경 quick menu. (단일 클릭은 mousedown 의 선택만 — popover 안 뜸.)
    onGridDblClick(event: MouseEvent) {
      const td = (event.target as HTMLElement).closest("td[data-ck]") as HTMLElement | null;
      const cell = td?.dataset.ck ? this.cellFromCk(td.dataset.ck) : null;
      if (cell) {
        // 더블클릭도 우클릭과 동일한 퀵 메뉴(컨텍스트 메뉴)를 연다.
        this.handleCellContextMenu(event, cell.rowIdx, cell.colIdx);
      }
    },

    onGridContextMenu(event: MouseEvent) {
      const td = (event.target as HTMLElement).closest("td[data-ck]") as HTMLElement | null;
      const cell = td?.dataset.ck ? this.cellFromCk(td.dataset.ck) : null;
      if (cell) {
        this.handleCellContextMenu(event, cell.rowIdx, cell.colIdx);
      }
    },

    onCellMouseDown(event: MouseEvent, rowIdx: number, colIdx: number) {
      this.cellCtxMenu.show = false;
      this.nurseCtxMenu.show = false;
      if (!this.isNurseManager) {
        return;
      }
      if (this.scheduleStatus === "published" || this.scheduleStatus === "confirmed") {
        return;
      }
      if (event.button !== 0) {
        return;
      }
      event.preventDefault();

      if (this.selectedWorkTypeRows.size > 0) {
        this.selectedWorkTypeRows = new Set();
        this.updateWorkTypeSelectionStyles();
      }

      const key = this.cellKeyAt(rowIdx, colIdx);
      if (!key) {
        return;
      }

      if (event.shiftKey && this.lastClickedCell) {
        this.selectedCells = this.keysInRange(this.lastClickedCell, { rowIdx, colIdx });
      } else if (event.metaKey || event.ctrlKey) {
        const next = new Set(this.selectedCells);
        if (next.has(key)) {
          next.delete(key);
        } else {
          next.add(key);
        }
        this.selectedCells = next;
      } else {
        this.selectedCells = new Set([key]);
        this.dragStartCell = { rowIdx, colIdx };
        this.isDragging = true;
      }
      this.cursorCell = { rowIdx, colIdx };
      this.lastClickedCell = { rowIdx, colIdx };
      this.selectionVersion++;
      this.updateSelectionStyles();
    },

    onCellMouseEnter(rowIdx: number, colIdx: number) {
      if (this.isFillDragging && this.fillDragAnchor) {
        this.pendingDragTo = { rowIdx, colIdx };
        if (this.dragRafPending) {
          return;
        }
        this.dragRafPending = true;
        requestAnimationFrame(() => {
          this.dragRafPending = false;
          if (this.pendingDragTo && this.fillDragAnchor) {
            const fillKeys = this.keysInRange(this.fillDragAnchor, this.pendingDragTo);
            const merged = new Set(this.selectedCells);
            for (const k of fillKeys) {
              merged.add(k);
            }
            this.fillPreviewKeys = fillKeys;
            this.updateFillPreviewStyles(merged);
          }
        });
        return;
      }
      if (!(this.isDragging && this.dragStartCell)) {
        return;
      }
      this.pendingDragTo = { rowIdx, colIdx };
      if (this.dragRafPending) {
        return;
      }
      this.dragRafPending = true;
      requestAnimationFrame(() => {
        this.dragRafPending = false;
        if (this.pendingDragTo && this.dragStartCell) {
          this.selectedCells = this.keysInRange(this.dragStartCell, this.pendingDragTo);
          this.selectionVersion++;
          this.updateSelectionStyles();
        }
      });
    },

    updateFillPreviewStyles(allKeys: Set<string>) {
      const el = document.getElementById("sched-selection-styles");
      if (!el) {
        return;
      }
      const rules: string[] = [];
      for (const key of allKeys) {
        const isPreview = this.fillPreviewKeys.has(key) && !this.selectedCells.has(key);
        const bg = isPreview
          ? "background:var(--ds-selection-tint-08)!important"
          : "background:var(--ds-selection-tint-12)!important";
        const border = isPreview
          ? "outline:1px dashed var(--color-hr-blue-500);outline-offset:-1px;"
          : "";
        rules.push(`td[data-ck="${key}"]{${bg};${border}position:relative}`);
      }
      el.textContent = rules.join("\n");
    },

    onFillHandleMouseDown(rowIdx: number, colIdx: number) {
      if (!this.isNurseManager || this.selectedCells.size === 0) {
        return;
      }
      const sourceKey = this.cursorCell
        ? this.cellKeyAt(this.cursorCell.rowIdx, this.cursorCell.colIdx)
        : [...this.selectedCells][0];
      this.fillSourceValue = sourceKey ? this.entries.get(sourceKey) || "off" : "off";
      this.fillDragAnchor = { rowIdx, colIdx };
      this.isFillDragging = true;
      this.fillPreviewKeys = new Set();
    },

    onCellMouseUp() {
      if (this.isFillDragging) {
        this.isFillDragging = false;
        const newKeys = new Set<string>();
        for (const k of this.fillPreviewKeys) {
          if (!this.selectedCells.has(k)) {
            newKeys.add(k);
          }
        }
        this.fillPreviewKeys = new Set();
        if (newKeys.size > 0) {
          this.pushUndo();
          for (const k of newKeys) {
            this.entries.set(k, this.fillSourceValue);
            this.selectedCells.add(k);
          }
          this.selectionVersion++;
          this.updateSelectionStyles();
          this.persistFillEntries(newKeys, this.fillSourceValue);
        } else {
          this.selectionVersion++;
          this.updateSelectionStyles();
        }
        return;
      }
      if (this.isDragging) {
        this.isDragging = false;
        const dragEnd = this.pendingDragTo;
        if (this.pendingDragTo && this.dragStartCell) {
          this.selectedCells = this.keysInRange(this.dragStartCell, this.pendingDragTo);
          this.pendingDragTo = null;
        }
        this.selectionVersion++;
        this.updateSelectionStyles();
        // 드래그로 다중 선택 완료 → 마지막 셀에 popover 즉시 표시.
        // mouseup 이 mousedown 과 다른 td 에서 발생하므로 @click 이 안 뜸 — 직접 호출.
        if (dragEnd && this.selectedCells.size > 1) {
          this.onCellDblClick(dragEnd.rowIdx, dragEnd.colIdx);
        }
      }
    },

    async persistFillEntries(keys: Set<string>, assignmentType: string) {
      if (!(await this.ensureSchedule())) {
        return;
      }
      const entries = [...keys].map((k) => {
        const [employeeId, date] = k.split(":");
        return { employeeId, date, assignmentType };
      });
      try {
        await this.sdk.nurseScheduler.batchUpdateEntries({
          scheduleId: this.scheduleId as string,
          entries,
        });
        if (this.scheduleStatus === "ai_generated" || this.scheduleStatus === "draft") {
          this.scheduleStatus = "editing";
        }
        this.refreshDupConflicts();
      } catch {
        await this.loadSchedule();
      }
    },

    // 근무형태 모달이 열릴 때 초기 선택값:
    //   선택된 행이 모두 같은 workType이면 그 값
    //   서로 다르면 기본값(three_shift)
    getInitialSelectedWorkType(): string {
      if (this.selectedWorkTypeRows.size === 0) {
        return "three_shift";
      }
      const types = new Set<string>();
      for (const rowIdx of this.selectedWorkTypeRows) {
        const nurse = this.nurses[rowIdx];
        if (nurse?.workType) {
          types.add(nurse.workType);
        }
      }
      if (types.size === 1) {
        return [...types][0];
      }
      return "three_shift";
    },

    // 근무 변경 모달의 다중 선택 표시:
    //   1셀  → null (단일 표시 사용)
    //   같은 1명, 여러 날 → { primary: 간호사명, secondary: "N일" }
    //   여러 명          → { primary: "N명",  secondary: "M칸 선택됨" }
    entrySelectionLabel(): { primary: string; secondary: string } | null {
      const count = this.selectedCells.size;
      if (count <= 1) {
        return null;
      }
      const empIds = new Set<string>();
      for (const k of this.selectedCells) {
        empIds.add(k.split(":")[0]);
      }
      if (empIds.size === 1) {
        const empId = [...empIds][0];
        const nurse = this.nurses.find((n) => n.employeeId === empId);
        return { primary: nurse?.name ?? "", secondary: `${count}일` };
      }
      return { primary: `${empIds.size}명`, secondary: `${count}칸 선택됨` };
    },

    /** 희망근무 popover 라벨 — entrySelectionLabel 과 동일 규칙.
     *  1셀: null (간호사명 · 날짜 단일 표시 fallback)
     *  같은 1명 여러날: 간호사명 / N일
     *  여러 명: N명 / M칸 선택됨 */
    prefSelectionLabel(): { primary: string; secondary: string } | null {
      const count = this.prefSelectedCells.size;
      if (count <= 1) {
        return null;
      }
      const empIds = new Set<string>();
      for (const k of this.prefSelectedCells) {
        empIds.add(k.split(":")[0]);
      }
      if (empIds.size === 1) {
        const empId = [...empIds][0];
        const nurse = this.nurses.find((n) => n.employeeId === empId);
        return { primary: nurse?.name ?? "", secondary: `${count}일` };
      }
      return { primary: `${empIds.size}명`, secondary: `${count}칸 선택됨` };
    },

    onCellDblClick(rowIdx: number, colIdx: number, ev?: MouseEvent) {
      if (!this.isNurseManager) {
        return;
      }
      const nurse = this.nurses[rowIdx];
      const dateStr = this.cycleDates[colIdx];
      if (!(nurse && dateStr)) {
        return;
      }
      // 빈 셀(미배정) 은 "" — popover 에서 어떤 옵션도 highlight 되지 않도록.
      // (이전에는 || "off" 였어서 OFF 가 선택된 것처럼 보임.)
      const current = this.entries.get(`${nurse.employeeId}:${dateStr}`) ?? "";
      // 셀은 data-ck 로 유일하게 식별 — 항상 셀렉터로 td 를 찾는다.
      // (이벤트 위임 때문에 ev.currentTarget 은 tbody 라 셀 rect 로 쓸 수 없다.)
      const td = document.querySelector(
        `td[data-ck="${nurse.employeeId}:${dateStr}"]`,
      ) as HTMLElement | null;
      const rect = td?.getBoundingClientRect();
      const px = ev?.clientX ?? window.innerWidth / 2;
      const py = ev?.clientY ?? window.innerHeight / 2;
      this.editingEntry = {
        employeeId: nurse.employeeId,
        date: dateStr,
        assignmentType: current,
        nurseName: nurse.name,
        // 셀 rect 가 있으면 그대로, 없으면(키보드/드래그) 포인터를 0크기 rect 로.
        cellRect: rect
          ? { left: rect.left, right: rect.right, top: rect.top, bottom: rect.bottom }
          : { left: px, right: px, top: py, bottom: py },
      };
      this.showEntryEdit = true;
    },

    /** 근무표 탭의 그리드 밖(셀/popover/work-type cell/picker 아닌 영역) 클릭 → 선택 해제.
     *  희망근무 탭의 handlePrefClickOutside 와 같은 패턴. */
    handleScheduleClickOutside(event: MouseEvent) {
      if (this.activeTab !== "schedule") {
        return;
      }
      if (this.selectedCells.size === 0 && this.selectedWorkTypeRows.size === 0) {
        return;
      }
      const target = event.target as HTMLElement | null;
      if (!target) {
        return;
      }
      // 셀(데이터 셀, 근무형태 셀) 클릭 → 각자의 mousedown 핸들러가 처리.
      if (target.closest("td[data-ck]") || target.closest("td[data-wt-row]")) {
        return;
      }
      // popover (근무 변경 / 근무형태 picker / 컨텍스트 메뉴) 클릭 → 무시.
      if (
        target.closest("[data-entry-popover]") ||
        target.closest("[data-wt-popover]") ||
        target.closest("[data-cell-ctx-menu]") ||
        target.closest("[data-wt-ctx-menu]") ||
        target.closest("[data-nurse-ctx-menu]")
      ) {
        return;
      }
      if (this.selectedCells.size > 0) {
        this.clearSelection();
      }
      if (this.selectedWorkTypeRows.size > 0) {
        this.selectedWorkTypeRows = new Set();
        this.lastClickedWtRow = null;
        this.wtCursorRow = null;
        this.updateWorkTypeSelectionStyles();
      }
    },

    /** 근무 변경 popover 외부 클릭 시 닫기.
     *  셀 클릭(td[data-ck])은 무시 — 더블클릭으로 다른 셀에서 다시 열거나 옮길 수 있게 유지. */
    handleEntryEditClickOutside(event: MouseEvent) {
      if (!this.showEntryEdit) {
        return;
      }
      const target = event.target as HTMLElement | null;
      if (!target) {
        return;
      }
      if (target.closest("td[data-ck]")) {
        return;
      }
      if (target.closest("[data-entry-popover]")) {
        return;
      }
      this.showEntryEdit = false;
    },

    /**
     * 셀 우클릭 메뉴 열기 — 클릭 위치에 띄운 뒤 실측해서 뷰포트 안으로 밀어 넣는다.
     *
     * 예전에는 높이를 상수(380px)로 가정했는데, 배정 목록이 근무 유형·휴가 유형 설정
     * 개수만큼 늘어나므로 유형을 몇 개만 추가해도 가정이 깨져 메뉴가 화면 아래로
     * 잘려 나갔다. 이제 렌더된 실제 크기를 재서 보정한다.
     * (메뉴 자체 max-height 는 cell-context-menu.html 에서 걸어 높이를 묶어 둔다.)
     */
    showContextMenuAt(x: number, y: number) {
      // 폭은 고정(w-52=208)이라 첫 페인트부터 대략 맞춰 두고 → 다음 프레임에 정밀 보정.
      const rough = Math.min(Math.max(8, x), Math.max(8, window.innerWidth - 208 - 8));
      this.cellCtxMenu = { show: true, x: rough, y };
      requestAnimationFrame(() => this._clampCellContextMenu(x, y, 0));
    },

    /** 렌더된 메뉴 크기를 기준으로 좌표를 뷰포트 안쪽으로 잠근다(가로·세로 모두). */
    _clampCellContextMenu(x: number, y: number, attempt: number) {
      if (!this.cellCtxMenu.show) {
        return;
      }
      const el = document.querySelector<HTMLElement>("[data-cell-ctx-menu]");
      if (!el) {
        return;
      }
      const rect = el.getBoundingClientRect();
      // x-show 가 아직 적용 전이면 0 — 잘못된 값으로 잠그지 않고 다음 프레임에 다시 시도한다.
      // 몇 프레임 안에 안 잡히면 포기한다(매 프레임 도는 루프를 남기지 않는다).
      if (rect.height === 0) {
        if (attempt < 5) {
          requestAnimationFrame(() => this._clampCellContextMenu(x, y, attempt + 1));
        }
        return;
      }
      const gap = 8;
      const maxX = Math.max(gap, window.innerWidth - rect.width - gap);
      const maxY = Math.max(gap, window.innerHeight - rect.height - gap);
      this.cellCtxMenu.x = Math.min(Math.max(gap, x), maxX);
      this.cellCtxMenu.y = Math.min(Math.max(gap, y), maxY);
    },

    handleCellContextMenu(event: MouseEvent, rowIdx: number, colIdx: number) {
      if (!this.isNurseManager) {
        return;
      }
      event.preventDefault();
      this.wtCtxMenu.show = false;
      const nurse = this.nurses[rowIdx];
      const dateStr = this.cycleDates[colIdx];
      if (nurse && dateStr) {
        const ck = `${nurse.employeeId}:${dateStr}`;
        if (!this.selectedCells.has(ck)) {
          this.selectedCells = new Set([ck]);
          this.cursorCell = { rowIdx, colIdx };
          this.selectionVersion++;
          this.updateSelectionStyles();
        }
      }
      this.showContextMenuAt(event.clientX, event.clientY);
    },

    handleScheduleKeyDown(event: KeyboardEvent) {
      if (!this.isNurseManager || this.activeTab !== "scheduler") {
        return;
      }
      const tag = (event.target as HTMLElement).tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") {
        return;
      }
      // Delete/Backspace 는 popover/picker 표시 여부와 무관하게 동작 —
      // 셀 선택 자체는 popover 와 독립이므로 popover 열려있어도 먼저 닫고 삭제.
      // (희망근무 탭 handlePrefKeyDown 과 동일 패턴.)
      if (event.key === "Delete" || event.key === "Backspace") {
        if (this.selectedCells.size === 0) {
          return;
        }
        event.preventDefault();
        if (this.showEntryEdit) {
          this.showEntryEdit = false;
        }
        if (this.showWorkTypePicker) {
          this.showWorkTypePicker = false;
        }
        this.clearSelected();
        return;
      }
      if (
        this.showEntryEdit ||
        this.showWardSettings ||
        this.showShortcutsHelp ||
        this.showSnapshotPicker ||
        this.showWorkTypePicker
      ) {
        return;
      }
      if (this.selectedWorkTypeRows.size > 0) {
        this.onWorkTypeKeyDown(event);
        return;
      }
      if (this.cellCtxMenu.show && event.key !== "Escape") {
        this.cellCtxMenu.show = false;
      }

      if (event.key === "?" || (event.shiftKey && event.key === "/")) {
        event.preventDefault();
        this.showShortcutsHelp = !this.showShortcutsHelp;
        return;
      }

      if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === "z") {
        event.preventDefault();
        this.redo();
        return;
      }

      if ((event.ctrlKey || event.metaKey) && event.key === "z") {
        event.preventDefault();
        this.undo();
        return;
      }

      if ((event.ctrlKey || event.metaKey) && event.key === "y") {
        event.preventDefault();
        this.redo();
        return;
      }

      if ((event.ctrlKey || event.metaKey) && event.key === "s") {
        event.preventDefault();
        this.saveSnapshot();
        Alpine.store("toast")?.success(
          `임시저장되었습니다 (${this.formatSnapshotDate(new Date().toISOString())})`,
        );
        return;
      }

      if ((event.ctrlKey || event.metaKey) && event.key === "l") {
        event.preventDefault();
        this.openSnapshotPicker();
        return;
      }

      const maxRow = this.nurses.length - 1;
      const maxCol = this.cycleDates.length - 1;

      if ((event.ctrlKey || event.metaKey) && event.key === "a") {
        event.preventDefault();
        this.selectedCells = this.keysInRange(
          { rowIdx: 0, colIdx: 0 },
          { rowIdx: maxRow, colIdx: maxCol },
        );
        this.selectionVersion++;
        this.updateSelectionStyles();
        return;
      }

      if ((event.ctrlKey || event.metaKey) && event.key === "c") {
        this.handleScheduleCopy();
        return;
      }

      if ((event.ctrlKey || event.metaKey) && event.key === "v") {
        this.handleSchedulePaste();
        return;
      }

      if (event.key === "Escape") {
        this.clearSelection();
        return;
      }

      if ((event.key === "Enter" || event.key === " ") && this.cursorCell) {
        event.preventDefault();
        this.onCellDblClick(this.cursorCell.rowIdx, this.cursorCell.colIdx);
        return;
      }

      const quickKeys: Record<string, string> = {
        d: "day",
        e: "evening",
        n: "night",
        o: "off",
        v: "annual_leave",
        b: "bereavement",
        t: "education",
        u: "unavailable",
      };
      const qk = quickKeys[event.key.toLowerCase()];
      if (qk && !event.ctrlKey && !event.metaKey && this.selectedCells.size > 0) {
        event.preventDefault();
        this.assignToSelected(qk);
        return;
      }

      if (!["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(event.key)) {
        return;
      }
      event.preventDefault();

      let r = this.cursorCell?.rowIdx ?? 0;
      let c = this.cursorCell?.colIdx ?? 0;
      switch (event.key) {
        case "ArrowUp":
          r = Math.max(0, r - 1);
          break;
        case "ArrowDown":
          r = Math.min(maxRow, r + 1);
          break;
        case "ArrowLeft":
          c = Math.max(0, c - 1);
          break;
        case "ArrowRight":
          c = Math.min(maxCol, c + 1);
          break;
      }

      if (event.shiftKey && this.lastClickedCell) {
        this.selectedCells = this.keysInRange(this.lastClickedCell, { rowIdx: r, colIdx: c });
      } else {
        const key = this.cellKeyAt(r, c);
        if (key) {
          this.selectedCells = new Set([key]);
          this.lastClickedCell = { rowIdx: r, colIdx: c };
        }
      }
      this.cursorCell = { rowIdx: r, colIdx: c };
      this.selectionVersion++;
      this.updateSelectionStyles();

      requestAnimationFrame(() => {
        const ck = this.cellKeyAt(r, c);
        if (!ck) {
          return;
        }
        const cell = document.querySelector(`td[data-ck="${ck}"]`) as HTMLElement | null;
        const scroll = cell?.closest(".sheet-scroll") as HTMLElement | null;
        if (!(cell && scroll)) {
          return;
        }
        const cellRect = cell.getBoundingClientRect();
        const scrollRect = scroll.getBoundingClientRect();
        const topPad = 44;
        const bottomPad = 34;
        const leftPad = 160;

        if (cellRect.top < scrollRect.top + topPad) {
          scroll.scrollTop -= scrollRect.top + topPad - cellRect.top;
        } else if (cellRect.bottom > scrollRect.bottom - bottomPad) {
          scroll.scrollTop += cellRect.bottom - (scrollRect.bottom - bottomPad);
        }
        if (cellRect.left < scrollRect.left + leftPad) {
          scroll.scrollLeft -= scrollRect.left + leftPad - cellRect.left;
        } else if (cellRect.right > scrollRect.right) {
          scroll.scrollLeft += cellRect.right - scrollRect.right;
        }
      });
    },

    async ensureSchedule(): Promise<boolean> {
      if (this.scheduleId) {
        return true;
      }
      if (!(this.sdk && this.selectedWardId)) {
        return false;
      }
      try {
        const { startDate, endDate } = this.getCycleDates(this.period.year, this.period.month);
        const result = await this.sdk.nurseScheduler.createSchedule({
          wardId: this.selectedWardId,
          scheduleGroupId: this.selectedScheduleGroupId || undefined,
          year: this.period.year,
          month: this.period.month,
          startDate,
          endDate,
        });
        // 기존 `as { id: string }` 캐스트는 실제 응답({success, message, data:{id}})과
        // 달라 scheduleId가 항상 undefined로 들어가던 버그였다.
        this.scheduleId = result.data.id;
        this.scheduleStatus = "draft";
        return true;
      } catch {
        return false;
      }
    },

    async assignToSelected(assignmentType: string) {
      if (!(this.sdk && this.selectedCells.size > 0)) {
        return;
      }
      if (!(await this.ensureSchedule())) {
        return;
      }
      this.pushUndo();
      // 연동 근무 유형 id → shift_custom + shiftId, 커스텀 휴가 유형 id → leave_custom + customLeaveReasonId.
      // 셀 값은 항상 원본 id로 저장(라벨/색 해석용).
      const isCustomLeave = this.isCustomLeaveType(assignmentType);
      const isShift = !isCustomLeave && this.isSchedulerShift(assignmentType);
      const wireType = isCustomLeave ? "leave_custom" : isShift ? "shift_custom" : assignmentType;
      const customLeaveReasonId = isCustomLeave ? assignmentType : undefined;
      const shiftId = isShift ? assignmentType : undefined;
      const updates: { employeeId: string; date: string }[] = [];
      for (const key of this.selectedCells) {
        const [empId, dateStr] = key.split(":");
        updates.push({ employeeId: empId, date: dateStr });
        this.entries.set(key, assignmentType);
      }
      try {
        await this.sdk.nurseScheduler.batchUpdateEntries({
          scheduleId: this.scheduleId as string,
          entries: updates.map((u) => ({
            employeeId: u.employeeId,
            date: u.date,
            assignmentType: wireType,
            ...(customLeaveReasonId ? { customLeaveReasonId } : {}),
            ...(shiftId ? { shiftId } : {}),
          })),
        });
        if (this.scheduleStatus === "ai_generated" || this.scheduleStatus === "draft") {
          this.scheduleStatus = "editing";
        }
        this.refreshDupConflicts();
      } catch {
        await this.loadSchedule();
      }
    },

    // 선택 셀의 배정을 완전히 제거 — Delete/Backspace 키 동작. OFF 배정과는 구분.
    async clearSelected() {
      if (!(this.sdk && this.selectedCells.size > 0)) {
        return;
      }
      if (!(await this.ensureSchedule())) {
        return;
      }
      this.pushUndo();
      const deletes: { employeeId: string; date: string }[] = [];
      for (const key of this.selectedCells) {
        const [empId, dateStr] = key.split(":");
        deletes.push({ employeeId: empId, date: dateStr });
        this.entries.delete(key);
      }
      try {
        await this.sdk.nurseScheduler.batchDeleteEntries({
          scheduleId: this.scheduleId as string,
          entries: deletes,
        });
        if (this.scheduleStatus === "ai_generated" || this.scheduleStatus === "draft") {
          this.scheduleStatus = "editing";
        }
        this.refreshDupConflicts();
      } catch {
        await this.loadSchedule();
      }
    },

    handleScheduleCopy() {
      if (this.selectedCells.size === 0) {
        return;
      }
      let minR = Number.MAX_SAFE_INTEGER;
      let maxR = 0;
      let minC = Number.MAX_SAFE_INTEGER;
      let maxC = 0;
      for (const key of this.selectedCells) {
        const [empId, dateStr] = key.split(":");
        const r = this.nurseRowIdx(empId);
        const c = this.cycleDates.indexOf(dateStr);
        if (r >= 0 && c >= 0) {
          minR = Math.min(minR, r);
          maxR = Math.max(maxR, r);
          minC = Math.min(minC, c);
          maxC = Math.max(maxC, c);
        }
      }
      const lines: string[] = [];
      for (let r = minR; r <= maxR; r++) {
        const cols: string[] = [];
        for (let c = minC; c <= maxC; c++) {
          const key = this.cellKeyAt(r, c);
          const val = key ? this.entries.get(key) || "off" : "";
          const labels = getAssignmentLabels(this);
          cols.push(labels[val] || val);
        }
        lines.push(cols.join("\t"));
      }
      navigator.clipboard.writeText(lines.join("\n")).catch(() => {
        // 클립보드 접근 실패는 무시 (브라우저 권한/포커스 이슈 등)
      });
    },

    async handleSchedulePaste() {
      if (!(this.lastClickedCell && this.selectedCells.size > 0)) {
        return;
      }
      try {
        const text = await navigator.clipboard.readText();
        if (!text.trim()) {
          return;
        }
        const labelToType: Record<string, string> = {};
        const allLabels = getAssignmentLabels(this);
        for (const [k, v] of Object.entries(allLabels)) {
          labelToType[v] = k;
          labelToType[k] = k;
        }

        const rows = text
          .trim()
          .split("\n")
          .map((line) => line.split("\t"));
        const isSingle = rows.length === 1 && rows[0].length === 1;

        if (isSingle) {
          const val = labelToType[rows[0][0].trim()] || rows[0][0].trim();
          if (val) {
            await this.assignToSelected(val);
          }
        } else {
          this.pushUndo();
          const startR = this.lastClickedCell.rowIdx;
          const startC = this.lastClickedCell.colIdx;
          const updates: { employeeId: string; date: string; assignmentType: string }[] = [];
          for (let r = 0; r < rows.length; r++) {
            for (let c = 0; c < rows[r].length; c++) {
              const key = this.cellKeyAt(startR + r, startC + c);
              if (!key) {
                continue;
              }
              const val = labelToType[rows[r][c].trim()] || rows[r][c].trim();
              if (val) {
                const [empId, dateStr] = key.split(":");
                updates.push({ employeeId: empId, date: dateStr, assignmentType: val });
                this.entries.set(key, val);
              }
            }
          }
          if (updates.length > 0 && this.sdk && this.scheduleId) {
            await this.sdk.nurseScheduler.batchUpdateEntries({
              scheduleId: this.scheduleId as string,
              entries: updates.map((u) => ({
                employeeId: u.employeeId,
                date: u.date,
                assignmentType: u.assignmentType,
              })),
            });
            if (this.scheduleStatus === "ai_generated" || this.scheduleStatus === "draft") {
              this.scheduleStatus = "editing";
            }
            this.refreshDupConflicts();
          }
        }
      } catch {
        // Clipboard error
      }
    },

    cycleCellType() {
      const idx = CYCLE_ORDER.indexOf(this.editingEntry.assignmentType);
      this.editingEntry.assignmentType = CYCLE_ORDER[(idx + 1) % CYCLE_ORDER.length];
    },

    async saveEntry() {
      if (!this.sdk) {
        return;
      }
      // 옵션 미선택 상태에서 저장 시도 → 무시 (popover 빈 셀 디폴트 = "" 대응).
      if (!this.editingEntry.assignmentType) {
        return;
      }
      if (!(await this.ensureSchedule())) {
        return;
      }
      // 다중 선택된 경우: 선택된 셀 전체에 동일 옵션 일괄 적용
      if (this.selectedCells.size > 1) {
        const assignmentType = this.editingEntry.assignmentType;
        this.showEntryEdit = false;
        await this.assignToSelected(assignmentType);
        return;
      }
      this.pushUndo();
      // 연동 근무 유형 id → shift_custom+shiftId, 커스텀 휴가 유형 id → leave_custom+customLeaveReasonId.
      const at = this.editingEntry.assignmentType;
      const isCustomLeave = this.isCustomLeaveType(at);
      const isShift = !isCustomLeave && this.isSchedulerShift(at);
      const wireType = isCustomLeave ? "leave_custom" : isShift ? "shift_custom" : at;
      try {
        await this.sdk.nurseScheduler.updateEntry({
          scheduleId: this.scheduleId as string,
          employeeId: this.editingEntry.employeeId,
          date: this.editingEntry.date,
          assignmentType: wireType,
          ...(isCustomLeave ? { customLeaveReasonId: at } : {}),
          ...(isShift ? { shiftId: at } : {}),
        });
        // 셀 값은 항상 원본 id로 저장(라벨/색 해석용)
        this.entries.set(`${this.editingEntry.employeeId}:${this.editingEntry.date}`, at);
        this.showEntryEdit = false;
        this.refreshDupConflicts();
      } catch {
        await this.loadSchedule();
      }
    },

    getCycleDates(year: number, month: number): { startDate: string; endDate: string } {
      const pad = (n: number) => String(n).padStart(2, "0");
      const startDay = this.cycleStartDay;
      const startDate = `${year}-${pad(month)}-${pad(startDay)}`;
      if (startDay === 1) {
        const lastDay = new Date(year, month, 0).getDate();
        return { startDate, endDate: `${year}-${pad(month)}-${pad(lastDay)}` };
      }
      const endDay = startDay - 1;
      let nextM = month + 1;
      let nextY = year;
      if (nextM > 12) {
        nextM = 1;
        nextY++;
      }
      const endDate = `${nextY}-${pad(nextM)}-${pad(endDay)}`;
      return { startDate, endDate };
    },

    async loadSchedulerConfig() {
      if (!this.sdk) {
        return;
      }
      try {
        const cfg = await this.sdk.nurseScheduler.getConfig();
        // 종별(요양병원 여부)에 따라 간호등급 표가 갈린다 — resolveNursingGrade 분기 키.
        this.organizationType = cfg?.organizationType ?? null;
        this.cycleStartDay = cfg?.cycleStartDay ?? 16;
        if (cfg?.staffingRatios) {
          this.wardStaffingRatios = cfg.staffingRatios;
          this.wardSettingsForm.ratioDay = cfg.staffingRatios.day;
          this.wardSettingsForm.ratioEvening = cfg.staffingRatios.evening;
          this.wardSettingsForm.ratioNight = cfg.staffingRatios.night;
        }
        this.wardAideRatio = cfg?.aideRatio ?? 0.5;
        this.wardCaregiverRatio = cfg?.caregiverRatio ?? 0.3;
        this.wardSettingsForm.aideRatio = this.wardAideRatio;
        this.wardSettingsForm.caregiverRatio = this.wardCaregiverRatio;
        this.wardSettingsForm.aidePatientRatio = cfg?.aidePatientRatio ?? 0;
        this.wardSettingsForm.supportPatientRatio = cfg?.supportPatientRatio ?? 0;
        this.wardSettingsForm.rehabSupportPatientRatio = cfg?.rehabSupportPatientRatio ?? 0;
        // 교대 매핑 · 교육 정책은 스케줄러 설정(loadScheduleGroupSettings)이 담당 — 조직 설정은 안 읽는다.
        this.preferenceTabEnabled = cfg?.preferenceTabEnabled ?? true;
        // 탭이 히든인데 현재 희망근무 탭에 있으면 근무표로 이동.
        if (!this.preferenceTabEnabled && this.activeTab === "pref-input") {
          this.activeTab = "scheduler";
        }
      } catch {
        this.cycleStartDay = 16;
      }
      this.cycleStartDayDraft = this.cycleStartDay;
      this.staffingSettingsSaved = JSON.stringify(this.wardSettingsForm);
    },

    /** 주기 설정 저장 진입점 — DS 컨펌 다이얼로그로 확인 후 저장. */
    async askSaveSchedulerConfig() {
      const ok = await askConfirm({
        message:
          "근무표 주기 시작일을 변경하시겠습니까?\n이 설정은 새로 생성되는 근무표에만 적용되며, 기존에 생성된 근무표의 기간은 변경되지 않아요.",
        emphasis: "근무표 주기 시작일을 변경하시겠습니까?",
      });
      if (ok) {
        await this.confirmSaveSchedulerConfig();
      }
    },

    async confirmSaveSchedulerConfig() {
      if (!this.sdk) {
        return;
      }
      const val = Math.max(1, Math.min(28, this.cycleStartDayDraft || 16));
      this.schedulerConfigSaving = true;
      try {
        await this.sdk.nurseScheduler.updateConfig({ cycleStartDay: val });
        this.cycleStartDay = val;
        this.cycleStartDayDraft = val;
        this.cycleJustSaved = true;
        Alpine.store("toast")?.success("수정이 완료되었습니다");
      } catch (e: unknown) {
        // 저장 실패 시 원인 디버깅용 — devtools 콘솔에서 확인
        // biome-ignore lint/suspicious/noConsole: 사용자 액션 실패 원인 노출
        console.error("confirmSaveSchedulerConfig failed:", e);
        Alpine.store("toast")?.error?.("주기 저장에 실패했어요. 잠시 후 다시 시도해주세요.");
      }
      this.schedulerConfigSaving = false;
    },

    async createSchedule() {
      if (!(this.sdk && this.selectedWardId)) {
        return;
      }
      try {
        let minimumStaffing = { day: 2, evening: 2, night: 2 };
        if (this.wardStaffingRatios && this.wardBedCount) {
          minimumStaffing = {
            day: Math.ceil(this.wardBedCount / this.wardStaffingRatios.day),
            evening: Math.ceil(this.wardBedCount / this.wardStaffingRatios.evening),
            night: Math.ceil(this.wardBedCount / this.wardStaffingRatios.night),
          };
        }
        const cycle = this.getCycleDates(this.period.year, this.period.month);
        const result = await this.sdk.nurseScheduler.createSchedule({
          wardId: this.selectedWardId,
          scheduleGroupId: this.selectedScheduleGroupId || undefined,
          year: this.period.year,
          month: this.period.month,
          startDate: cycle.startDate,
          endDate: cycle.endDate,
          settings: {
            maxSimultaneousVacations: 2,
            maxConsecutiveNights: 3,
            minimumStaffing,
          },
        });
        this.scheduleId = result.data?.id;
        this.scheduleStatus = "draft";
        this.cycleStartDate = cycle.startDate;
        this.cycleEndDate = cycle.endDate;
      } catch {
        await this.loadSchedule();
      }
    },

    async generateSchedule() {
      if (!this.sdk) {
        return;
      }
      if (!this.scheduleId) {
        await this.createSchedule();
      }
      if (!this.scheduleId) {
        return;
      }

      this.generating = true;
      this.scheduleStatus = "generating";
      try {
        await this.sdk.nurseScheduler.generateSchedule(this.scheduleId);
        this.startGenerationPolling();
      } catch {
        this.generating = false;
        this.scheduleStatus = "draft";
      }
    },

    startGenerationPolling() {
      if (!(this.sdk && this.scheduleId)) {
        this.generating = false;
        return;
      }

      const scheduleId = this.scheduleId;
      const poller = pollAsyncOperation<{ status: string; metadata: Record<string, unknown> }>({
        statusFn: () => this.sdk.nurseScheduler.getGenerationStatus(scheduleId),
        getStatus: (data) => data.status,
        pendingStatuses: ["generating"],
        interval: 3000,
        timeout: 180_000,
        onProgress: (data) => {
          this.scheduleStatus = data.status;
        },
      });
      this.generationPoller = poller;

      poller.result.then(async ({ data, timedOut }) => {
        this.generating = false;
        this.generationPoller = null;

        if (timedOut) {
          this.scheduleStatus = data?.status ?? "draft";
          return;
        }

        await this.loadSchedule();

        if (Array.isArray(data.metadata?.warnings) && data.metadata.warnings.length > 0) {
          this.warnings = (data.metadata.warnings as string[]).map((w: string) => {
            const [date, ...rest] = w.split(": ");
            return { date, message: rest.join(": ") };
          });
          this.showConflicts = true;
        }
      });
    },

    async cancelGeneration() {
      if (this.generationPoller) {
        this.generationPoller.cancel();
        this.generationPoller = null;
      }
      this.generating = false;
      this.scheduleStatus = "draft";
      if (this.sdk && this.scheduleId) {
        try {
          await this.sdk.nurseScheduler.cancelGeneration(this.scheduleId);
        } catch {
          // Best-effort
        }
      }
    },

    async confirmSchedule() {
      if (!(this.sdk && this.scheduleId)) {
        return;
      }
      try {
        await this.sdk.nurseScheduler.confirmSchedule(this.scheduleId);
        this.scheduleStatus = "confirmed";
        this.showConfirmDialog = false;
      } catch {
        // Error
      }
    },

    async publishSchedule() {
      if (!(this.sdk && this.scheduleId)) {
        return;
      }
      try {
        await this.sdk.nurseScheduler.publishSchedule(this.scheduleId);
        this.scheduleStatus = "published";
        this.showConfirmDialog = false;
      } catch (e: unknown) {
        // biome-ignore lint/suspicious/noConsole: 디버깅용
        console.error("Failed to publish schedule:", e);
      }
    },

    /** 공용 확인 다이얼로그에서 OK 클릭 시 분기 진입점.
     *  confirm = 작성→확정, publish = 확정→공유. */
    async submitConfirmDialog() {
      if (this.confirmDialogAction === "publish") {
        await this.publishSchedule();
      } else {
        await this.confirmSchedule();
      }
    },

    openConfirmDialogFor(action: "confirm" | "publish") {
      this.confirmDialogAction = action;
      this.showConfirmDialog = true;
    },

    /** 확정/공유된 표를 다시 편집 가능한 상태로 되돌림.
     *  결근·스왑 등 공유 후 수정이 일반적이라 잠금을 가역적으로 푼다. */
    async revertScheduleToEditing() {
      if (!(this.sdk && this.scheduleId)) {
        return;
      }
      try {
        await this.sdk.nurseScheduler.revertScheduleToEditing(this.scheduleId);
        this.scheduleStatus = "editing";
      } catch (e: unknown) {
        // biome-ignore lint/suspicious/noConsole: 디버깅용
        console.error("Failed to revert schedule to editing:", e);
      }
    },

    async toggleAttendance() {
      this.showAttendance = !this.showAttendance;
      if (this.showAttendance) {
        await this.loadAttendanceOverlay();
      }
    },

    async loadAttendanceOverlay() {
      if (!this.sdk || this.nurses.length === 0 || this.cycleDates.length === 0) {
        return;
      }
      this.attendanceLoading = true;
      try {
        const empIds = this.nurses.map((n: NurseRow) => n.employeeId);
        const from = new Date(`${this.cycleDates[0]}T00:00:00`);
        const to = new Date(`${this.cycleDates[this.cycleDates.length - 1]}T23:59:59`);

        type AttItem = {
          employeeId: string;
          date: string;
          status: string;
          isLate?: boolean;
          isEarlyLeave?: boolean;
          checkIn?: string | null;
          checkOut?: string | null;
          totalHours?: string | null;
        };
        let allItems: AttItem[] = [];
        for (let page = 1; page <= 20; page++) {
          const result = (await this.sdk.attendance.list({
            filter: { dateRange: { from, to } },
            pagination: { page, limit: 500 },
          })) as unknown as { items: AttItem[] };
          const items = (result?.items || []) as AttItem[];
          allItems = allItems.concat(items);
          if (items.length < 500) {
            break;
          }
        }

        const empSet = new Set(empIds);
        const obj: Record<string, string> = {};
        const tips: Record<string, string> = {};
        const ciHours: Record<string, number> = {};
        const times: Record<string, { checkIn: string; checkOut: string }> = {};
        const tz = Alpine.store("hereby")?.timezone || "Asia/Seoul";
        const fmtTime = (iso: string | null | undefined): string => {
          if (!iso) {
            return "-";
          }
          const d = new Date(iso);
          return d.toLocaleTimeString("ko-KR", {
            hour: "2-digit",
            minute: "2-digit",
            timeZone: tz,
          });
        };
        for (const att of allItems) {
          if (!empSet.has(att.employeeId)) {
            continue;
          }
          const dateStr =
            typeof att.date === "string"
              ? att.date.split("T")[0]
              : new Date(att.date).toISOString().split("T")[0];
          const key = `${att.employeeId}:${dateStr}`;
          const ci = fmtTime(att.checkIn);
          const co = fmtTime(att.checkOut);
          times[key] = { checkIn: ci, checkOut: co };
          // h:mm 표기 (실근로시간 정산 규칙, 2026-07)
          const hrs = att.totalHours ? formatHoursHM(att.totalHours) : "";
          const parts = [`출근 ${ci}`, `퇴근 ${co}`];
          if (hrs) {
            parts.push(hrs);
          }
          if (att.isLate) {
            parts.push("지각");
          }
          if (att.isEarlyLeave) {
            parts.push("조퇴");
          }
          tips[key] = parts.join(" · ");
          if (att.checkIn) {
            const localHour = Number.parseInt(
              new Date(att.checkIn).toLocaleString("en-US", {
                hour: "numeric",
                hour12: false,
                timeZone: tz,
              }),
              10,
            );
            ciHours[key] = localHour;
          }
          if (att.status === "absent") {
            obj[key] = "absent";
            tips[key] = "결근";
          } else if (
            att.status === "leave" ||
            att.status === "sick_leave" ||
            att.status === "holiday"
          ) {
            obj[key] = "leave";
            tips[key] =
              att.status === "sick_leave" ? "병가" : att.status === "holiday" ? "공휴일" : "휴가";
          } else if (
            att.isLate ||
            att.isEarlyLeave ||
            att.status === "checked_out" ||
            att.status === "checked_in"
          ) {
            // 출근 기록이 있으면 지각/조퇴여도 모두 ok (그린 체크) — 결근만 시각적으로 구분.
            // 지각/조퇴 정보는 tooltip(parts) 에 이미 들어가 있다.
            obj[key] = "ok";
          }
        }

        const offTypes = new Set([
          "off",
          "annual_leave",
          "sick_leave",
          "bereavement",
          "education",
          "education_off",
          "unavailable",
          "half_day",
        ]);

        const today = Alpine.store("hereby").todayLocal() as string;
        for (const nurse of this.nurses) {
          for (const dateStr of this.cycleDates) {
            if (dateStr >= today) {
              continue;
            }
            const ck = `${nurse.employeeId}:${dateStr}`;
            const planned = this.entries.get(ck);
            if (planned && !offTypes.has(planned)) {
              if (!obj[ck]) {
                obj[ck] = "absent";
                tips[ck] = "출근 기록 없음";
              }
            }
          }
        }
        this.attendanceMap = obj;
        this.attendanceTips = tips;
        this.attendanceCheckInHours = ciHours;
        this.attendanceTimes = times;
        this.detectShiftMismatches();
      } catch {
        // Error
      }
      this.attendanceLoading = false;
    },

    getAttendanceStatus(employeeId: string, dateStr: string): string {
      if (!this.showAttendance) {
        return "";
      }
      return this.attendanceMap[`${employeeId}:${dateStr}`] || "";
    },

    showAttTooltip(event: MouseEvent, employeeId: string, dateStr: string) {
      const tip = this.attendanceTips[`${employeeId}:${dateStr}`];
      if (!tip) {
        return;
      }
      // 아이콘 기준 고정 — 마우스 위치와 무관. data-placement="top"이
      // transform: translate(-50%, -100%)로 위쪽 가운데 정렬.
      // y 오프셋 10 = 화살표 protrusion 6 + 아이콘과 화살표 끝 사이 여백 4
      // (admin 공용 icon-btn 툴팁과 동일 규약, main.ts:TopGap 참조).
      const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
      this.attTooltip = {
        show: true,
        x: rect.left + rect.width / 2,
        y: rect.top - 10,
        text: tip,
      };
    },

    hideAttTooltip() {
      this.attTooltip.show = false;
    },

    detectShiftMismatches() {
      const shiftStartHours: Array<{ type: string; startHour: number }> = [];
      const threeShiftMap = this.shiftMappingsForm?.threeShift;
      for (const [type, shiftId] of Object.entries(threeShiftMap || {})) {
        if (!shiftId || type.includes("Weekend") || type.includes("Holiday")) {
          continue;
        }
        const shift = this.availableShifts.find(
          (s: { id: string; startTime: string }) => s.id === shiftId,
        );
        if (shift?.startTime) {
          const h = Number.parseInt(shift.startTime.split(":")[0], 10);
          shiftStartHours.push({ type, startHour: h });
        }
      }
      shiftStartHours.sort((a, b) => a.startHour - b.startHour);

      const inferShift = (hour: number): string => {
        if (shiftStartHours.length === 0) {
          if (hour >= 6 && hour < 14) {
            return "day";
          }
          if (hour >= 14 && hour < 20) {
            return "evening";
          }
          return "night";
        }
        let best = shiftStartHours[0].type;
        let bestDist = 24;
        for (const s of shiftStartHours) {
          const forward = (hour - s.startHour + 24) % 24;
          const backward = (s.startHour - hour + 24) % 24;
          const dist = Math.min(forward, backward);
          if (dist < bestDist) {
            bestDist = dist;
            best = s.type;
          }
        }
        return best;
      };

      const shiftLabel: Record<string, string> = {
        day: "D (주간)",
        evening: "E (초번)",
        night: "N (야간)",
      };

      const nextDate = (ds: string): string => {
        const d = new Date(`${ds}T12:00:00`);
        d.setDate(d.getDate() + 1);
        return d.toISOString().split("T")[0];
      };

      const nameMap = new Map<string, string>();
      for (const n of this.nurses) {
        nameMap.set(n.employeeId, n.name);
      }

      const results: typeof this.attMismatches = [];
      const today = Alpine.store("hereby").todayLocal() as string;

      for (const nurse of this.nurses) {
        for (const dateStr of this.cycleDates) {
          if (dateStr >= today) {
            continue;
          }
          const ck = `${nurse.employeeId}:${dateStr}`;
          const scheduled = this.entries.get(ck);
          const offDutyTypes = new Set([
            "off",
            "annual_leave",
            "sick_leave",
            "bereavement",
            "education",
            "education_off",
            "unavailable",
            "half_day",
          ]);
          if (!scheduled || offDutyTypes.has(scheduled)) {
            continue;
          }
          const scheduledBase = scheduled.replace("_education", "");
          const ciHour = this.attendanceCheckInHours[ck];
          if (ciHour === undefined) {
            continue;
          }
          const actual = inferShift(ciHour);
          if (actual !== scheduledBase) {
            const nd = nextDate(dateStr);
            const ndInCycle = this.cycleDates.includes(nd) ? nd : null;
            const attTime = this.attendanceTimes[ck];
            const mappedShiftId = threeShiftMap?.[scheduledBase] || "";
            const mappedShift = mappedShiftId
              ? this.availableShifts.find((s: { id: string }) => s.id === mappedShiftId)
              : null;
            const expectedTime = mappedShift
              ? `${mappedShift.startTime}~${mappedShift.endTime}`
              : "";
            results.push({
              key: ck,
              nurseName: nameMap.get(nurse.employeeId) || nurse.employeeNumber,
              date: dateStr,
              scheduled: shiftLabel[scheduledBase] || scheduledBase,
              actual: shiftLabel[actual] || actual,
              nextDate: ndInCycle,
              checkIn: attTime?.checkIn || "-",
              checkOut: attTime?.checkOut || "-",
              expectedTime,
            });
          }
        }
      }
      this.attMismatches = results;
    },

    async applyMismatchFix(idx: number) {
      const m = this.attMismatches[idx];
      if (!m) {
        return;
      }
      const actualType = m.actual.startsWith("D")
        ? "day"
        : m.actual.startsWith("E")
          ? "evening"
          : "night";
      this.pushUndo();
      this.entries.set(m.key, actualType);
      const updates: { employeeId: string; date: string; assignmentType: string }[] = [
        { employeeId: m.key.split(":")[0], date: m.date, assignmentType: actualType },
      ];
      if (actualType === "night" && m.nextDate) {
        const nextKey = `${m.key.split(":")[0]}:${m.nextDate}`;
        const nextScheduled = this.entries.get(nextKey);
        if (nextScheduled === "day") {
          this.entries.set(nextKey, "off");
          updates.push({
            employeeId: m.key.split(":")[0],
            date: m.nextDate,
            assignmentType: "off",
          });
        }
      }
      if (await this.ensureSchedule()) {
        try {
          await this.sdk.nurseScheduler.batchUpdateEntries({
            scheduleId: this.scheduleId as string,
            entries: updates,
          });
          this.refreshDupConflicts();
        } catch {
          await this.loadSchedule();
        }
      }
      this.attMismatches.splice(idx, 1);
      this.showToast("보정 완료");
    },

    async applyAllMismatchFixes() {
      if (this.attMismatches.length === 0) {
        return;
      }
      this.pushUndo();
      const updates: { employeeId: string; date: string; assignmentType: string }[] = [];
      for (const m of this.attMismatches) {
        const actualType = m.actual.startsWith("D")
          ? "day"
          : m.actual.startsWith("E")
            ? "evening"
            : "night";
        const empId = m.key.split(":")[0];
        this.entries.set(m.key, actualType);
        updates.push({ employeeId: empId, date: m.date, assignmentType: actualType });
        if (actualType === "night" && m.nextDate) {
          const nextKey = `${empId}:${m.nextDate}`;
          const nextScheduled = this.entries.get(nextKey);
          if (nextScheduled === "day") {
            this.entries.set(nextKey, "off");
            updates.push({ employeeId: empId, date: m.nextDate, assignmentType: "off" });
          }
        }
      }
      if (updates.length > 0 && (await this.ensureSchedule())) {
        try {
          await this.sdk.nurseScheduler.batchUpdateEntries({
            scheduleId: this.scheduleId as string,
            entries: updates,
          });
          this.refreshDupConflicts();
        } catch {
          await this.loadSchedule();
        }
      }
      this.attMismatches = [];
      this.showMismatchPanel = false;
      this.showToast(`${updates.length}건 보정 완료`);
    },

    printSchedule() {
      const wardName = this.wards.find((w: Ward) => w.id === this.selectedWardId)?.name || "";
      // 스케줄러가 둘 이상인 병동만 이름을 덧붙인다 — 하나뿐이면 예전 제목 그대로.
      const groupName =
        this.wardScheduleGroups.length > 1 ? ` ${this.selectedScheduleGroup?.name || ""}` : "";
      const title = `${wardName}${groupName} ${this.period.year}년 ${this.period.month}월 근무표`;
      document.title = title;
      window.print();
    },

    pushUndo() {
      this.undoStack.push(new Map(this.entries));
      if (this.undoStack.length > 50) {
        this.undoStack.shift();
      }
      this.redoStack.length = 0;
      this.autoSave();
    },

    diffEntries(
      from: Map<string, string>,
      to: Map<string, string>,
    ): { employeeId: string; date: string; assignmentType: string }[] {
      const updates: { employeeId: string; date: string; assignmentType: string }[] = [];
      for (const [key, val] of from) {
        if (to.get(key) !== val) {
          const toVal = to.get(key) || "off";
          updates.push({
            employeeId: key.split(":")[0],
            date: key.split(":")[1],
            assignmentType: toVal,
          });
        }
      }
      for (const [key, val] of to) {
        if (!from.has(key)) {
          updates.push({
            employeeId: key.split(":")[0],
            date: key.split(":")[1],
            assignmentType: val,
          });
        }
      }
      return updates;
    },

    async syncEntries(updates: { employeeId: string; date: string; assignmentType: string }[]) {
      if (updates.length > 0 && this.scheduleId) {
        try {
          await this.sdk.nurseScheduler.batchUpdateEntries({
            scheduleId: this.scheduleId as string,
            entries: updates,
          });
          this.refreshDupConflicts();
        } catch {
          await this.loadSchedule();
        }
      }
    },

    async undo() {
      const prev = this.undoStack.pop();
      if (!prev) {
        return;
      }
      this.redoStack.push(new Map(this.entries));
      const updates = this.diffEntries(this.entries, prev);
      this.entries = prev;
      this.selectionVersion++;
      this.updateSelectionStyles();
      await this.syncEntries(updates);
      this.showToast("되돌리기 완료");
    },

    async redo() {
      const next = this.redoStack.pop();
      if (!next) {
        return;
      }
      this.undoStack.push(new Map(this.entries));
      const updates = this.diffEntries(this.entries, next);
      this.entries = next;
      this.selectionVersion++;
      this.updateSelectionStyles();
      await this.syncEntries(updates);
      this.showToast("다시 실행 완료");
    },

    /** 스냅샷은 스케줄러별 — 한 병동의 두 근무표가 서로의 스냅샷을 덮지 않게 그룹 id 를 키에 넣는다.
     *  (스케줄러 목록을 못 받은 폴백 상태에서만 예전 병동 키를 쓴다.) */
    snapshotStorageKey(): string {
      const scope = this.selectedScheduleGroupId
        ? `${this.selectedWardId}:${this.selectedScheduleGroupId}`
        : this.selectedWardId;
      return `sched-snapshot:${scope}:${this.period.year}-${this.period.month}`;
    },

    loadSnapshots() {
      this.savedSnapshots = JSON.parse(localStorage.getItem(this.snapshotStorageKey()) || "[]");
    },

    autoSave() {
      if (this.entries.size === 0) {
        return;
      }
      const key = this.snapshotStorageKey();
      const obj: Record<string, string> = {};
      for (const [k, v] of this.entries) {
        obj[k] = v;
      }
      const existing = JSON.parse(localStorage.getItem(key) || "[]") as {
        date: string;
        auto?: boolean;
        entries: Record<string, string>;
      }[];
      if (existing.length > 0 && existing[0].auto) {
        existing[0] = { date: new Date().toISOString(), auto: true, entries: obj };
      } else {
        existing.unshift({ date: new Date().toISOString(), auto: true, entries: obj });
      }
      if (existing.length > 20) {
        existing.length = 20;
      }
      localStorage.setItem(key, JSON.stringify(existing));
      this.savedSnapshots = existing;
    },

    saveSnapshot() {
      const key = this.snapshotStorageKey();
      const obj: Record<string, string> = {};
      for (const [k, v] of this.entries) {
        obj[k] = v;
      }
      const snapshot = { date: new Date().toISOString(), auto: false, entries: obj };
      const existing = JSON.parse(localStorage.getItem(key) || "[]") as {
        date: string;
        auto?: boolean;
        entries: Record<string, string>;
      }[];
      const insertIdx = existing.length > 0 && existing[0].auto ? 1 : 0;
      existing.splice(insertIdx, 0, snapshot);
      if (existing.length > 20) {
        existing.length = 20;
      }
      localStorage.setItem(key, JSON.stringify(existing));
      this.savedSnapshots = existing;
    },

    openSnapshotPicker() {
      this.loadSnapshots();
      if (this.savedSnapshots.length === 0) {
        this.showToast("저장된 스냅샷이 없습니다");
        return;
      }
      this.showSnapshotPicker = true;
    },

    async restoreSnapshotAt(idx: number) {
      const snap = this.savedSnapshots[idx];
      if (!snap) {
        return;
      }
      this.showSnapshotPicker = false;
      this.pushUndo();
      const restored = new Map<string, string>();
      for (const [k, v] of Object.entries(snap.entries)) {
        restored.set(k, v);
      }
      const updates: { employeeId: string; date: string; assignmentType: string }[] = [];
      const allKeys = new Set([...this.entries.keys(), ...restored.keys()]);
      for (const key of allKeys) {
        const curr = this.entries.get(key) || "off";
        const rest = restored.get(key) || "off";
        if (curr !== rest) {
          updates.push({
            employeeId: key.split(":")[0],
            date: key.split(":")[1],
            assignmentType: rest,
          });
        }
      }
      this.entries = restored;
      this.selectionVersion++;
      this.updateSelectionStyles();
      if (updates.length > 0 && (await this.ensureSchedule())) {
        try {
          await this.sdk.nurseScheduler.batchUpdateEntries({
            scheduleId: this.scheduleId as string,
            entries: updates,
          });
          this.refreshDupConflicts();
        } catch {
          await this.loadSchedule();
        }
      }
      this.showToast(`복원 완료 (${this.formatSnapshotDate(snap.date)})`);
    },

    deleteSnapshot(idx: number) {
      const key = this.snapshotStorageKey();
      this.savedSnapshots.splice(idx, 1);
      localStorage.setItem(key, JSON.stringify(this.savedSnapshots));
    },

    /** 화면용 날짜 「2026.10.13.」 — 「YYYY-MM-DD」로 시작하는 값의 날짜 부분만 바꾸고(뒤 글자는 유지), 날짜가 아닌 글자(생성기 경고의 머리말 등)는 그대로 둔다 */
    fmtDisplayDate(v: string | null | undefined): string {
      if (!v) {
        return "";
      }
      const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v));
      return m ? `${m[1]}.${m[2]}.${m[3]}.${String(v).slice(10)}` : String(v);
    },

    /** 저장본 시각 — 화면 형식 「2026.10.13. 14:30」(조직 시간대 · 24시간제) */
    formatSnapshotDate(iso: string): string {
      return Alpine.store("hereby").formatDateTime(iso);
    },

    showToast(msg: string) {
      this.showSaveToast = msg;
      setTimeout(() => {
        this.showSaveToast = "";
      }, 2000);
    },

    toggleVacationDate(dateStr: string) {
      const idx = this.prefVacationDates.indexOf(dateStr);
      if (idx >= 0) {
        this.prefVacationDates.splice(idx, 1);
      } else {
        this.prefVacationDates.push(dateStr);
      }
    },

    isVacationDate(dateStr: string): boolean {
      return this.prefVacationDates.includes(dateStr);
    },

    addUnavailableDate() {
      this.prefUnavailableDates.push({ date: "", reason: "" });
    },

    removeUnavailableDate(idx: number) {
      this.prefUnavailableDates.splice(idx, 1);
    },

    async savePreference() {
      if (!(this.sdk && this.selectedWardId)) {
        return;
      }
      try {
        if (this.prefId) {
          await this.sdk.nurseScheduler.updatePreference({
            id: this.prefId,
            vacationDates: this.prefVacationDates,
            unavailableDates: this.prefUnavailableDates,
            restrictedDates: this.prefRestrictedDates,
            notes: this.prefNotes,
          });
        } else {
          const result = await this.sdk.nurseScheduler.createPreference({
            year: this.prefYear,
            month: this.prefMonth,
            wardId: this.selectedWardId,
            vacationDates: this.prefVacationDates,
            unavailableDates: this.prefUnavailableDates,
            restrictedDates: this.prefRestrictedDates,
            notes: this.prefNotes,
          });
          this.prefId = result.data?.id;
        }
      } catch {
        // Error
      }
    },

    async submitPreference() {
      if (!this.sdk) {
        return;
      }
      await this.savePreference();
      if (!this.prefId) {
        return;
      }
      try {
        await this.sdk.nurseScheduler.submitPreference(this.prefId);
        this.prefStatus = "submitted";
      } catch {
        // Error
      }
    },

    async openAddProfile() {
      this.isEditing = false;
      this.newProfile = {
        employeeId: "",
        workType: "three_shift",
        wardId: this.selectedWardId,
        scheduleGroupId:
          this.selectedScheduleGroupId || this.defaultScheduleGroupId(this.selectedWardId),
        isHeadNurse: false,
      };
      this.selectedEmployeeIds = [];
      this.addProfileFailures = [];
      this.employeeSearch = "";
      this.employeeJobFilter = "";
      this.employeeCandidates = [];
      this.showAddProfile = true;
      this.loadEmployeeCandidates();
      // 이미 등록된 간호사 목록 갱신 — 드롭다운에서 disabled 처리에 사용
      if (this.sdk) {
        try {
          const items = await fetchAllPages<NurseProfileItem>(
            (page, limit) => this.sdk.nurseScheduler.listProfiles({}, { page, limit }),
            500,
          );
          this.registeredProfilePairs = items.map((p: NurseProfileItem) => ({
            employeeId: p.employeeId,
            scheduleGroupId: p.scheduleGroupId || p.scheduleGroup?.id || "",
          }));
        } catch (e: unknown) {
          // biome-ignore lint/suspicious/noConsole: 디버깅용
          console.error("Failed to load registered nurses:", e);
          this.registeredProfilePairs = [];
        }
      }
    },

    /** 후보 전체를 한 번 받아 둔다 — 퇴사자는 빼고(휴직은 포함, 병동 「직원 배정」과 같은 기준). */
    async loadEmployeeCandidates() {
      if (!this.sdk) {
        return;
      }
      this.employeeSearchLoading = true;
      try {
        const items = await fetchAllPages<{
          id: string;
          employeeNumber: string | null;
          firstName: string | null;
          lastName: string | null;
          status: string;
          jobDuty?: string | null;
          teamNames?: string[];
        }>((page, limit) => this.sdk.employee.getEmployees({ page, limit }), 500);
        this.employeeCandidates = items
          .filter((e) => e.status !== "TERMINATED")
          .map((e) => ({
            id: e.id,
            employeeNumber: e.employeeNumber || "",
            firstName: e.firstName || "",
            lastName: e.lastName || "",
            jobDuty: e.jobDuty || "",
            teamNames: e.teamNames || [],
          }));
      } catch {
        this.employeeCandidates = [];
      }
      this.employeeSearchLoading = false;
    },

    /** 직무 선택지 — 지금 후보에 있는 직무만(가나다순), 비어 있으면 「미입력」을 맨 뒤에 */
    get employeeJobOptions(): string[] {
      const set = new Set(this.employeeCandidates.map((e) => e.jobDuty || "__unset"));
      return [...set].sort(
        (a, b) => Number(a === "__unset") - Number(b === "__unset") || a.localeCompare(b, "ko"),
      );
    },

    /** 표에 그릴 후보 — 검색어(이름 · 사번)와 직무 필터를 적용한다. */
    employeeCandidateRows() {
      const q = (this.employeeSearch || "").trim().toLowerCase();
      const job = this.employeeJobFilter;
      return this.employeeCandidates.filter((e) => {
        if (job && (e.jobDuty || "__unset") !== job) {
          return false;
        }
        if (!q) {
          return true;
        }
        const name = `${e.lastName}${e.firstName}`.toLowerCase();
        return name.includes(q) || (e.employeeNumber || "").toLowerCase().includes(q);
      });
    },

    // biome-ignore lint/suspicious/noExplicitAny: Employee picker
    /** 한 줄 체크 토글 — 이미 이 스케줄러에 있는 사람은 고를 수 없다 */
    toggleEmployeePick(id: string) {
      if (this.registeredNurseIds.includes(id)) {
        return;
      }
      const i = this.selectedEmployeeIds.indexOf(id);
      if (i >= 0) {
        this.selectedEmployeeIds.splice(i, 1);
      } else {
        this.selectedEmployeeIds.push(id);
      }
      this.newProfile.employeeId = this.selectedEmployeeIds[0] || "";
    },

    /** 지금 보이는 줄(검색·필터 적용) 중 고를 수 있는 것 전부 선택/해제 */
    toggleEmployeePickAll() {
      const pickable = this.employeeCandidateRows()
        .filter((e: { id: string }) => !this.registeredNurseIds.includes(e.id))
        .map((e: { id: string }) => e.id);
      const allPicked =
        pickable.length > 0 && pickable.every((id) => this.selectedEmployeeIds.includes(id));
      if (allPicked) {
        this.selectedEmployeeIds = this.selectedEmployeeIds.filter((id) => !pickable.includes(id));
      } else {
        for (const id of pickable) {
          if (!this.selectedEmployeeIds.includes(id)) {
            this.selectedEmployeeIds.push(id);
          }
        }
      }
      this.newProfile.employeeId = this.selectedEmployeeIds[0] || "";
    },

    employeePickAllChecked(): boolean {
      const pickable = this.employeeCandidateRows()
        .filter((e: { id: string }) => !this.registeredNurseIds.includes(e.id))
        .map((e: { id: string }) => e.id);
      return pickable.length > 0 && pickable.every((id) => this.selectedEmployeeIds.includes(id));
    },

    clearEmployee() {
      this.selectedEmployeeIds = [];
      this.newProfile.employeeId = "";
    },

    /** 고른 사람들을 같은 교대 형태로 한 번에 배정한다.
     *  한 사람이 실패해도 나머지는 그대로 들어간다 — 실패한 사람과 이유는 팝업에 남긴다
     *  (병동 「직원 배정」과 같은 규칙: 성공한 것을 되돌리지 않는다). */
    async submitProfile() {
      if (!this.sdk || this.selectedEmployeeIds.length === 0 || this.addProfileSaving) {
        return;
      }
      this.addProfileSaving = true;
      this.addProfileFailures = [];
      const nameOf = (id: string) => {
        const e = this.employeeCandidates.find((c: { id: string }) => c.id === id);
        return e ? `${e.lastName}${e.firstName}` : id;
      };
      let ok = 0;
      for (const employeeId of [...this.selectedEmployeeIds]) {
        try {
          // scheduleGroupId 가 있으면 그 스케줄러로, 없으면(목록 미수신) 병동 기본 스케줄러로 서버가 배정한다.
          await this.sdk.nurseScheduler.createProfile({
            ...this.newProfile,
            employeeId,
            // 책임자는 한 명만 — 여럿을 고른 경우 체크는 꺼져 있다(화면에서 막는다)
            isHeadNurse: this.selectedEmployeeIds.length === 1 && this.newProfile.isHeadNurse,
            scheduleGroupId: this.newProfile.scheduleGroupId || undefined,
          });
          ok += 1;
        } catch (err: unknown) {
          this.addProfileFailures.push({
            name: nameOf(employeeId),
            message: err instanceof Error ? err.message : "배정하지 못했어요.",
          });
        }
      }
      this.addProfileSaving = false;
      await this.loadProfiles();
      if (this.addProfileFailures.length === 0) {
        this.showAddProfile = false;
        Alpine.store("toast")?.success?.(`${ok}명을 배정했어요.`);
        return;
      }
      // 실패가 남았으면 팝업을 닫지 않는다 — 누가 왜 안 됐는지 보고 다시 고를 수 있게.
      this.selectedEmployeeIds = this.selectedEmployeeIds.filter((id) =>
        this.addProfileFailures.some((f: { name: string }) => f.name === nameOf(id)),
      );
      if (ok > 0) {
        Alpine.store("toast")?.info?.(`${ok}명 배정 · ${this.addProfileFailures.length}명 실패`);
      }
    },

    /** 간호사 프로필 편집 모달 열기 — workType / wardId / isHeadNurse 만 수정 가능. */
    openEditProfile(row: NurseRow) {
      const currentWardId = row.wardId || this.selectedWardId || "";
      const currentGroupId =
        row.scheduleGroupId ||
        (currentWardId === this.selectedWardId ? this.selectedScheduleGroupId : "") ||
        this.defaultScheduleGroupId(currentWardId);
      const pending = row.pendingReservation ?? null;
      this.editProfileForm = {
        profileId: row.profileId,
        employeeName: row.name,
        employeeNumber: row.employeeNumber,
        workType: row.workType || "three_shift",
        // 예약이 있으면 병동·스케줄러 select 는 예약 대상을 보여준다(편집용).
        wardId: pending ? pending.toWardId : currentWardId,
        scheduleGroupId: pending
          ? pending.toScheduleGroupId || this.defaultScheduleGroupId(pending.toWardId)
          : currentGroupId,
        isHeadNurse: !!row.isHeadNurse,
        reserveMode: !!pending,
        reserveDate: pending ? pending.effectiveDate : "",
        originalWardId: currentWardId,
        originalScheduleGroupId: currentGroupId,
        pendingReservation: pending,
      };
      this.showEditProfile = true;
    },

    /** 편집 모달 병동 select 변경 — 스케줄러는 그 병동의 기본으로 따라간다. */
    onEditProfileWardChange() {
      this.editProfileForm.scheduleGroupId = this.defaultScheduleGroupId(
        this.editProfileForm.wardId,
      );
    },

    /** 이동 대상이 현재 소속과 같은가 — 스케줄러 목록이 있으면 스케줄러로, 없으면 병동으로 비교. */
    editProfileSameTarget(): boolean {
      const f = this.editProfileForm;
      return this.scheduleGroups.length > 0
        ? f.scheduleGroupId === f.originalScheduleGroupId
        : f.wardId === f.originalWardId;
    },

    /** 예약 이동 적용 시작일의 최소값(내일, 조직 타임존 기준). */
    reserveMinDate(): string {
      const today = Alpine.store("hereby").todayLocal();
      const d = new Date(`${today}T00:00:00`);
      d.setDate(d.getDate() + 1);
      return d.toISOString().slice(0, 10);
    },

    /** 예약 이동 입력 검증 — 유효하면 "", 아니면 안내 문구. */
    reserveWardError(): string {
      const f = this.editProfileForm;
      if (!f.reserveMode) {
        return "";
      }
      if (this.editProfileSameTarget()) {
        return "현재와 다른 스케줄러를 선택하세요.";
      }
      if (!f.reserveDate) {
        return "적용 시작일을 입력하세요.";
      }
      if (f.reserveDate < this.reserveMinDate()) {
        return "적용 시작일은 미래 날짜여야 합니다.";
      }
      return "";
    },

    /** 저장 가능 여부 — 예약 모드면 검증 통과해야 한다. */
    editProfileValid(): boolean {
      return this.reserveWardError() === "";
    },

    /**
     * 저장 — 근무형태/수간호사(+즉시 모드면 병동)는 즉시 반영,
     * 예약 모드면 병동은 그대로 두고 예약 이동으로 저장한다.
     */
    async submitEditProfile() {
      if (!(this.sdk && this.editProfileForm.profileId)) {
        return;
      }
      if (!this.editProfileValid()) {
        return;
      }
      const f = this.editProfileForm;
      const rawIsHeadNurse = f.isHeadNurse as boolean | "true" | "false";
      const safeIsHeadNurse = rawIsHeadNurse !== false && rawIsHeadNurse !== "false";
      // 예약 모드에서는 소속을 지금 바꾸지 않는다(현재 스케줄러 유지) — 즉시 모드에서 대상이 바뀐 경우만 이동.
      // 이동 대상은 스케줄러(병동은 파생); 스케줄러 목록을 못 받았으면 병동으로 보낸다(서버가 기본 스케줄러 배정).
      const moveNow = !(f.reserveMode || this.editProfileSameTarget());
      let moveTarget: { scheduleGroupId?: string; wardId?: string } = {};
      if (moveNow) {
        moveTarget = f.scheduleGroupId
          ? { scheduleGroupId: f.scheduleGroupId }
          : { wardId: f.wardId };
      }
      const reserveTarget = f.scheduleGroupId
        ? { toScheduleGroupId: f.scheduleGroupId }
        : { toWardId: f.wardId };
      this.editProfileSaving = true;
      try {
        await this.sdk.nurseScheduler.updateProfile({
          id: f.profileId,
          workType: f.workType as
            | "three_shift"
            | "day_dedicated"
            | "evening_dedicated"
            | "night_dedicated",
          ...moveTarget,
          isHeadNurse: safeIsHeadNurse,
        });

        if (f.reserveMode) {
          if (f.pendingReservation) {
            await this.sdk.nurseScheduler.updateWardReservation({
              id: f.pendingReservation.id,
              ...reserveTarget,
              effectiveDate: f.reserveDate,
            });
          } else {
            await this.sdk.nurseScheduler.createWardReservation({
              nurseProfileId: f.profileId,
              ...reserveTarget,
              effectiveDate: f.reserveDate,
            });
          }
        } else if (f.pendingReservation) {
          // 예약 모드를 끄고 저장 → 즉시 변경 의도이므로 기존 예약은 취소.
          await this.sdk.nurseScheduler.cancelWardReservation(f.pendingReservation.id);
        }

        this.showEditProfile = false;
        await this.loadProfiles();
      } catch (e: unknown) {
        // biome-ignore lint/suspicious/noConsole: 디버깅용
        console.error("Failed to update nurse profile:", e);
        showError(`저장에 실패했습니다: ${e instanceof Error ? e.message : String(e)}`);
      }
      this.editProfileSaving = false;
    },

    /** 대기 예약을 즉시 취소(모달 배너의 [예약 취소] 버튼). */
    async cancelWardReservation() {
      const pending = this.editProfileForm.pendingReservation;
      if (!(this.sdk && pending)) {
        return;
      }
      this.editProfileSaving = true;
      try {
        await this.sdk.nurseScheduler.cancelWardReservation(pending.id);
        this.editProfileForm.pendingReservation = null;
        this.editProfileForm.reserveMode = false;
        this.editProfileForm.reserveDate = "";
        this.editProfileForm.wardId = this.editProfileForm.originalWardId;
        this.editProfileForm.scheduleGroupId = this.editProfileForm.originalScheduleGroupId;
        await this.loadProfiles();
      } catch (e: unknown) {
        // biome-ignore lint/suspicious/noConsole: 디버깅용
        console.error("Failed to cancel ward reservation:", e);
        showError(`예약 취소에 실패했습니다: ${e instanceof Error ? e.message : String(e)}`);
      }
      this.editProfileSaving = false;
    },

    showNurseContextMenu(event: MouseEvent, nurse: NurseRow, _rowIdx: number) {
      this.cellCtxMenu.show = false;
      this.wtCtxMenu.show = false;
      const menuW = 160;
      const menuH = 100;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      this.nurseCtxMenu = {
        show: true,
        x: Math.min(event.clientX, vw - menuW - 8),
        y: event.clientY + menuH > vh ? Math.max(8, event.clientY - menuH) : event.clientY,
        nurse,
      };
    },

    async recalculateNurseAttendance(nurse: NurseRow | null) {
      if (!(this.sdk && nurse)) {
        return;
      }
      if (this.cycleDates.length === 0) {
        return;
      }
      const dateFrom = this.cycleDates[0];
      const dateTo = this.cycleDates[this.cycleDates.length - 1];
      if (
        !(await askConfirm(
          `${nurse.name}의 출퇴근 기록을 ${dateFrom} ~ ${dateTo} 기간에 대해 Secom 데이터로 재계산하시겠습니까?\n\n수동 입력 기록은 유지됩니다.`,
        ))
      ) {
        return;
      }
      try {
        const result = (await this.sdk.attendance.recalculateFromSecom({
          employeeId: nurse.employeeId,
          dateFrom,
          dateTo,
        })) as { processed?: number };
        showAlert(`재계산 완료: ${result?.processed ?? 0}건 처리됨`);
        if (this.showAttendance) {
          await this.loadAttendanceOverlay();
        }
      } catch {
        showAlert("재계산 중 오류가 발생했습니다.");
      }
    },

    /** Open the ward-change modal for a specific nurse (from nurse context menu). */
    openChangeWard(nurse: NurseRow | null) {
      this.nurseCtxMenu.show = false;
      if (!nurse) {
        return;
      }
      const currentWardId = nurse.wardId || this.selectedWardId;
      const currentWard = this.wards.find((w) => w.id === currentWardId);
      const currentGroupId =
        nurse.scheduleGroupId ||
        (currentWardId === this.selectedWardId ? this.selectedScheduleGroupId : "") ||
        this.defaultScheduleGroupId(currentWardId);
      this.changeWardForm = {
        profileId: nurse.profileId,
        nurseName: nurse.name,
        employeeNumber: nurse.employeeNumber,
        currentWardId,
        currentWardName: currentWard?.name || "",
        currentScheduleGroupId: currentGroupId,
        currentScheduleGroupName: nurse.scheduleGroupName || this.scheduleGroupName(currentGroupId),
        newWardId: currentWardId,
        newScheduleGroupId: currentGroupId,
      };
      this.showChangeWard = true;
    },

    /** 병동 변경 모달의 병동 select 변경 — 스케줄러는 그 병동의 기본으로. */
    onChangeWardWardChange() {
      this.changeWardForm.newScheduleGroupId = this.defaultScheduleGroupId(
        this.changeWardForm.newWardId,
      );
    },

    /** 변경 모달 저장 가능 여부 — 대상이 현재와 달라야 한다(스케줄러 목록이 없으면 병동 비교). */
    changeWardTargetDiffers(): boolean {
      const f = this.changeWardForm;
      if (!f.newWardId) {
        return false;
      }
      return this.scheduleGroups.length > 0
        ? !!f.newScheduleGroupId && f.newScheduleGroupId !== f.currentScheduleGroupId
        : f.newWardId !== f.currentWardId;
    },

    /** Submit the ward change for the currently-targeted nurse. */
    async submitChangeWard() {
      if (!this.sdk) {
        return;
      }
      const f = this.changeWardForm;
      if (!(f.profileId && this.changeWardTargetDiffers())) {
        return;
      }
      this.changeWardSaving = true;
      try {
        await this.sdk.nurseScheduler.updateProfile({
          id: f.profileId,
          ...(f.newScheduleGroupId
            ? { scheduleGroupId: f.newScheduleGroupId }
            : { wardId: f.newWardId }),
        });
        this.showChangeWard = false;
        // Reload the active tab so the moved nurse drops out of (or appears in) the current ward.
        await this.loadTabData();
      } catch {
        // Error
      }
      this.changeWardSaving = false;
    },

    // ===== Schedule Excel upload =====

    /** Open the upload modal — initialize year/month from the current period. */
    openScheduleUpload() {
      this.scheduleUploadForm = {
        year: this.period.year,
        month: this.period.month,
        fileName: "",
        fileSize: 0,
        isParsing: false,
        error: "",
        parsedEntries: [],
        parsedCount: 0,
      };
      this.showScheduleUpload = true;
    },

    /**
     * Generate an empty .xlsx template based on the current ward + selected year/month.
     * Header: 사번 | 이름 | 1 | 2 | ... | N (cycle days)
     * Rows: one per nurse currently in the ward, with empty assignment cells.
     */
    async downloadScheduleTemplate() {
      if (!(this.sdk && this.selectedWardId)) {
        return;
      }
      const xlsx = await import("xlsx");
      const { year, month } = this.scheduleUploadForm;
      const cycle = this.getCycleDates(year, month);
      const pad = (n: number) => String(n).padStart(2, "0");
      const [sy, sm, sd] = cycle.startDate.split("-").map(Number);
      const [ey, em, ed] = cycle.endDate.split("-").map(Number);
      const start = new Date(sy, sm - 1, sd);
      const end = new Date(ey, em - 1, ed);
      const dates: { dateStr: string; day: number }[] = [];
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        dates.push({
          dateStr: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
          day: d.getDate(),
        });
      }

      type ProfileRow = {
        employee?: { employeeNumber?: string; lastName?: string; firstName?: string };
      };
      const profiles = await fetchAllPages<ProfileRow>(
        (page, limit) => this.sdk.nurseScheduler.listProfiles(this.profileScope(), { page, limit }),
        500,
      );

      const headerRow = ["사번", "이름", ...dates.map((d) => d.day)];
      const rows = profiles.map((p) => [
        p.employee?.employeeNumber || "",
        `${p.employee?.lastName || ""}${p.employee?.firstName || ""}`,
        ...dates.map(() => ""),
      ]);
      const aoa = [headerRow, ...rows];

      const ws = xlsx.utils.aoa_to_sheet(aoa);
      ws["!cols"] = [{ wch: 12 }, { wch: 14 }, ...dates.map(() => ({ wch: 4 }))];

      const wb = xlsx.utils.book_new();
      xlsx.utils.book_append_sheet(wb, ws, "Schedule");

      const ward = this.wards.find((w) => w.id === this.selectedWardId);
      const wardName = ward?.name || "ward";
      // 파일명에 스케줄러 이름을 넣어 한 병동의 두 양식이 구분되게 — 경로 구분자는 빼둔다.
      const safe = (v: string) => v.replace(/[\\/:*?"<>|]/g, "-");
      const groupName = this.selectedScheduleGroup?.name || "";
      const fileName = `schedule-template-${safe(wardName)}${groupName ? `-${safe(groupName)}` : ""}-${year}-${pad(month)}.xlsx`;
      xlsx.writeFile(wb, fileName);
    },

    /**
     * Parse the uploaded .xlsx file. On schema problems, sets `error` so the UI
     * shows "업로드에 실패했습니다. 파일을 확인해 주세요." Empty/unknown cells are skipped.
     */
    async onScheduleUploadFile(event: Event) {
      const input = event.target as HTMLInputElement;
      const file = input.files?.[0];
      this.scheduleUploadForm.error = "";
      this.scheduleUploadForm.parsedEntries = [];
      this.scheduleUploadForm.parsedCount = 0;
      this.scheduleUploadForm.fileName = "";
      this.scheduleUploadForm.fileSize = 0;
      this.scheduleUploadForm.isParsing = false;
      if (!file) {
        return;
      }
      this.scheduleUploadForm.fileName = file.name;
      this.scheduleUploadForm.fileSize = file.size;
      this.scheduleUploadForm.isParsing = true;

      try {
        const xlsx = await import("xlsx");
        const buf = await file.arrayBuffer();
        const wb = xlsx.read(buf, { type: "array" });
        const sheetName = wb.SheetNames[0];
        const ws = wb.Sheets[sheetName];
        if (!ws) {
          throw new Error("no_sheet");
        }
        // biome-ignore lint/suspicious/noExplicitAny: xlsx generic shape
        const aoa = xlsx.utils.sheet_to_json<any[]>(ws, { header: 1, raw: false });
        if (!aoa.length || aoa.length < 2) {
          throw new Error("empty");
        }
        const header = aoa[0] as (string | number)[];
        if (
          String(header[0] ?? "").trim() !== "사번" ||
          String(header[1] ?? "").trim() !== "이름"
        ) {
          throw new Error("bad_header");
        }
        const dayNumbers = header
          .slice(2)
          .map((v) => Number.parseInt(String(v ?? "").trim(), 10))
          .filter((n) => !Number.isNaN(n));
        if (dayNumbers.length === 0) {
          throw new Error("no_days");
        }

        const { year, month } = this.scheduleUploadForm;
        const cycle = this.getCycleDates(year, month);
        const pad = (n: number) => String(n).padStart(2, "0");
        const [sy, sm, sd] = cycle.startDate.split("-").map(Number);
        const [ey, em, ed] = cycle.endDate.split("-").map(Number);
        const start = new Date(sy, sm - 1, sd);
        const end = new Date(ey, em - 1, ed);
        const dayToDate = new Map<number, string>();
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          const ds = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
          dayToDate.set(d.getDate(), ds);
        }

        let nurseList = this.nurses;
        if (nurseList.length === 0) {
          type ProfileItem = {
            id?: string;
            employeeId: string;
            workType: string;
            isHeadNurse?: boolean;
            wardId?: string;
            employee?: { employeeNumber?: string; lastName?: string; firstName?: string };
            ward?: { id?: string; name?: string };
          };
          const result = await fetchAllPages<ProfileItem>(
            (page, limit) =>
              this.sdk.nurseScheduler.listProfiles(this.profileScope(), { page, limit }),
            500,
          );
          nurseList = result.map((p) => ({
            employeeId: p.employeeId,
            employeeNumber: p.employee?.employeeNumber || "",
            name: `${p.employee?.lastName || ""}${p.employee?.firstName || ""}`,
            workType: p.workType,
            wardId: p.ward?.id || p.wardId || "",
            wardName: p.ward?.name || "",
            isHeadNurse: !!p.isHeadNurse,
            profileId: p.id || "",
          }));
        }
        const empNumToId = new Map<string, string>();
        for (const n of nurseList) {
          // 사번은 대소문자 무시 — 엑셀 근무표의 소문자 사번도 맞는다
          empNumToId.set(normalizeEmployeeNumber(n.employeeNumber), n.employeeId);
        }

        const codeToType: Record<string, string> = {
          D: "day",
          E: "evening",
          N: "night",
          OFF: "off",
          V: "annual_leave",
          B: "bereavement",
          T: "education",
          U: "unavailable",
        };

        const entries: { employeeId: string; date: string; assignmentType: string }[] = [];
        for (let r = 1; r < aoa.length; r++) {
          const row = aoa[r] as (string | number)[];
          const empNum = normalizeEmployeeNumber(String(row[0] ?? ""));
          if (!empNum) {
            continue;
          }
          const empId = empNumToId.get(empNum);
          if (!empId) {
            continue;
          }
          for (let c = 2; c < header.length; c++) {
            const dayNum = dayNumbers[c - 2];
            if (Number.isNaN(dayNum)) {
              continue;
            }
            const dateStr = dayToDate.get(dayNum);
            if (!dateStr) {
              continue;
            }
            const raw = String(row[c] ?? "")
              .trim()
              .toUpperCase();
            if (!raw) {
              continue;
            }
            const type = codeToType[raw];
            if (type) {
              entries.push({ employeeId: empId, date: dateStr, assignmentType: type });
            }
          }
        }

        if (entries.length === 0) {
          throw new Error("no_entries");
        }

        this.scheduleUploadForm.parsedEntries = entries;
        this.scheduleUploadForm.parsedCount = entries.length;
      } catch {
        this.scheduleUploadForm.error = "업로드에 실패했습니다. 파일을 확인해 주세요.";
        this.scheduleUploadForm.parsedEntries = [];
        this.scheduleUploadForm.parsedCount = 0;
      } finally {
        this.scheduleUploadForm.isParsing = false;
      }
    },

    /* Reset the file selection — used by both Cancel (during parsing) and Delete (after complete). */
    clearScheduleUploadFile() {
      this.scheduleUploadForm.fileName = "";
      this.scheduleUploadForm.fileSize = 0;
      this.scheduleUploadForm.isParsing = false;
      this.scheduleUploadForm.error = "";
      this.scheduleUploadForm.parsedEntries = [];
      this.scheduleUploadForm.parsedCount = 0;
      // biome-ignore lint/suspicious/noExplicitAny: Alpine $refs magic
      const refs = (this as any).$refs as Record<string, HTMLInputElement | undefined>;
      if (refs?.scheduleFileInput) {
        refs.scheduleFileInput.value = "";
      }
    },

    /* Format bytes as "843KB" / "1.2MB" — matches Figma complete state. */
    formatFileSize(bytes: number): string {
      if (!bytes) {
        return "0B";
      }
      if (bytes < 1024) {
        return `${bytes}B`;
      }
      if (bytes < 1024 * 1024) {
        return `${Math.round(bytes / 1024)}KB`;
      }
      return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
    },

    /**
     * Apply the parsed entries: find or create the schedule for upload's year/month
     * + selected ward, push entries via batchUpdateEntries, then reload + close modal.
     */
    async applyScheduleUpload() {
      const f = this.scheduleUploadForm;
      if (!(this.sdk && this.selectedWardId && f.year && f.month) || f.parsedCount === 0) {
        return;
      }
      this.scheduleUploadSaving = true;
      try {
        // 근무표는 스케줄러 단위 — 같은 병동의 다른 스케줄러 근무표에 얹히지 않게 그룹으로 좁힌다.
        const scheduleGroupId = this.selectedScheduleGroupId || undefined;
        const listResult = (await this.sdk.nurseScheduler.listSchedules(
          { wardId: this.selectedWardId, scheduleGroupId, year: f.year, month: f.month },
          { page: 1, limit: 1 },
        )) as { items?: { id?: string }[] };
        let scheduleId: string | undefined = (listResult.items || [])[0]?.id;
        if (!scheduleId) {
          const cycle = this.getCycleDates(f.year, f.month);
          const created = (await this.sdk.nurseScheduler.createSchedule({
            wardId: this.selectedWardId,
            scheduleGroupId,
            year: f.year,
            month: f.month,
            startDate: cycle.startDate,
            endDate: cycle.endDate,
          })) as { id?: string; data?: { id?: string } };
          scheduleId = created?.id || created?.data?.id;
        }
        if (!scheduleId) {
          throw new Error("no_schedule");
        }

        await this.sdk.nurseScheduler.batchUpdateEntries({
          scheduleId,
          entries: f.parsedEntries,
        });

        this.showScheduleUpload = false;
        if (this.selectedWardId && this.period.year === f.year && this.period.month === f.month) {
          await this.loadSchedule();
        }
        this.showToast(`${f.parsedCount}건 업로드 완료`);
      } catch {
        this.scheduleUploadForm.error = "업로드 적용에 실패했습니다. 다시 시도해 주세요.";
      }
      this.scheduleUploadSaving = false;
    },

    async removeNurseFromSchedule(nurse: NurseRow) {
      this.nurseCtxMenu.show = false;
      if (!(this.sdk && nurse.profileId)) {
        return;
      }
      if (
        !(await askConfirm({
          message: `${nurse.name}을(를) 이 스케줄러에서 제거하시겠습니까?`,
          tone: "danger",
        }))
      ) {
        return;
      }
      try {
        await this.sdk.nurseScheduler.deleteProfile(nurse.profileId);
        this.nurses = this.nurses.filter((n) => n.profileId !== nurse.profileId);
      } catch {
        // Error
      }
    },

    // 간호사 프로필 삭제 — 확인 모달을 띄움 (실제 삭제는 confirmDeleteProfile)
    deleteProfile(profileId: string) {
      const row = this.profileRows.find((r) => r.profileId === profileId);
      if (!row) {
        return;
      }
      this.deleteProfileTarget = {
        profileId,
        name: row.name,
        employeeNumber: row.employeeNumber,
      };
      this.showDeleteProfileConfirm = true;
    },

    async confirmDeleteProfile() {
      if (!(this.sdk && this.deleteProfileTarget)) {
        return;
      }
      const { profileId } = this.deleteProfileTarget;
      this.showDeleteProfileConfirm = false;
      this.deleteProfileTarget = null;
      try {
        await this.sdk.nurseScheduler.deleteProfile(profileId);
        await this.loadProfiles();
      } catch {
        // Error
      }
    },

    cancelDeleteProfile() {
      this.showDeleteProfileConfirm = false;
      this.deleteProfileTarget = null;
    },

    async scanAutoRegisterCandidates() {
      if (!(this.sdk && this.selectedWardId)) {
        return;
      }
      // 후보 조회는 스케줄러 단위 — 목록을 못 받은 폴백 상태에선 기본 스케줄러를 알 수 없어 안내만.
      if (!this.selectedScheduleGroupId) {
        this.showToast("스케줄러를 먼저 선택해 주세요");
        return;
      }
      this.autoRegisterLoading = true;
      this.autoRegisterCandidates = [];
      this.autoRegisterResult = null;
      try {
        // 후보 = 이 스케줄러 미등록 재직·휴직 직원 중 연결 팀 소속원 또는 직군·직무 조건에 맞는 사람 (서버에서 거른다)
        const candidates = await this.sdk.nurseScheduler.listAutoRegisterCandidates({
          scheduleGroupId: this.selectedScheduleGroupId,
        });
        this.autoRegisterCandidates = candidates.map((c) => ({ ...c, selected: false }));

        this.showAutoRegister = true;
      } catch {
        // Error
      }
      this.autoRegisterLoading = false;
    },

    toggleAllCandidates(checked: boolean) {
      for (const c of this.autoRegisterCandidates) {
        c.selected = checked;
      }
    },

    async confirmAutoRegister() {
      if (!this.sdk) {
        return;
      }
      const selected = this.autoRegisterCandidates.filter((c) => c.selected);
      if (selected.length === 0) {
        this.showAutoRegister = false;
        return;
      }
      this.autoRegistering = true;
      let added = 0;
      let skipped = 0;
      for (const emp of selected) {
        try {
          await this.sdk.nurseScheduler.createProfile({
            employeeId: emp.id,
            workType: "three_shift",
            wardId: this.selectedWardId,
            scheduleGroupId: this.selectedScheduleGroupId,
            isHeadNurse: false,
          });
          added++;
        } catch {
          skipped++;
        }
      }
      this.autoRegisterResult = { added, skipped };
      this.showAutoRegister = false;
      this.autoRegistering = false;
      await this.loadProfiles();
    },

    fmt(n: number | null | undefined): string {
      if (n === null || n === undefined || Number.isNaN(n)) {
        return "-";
      }
      return n.toLocaleString("ko-KR");
    },

    prefDaysInMonth(): number[] {
      const dim = new Date(this.prefYear, this.prefMonth, 0).getDate();
      return Array.from({ length: dim }, (_, i) => i + 1);
    },

    prefDateStr(day: number): string {
      return `${this.prefYear}-${String(this.prefMonth).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    },

    prefIsWeekend(day: number): boolean {
      const date = new Date(this.prefYear, this.prefMonth - 1, day);
      const dow = date.getDay();
      return dow === 0 || dow === 6;
    },

    prefGetDayOfWeek(day: number): string {
      const keys = [
        "team_mgr.day_sun",
        "team_mgr.day_mon",
        "team_mgr.day_tue",
        "team_mgr.day_wed",
        "team_mgr.day_thu",
        "team_mgr.day_fri",
        "team_mgr.day_sat",
      ];
      const date = new Date(this.prefYear, this.prefMonth - 1, day);
      return _t(this, keys[date.getDay()]);
    },

    mySchedShiftLabel(s: string): string {
      return getAssignmentLabels(this)[s] || s || "-";
    },

    mySchedShiftClass(s: string): string {
      return ASSIGNMENT_CLASSES[s] || "";
    },

    mySchedTime(ts: string | null): string {
      if (!ts) {
        return "-";
      }
      return Alpine.store("hereby").formatTime(new Date(ts));
    },

    mySchedDayLabel(dateStr: string): string {
      const d = new Date(dateStr);
      const keys = [
        "team_mgr.day_sun",
        "team_mgr.day_mon",
        "team_mgr.day_tue",
        "team_mgr.day_wed",
        "team_mgr.day_thu",
        "team_mgr.day_fri",
        "team_mgr.day_sat",
      ];
      return _t(this, keys[d.getDay()]);
    },

    mySchedIsWeekend(dateStr: string): boolean {
      const dow = new Date(dateStr).getDay();
      return dow === 0 || dow === 6;
    },

    mySchedIsPast(dateStr: string): boolean {
      const today = Alpine.store("hereby").todayLocal();
      return dateStr <= today;
    },

    mySchedFirstDayOffset(): number {
      return new Date(this.mySchedYear, this.mySchedMonth - 1, 1).getDay();
    },

    mySchedTodayDate(): string {
      return Alpine.store("hereby")?.todayLocal?.() ?? "";
    },

    get mySchedTotalScheduled(): number {
      return this.myScheduleEntries.filter(
        (e) => e.shift && e.shift !== "off" && !e.shift.includes("leave"),
      ).length;
    },

    get mySchedTotalWorked(): number {
      return this.myScheduleEntries.filter((e) => e.checkIn != null).length;
    },
  };
}
